import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth } from "../App";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SearchableSelect } from "../components/ui/searchable-select";
import { MultiSearchableSelect } from "../components/ui/multi-searchable-select";
import { Progress } from "../components/ui/progress";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../components/ui/command";
import { toast } from "sonner";
import DeleteImportDataModal from "../components/DeleteImportDataModal";
import {
  RefreshCw, Plus, Search, Download, Upload, FileSpreadsheet,
  TrendingUp, Users, BarChart3, PieChart, CheckCircle2, XCircle,
  Clock, Building2, DollarSign, Eye, Edit, Trash2, Filter,
  AlertTriangle, ArrowRight, ArrowUpDown, ArrowUp, ArrowDown,
  ClipboardList, ClipboardCheck, History, FileCheck, Settings, FileText, Users2, Loader2,
  ChevronsUpDown, Check, ChevronDown, Copy, Inbox, Calculator, X,
  CalendarCheck, Wrench, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Star,
  Layers, ListChecks
} from "lucide-react";
import PlantillasChecklist from "./PlantillasChecklist";
import HistorialChecklist from "./HistorialChecklist";
import HistorialReporte from "./HistorialReporte";
import ChecklistModal from "./ChecklistModal";
import ConfiguracionRenovaciones from "./ConfiguracionRenovaciones";
import { useAtencionPrioritaria, ClienteAtencionWatcher } from "../contexts/AtencionPrioritariaContext";
import { ClienteFacturaMesWatcher } from "../contexts/FacturaMismoMesContext";
import { crApi, crEsPersonas } from "../lib/crScope";

const VALIDACIONES_RENOVACION = [
  { key: "reporte_renovaciones", label: "Generar reporte de renovaciones" },
  { key: "carpeta_cliente", label: "Creación de la carpeta del cliente" },
  { key: "descarga_documentacion", label: "Descarga de la documentación de los portales o por correo electrónico" },
  { key: "revision_condiciones", label: "Revisión de las condiciones de la póliza" },
  { key: "checklist", label: "Realización del checklist" },
  { key: "incremento_3000", label: "Incremento mayor a RD$3,000.00 se solicita ajuste y se cotiza" },
  { key: "remision_hallazgos", label: "Remisión de los hallazgos a la aseguradora" },
  { key: "solicitud_ajustes", label: "Solicitud de ajustes y/o cotizaciones en el mercado, cuando corresponda" },
  { key: "incremento_10000", label: "Incremento mayor a RD$10,000.00, se espera respuesta de la aseguradora y cotizaciones antes del despacho al cliente" },
  { key: "revision_valores", label: "Revisión de los valores asegurados" },
  { key: "cuadros_comparativos", label: "Elaboración de cuadros comparativos" },
  { key: "registro_korrenetz", label: "Registro y actualización de la información en Korrenetz" },
  { key: "envio_documentacion", label: "Envío de la documentación al cliente por correo electrónico" },
  { key: "despacho_marbetes", label: "Despacho de marbetes por mensajería" },
  { key: "carga_gestor_documental", label: "Carga de los documentos en el Gestor Documental" },
  { key: "confirmacion_recepcion_cliente", label: "Confirmó con el cliente la recepción de la documentación (correo, WhatsApp o llamada)" },
  { key: "registro_control_llamadas", label: "Registró el contacto con el cliente en el módulo de control de llamadas" },
];

const esRamoAutoStr = (ramo) => {
  const n = (ramo || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase();
  return n.includes("AUTO") || n.includes("VEHICULO");
};

const DEFAULT_COLUMN_ORDER = [
  "cliente", "poliza", "ramo", "moneda", "sucursal", "status", "vence", "dia",
  "valor_asegurado", "prima", "balance", "ejecutivo", "compania", "clase",
  "grupo", "carpeta_entregada", "marbete", "completado", "supervisor", "tipo_poliza"
];

export default function ControlRenovacionesPage() {
  const { user, getAuthHeaders } = useAuth();
  const { esPrioritario, marcar, desmarcar, puedeMarcar } = useAtencionPrioritaria();
  const API_URL = (process.env.REACT_APP_BACKEND_URL || "");
  const esPersonas = crApi().includes("riesgos-personas");
  const fileInputRef = useRef(null);

  // === Restaurar estatus desde Excel exportado (solo admin) ===
  const restauraFileRef = useRef(null);
  const [restauraFile, setRestauraFile] = useState(null);
  const [restauraPreview, setRestauraPreview] = useState(null);
  const [restauraLoading, setRestauraLoading] = useState(false);
  const [showRestauraDialog, setShowRestauraDialog] = useState(false);

  const callRestaurarEstatus = async (archivo, dryRun) => {
    const fd = new FormData();
    fd.append("file", archivo);
    fd.append("dry_run", dryRun ? "true" : "false");
    const res = await fetch(`${API_URL}${crApi()}/control-renovaciones/restaurar-estatus`, {
      method: "POST",
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      body: fd,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || "Error al procesar el archivo");
    return data;
  };

  const handleRestauraFileSelect = async (e) => {
    const archivo = e.target.files?.[0];
    e.target.value = "";
    if (!archivo) return;
    setRestauraFile(archivo);
    setRestauraPreview(null);
    setShowRestauraDialog(true);
    setRestauraLoading(true);
    try {
      const data = await callRestaurarEstatus(archivo, true);
      setRestauraPreview(data);
    } catch (err) {
      toast.error(err.message);
      setShowRestauraDialog(false);
    } finally {
      setRestauraLoading(false);
    }
  };

  const handleConfirmarRestauracion = async () => {
    if (!restauraFile) return;
    setRestauraLoading(true);
    try {
      const data = await callRestaurarEstatus(restauraFile, false);
      toast.success(`${data.cambios} estatus restaurados correctamente`);
      setShowRestauraDialog(false);
      setRestauraFile(null);
      setRestauraPreview(null);
      fetchRenovaciones();
      fetchDashboard();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRestauraLoading(false);
    }
  };

  // State
  const [renovaciones, setRenovaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [config, setConfig] = useState(null);
  const [total, setTotal] = useState(0);
  // Paginación server-side de la grilla de renovaciones
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [canImport, setCanImport] = useState(false); // Only supervisors and admins can import
  const [reporteRefreshKey, setReporteRefreshKey] = useState(0); // Refresca el Historial de Reporte tras exportar
  const [canCreateRenovacion] = useState(true); // All users can create renovaciones

  // Dialog states
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [selectedRenovacion, setSelectedRenovacion] = useState(null);
  const [editMode, setEditMode] = useState(false);

  // Import states
  const [importPreview, setImportPreview] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importActualizarExistentes, setImportActualizarExistentes] = useState(false);
  const [importInvalidReport, setImportInvalidReport] = useState(null); // { count, rows: [{fila, campos}] }
  // Tipo del ejecutivo (Carta de Equipos): controla si se piden los campos
  // `fecha_recibida_oficina` / `fecha_facturado_korrenetz` (solo Individual/Pymes)
  const [ejecutivoIsIndividualPymes, setEjecutivoIsIndividualPymes] = useState(false);
  const [loadingEjecutivoTipo, setLoadingEjecutivoTipo] = useState(false);
  const [showValidacionesModal, setShowValidacionesModal] = useState(false);
  const [validacionesItems, setValidacionesItems] = useState({});
  const [validacionesFaltantes, setValidacionesFaltantes] = useState([]);
  const validacionesRef = useRef(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterClase, setFilterClase] = useState("");
  const [filterSucursal, setFilterSucursal] = useState("");
  const [filterVence, setFilterVence] = useState("");
  const [filterEjecutivo, setFilterEjecutivo] = useState("");
  const [filterCompania, setFilterCompania] = useState("");
  const [filterFechaDesde, setFilterFechaDesde] = useState("");
  const [filterFechaHasta, setFilterFechaHasta] = useState("");
  const [activeFilterColumn, setActiveFilterColumn] = useState("");
  const [activeFilterValue, setActiveFilterValue] = useState("");
  const [filterTrabajadasMes, setFilterTrabajadasMes] = useState(false);
  const [filterNoRenovadas, setFilterNoRenovadas] = useState(false);
  const [filterCarpetaPendiente, setFilterCarpetaPendiente] = useState(false);
  const [filterSupervisor, setFilterSupervisor] = useState("");
  const [filterChecklist, setFilterChecklist] = useState("");
  const [filterHastaMesActual, setFilterHastaMesActual] = useState(false);
  const [vistaSupervisor, setVistaSupervisor] = useState("");
  const [vistaSupervisorOpciones, setVistaSupervisorOpciones] = useState([]);
  const avisoCoberturaRef = useRef(false);
  const [venceOptions, setVenceOptions] = useState([]);
  const [ejecutivoOptions, setEjecutivoOptions] = useState([]);
  const [companiaOptions, setCompaniaOptions] = useState([]);
  const [supervisorOptions, setSupervisorOptions] = useState([]);
  const [companiasCatalogo, setCompaniasCatalogo] = useState([]);
  const [companiaFormOpen, setCompaniaFormOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/control-cotizaciones/companias`, getAuthHeaders());
        const data = await res.json();
        setCompaniasCatalogo(data.companias || []);
      } catch (e) { /* silent */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sorting state
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc"); // "asc" or "desc"

  // Tab state
  const [activeTab, setActiveTab] = useState("renovaciones");

  // Checklist modal state
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [checklistRenovacion, setChecklistRenovacion] = useState(null);

  // Carta de Equipos state
  const [showCartaEquiposDialog, setShowCartaEquiposDialog] = useState(false);
  const [cartaEquiposRenovacion, setCartaEquiposRenovacion] = useState(null);
  const [gestoresCobros, setGestoresCobros] = useState([]);
  const [sociosVendedores, setSociosVendedores] = useState([]);
  const [ejecutivosCarta, setEjecutivosCarta] = useState([]);
  const [selectedGestor, setSelectedGestor] = useState("");
  const [selectedSocioVendedor, setSelectedSocioVendedor] = useState("");
  const [selectedEjecutivoCarta, setSelectedEjecutivoCarta] = useState("");
  const [generatingCarta, setGeneratingCarta] = useState(false);
  
  // Search filters for Carta de Equipos selectors
  const [gestorSearchTerm, setGestorSearchTerm] = useState("");
  const [socioVendedorSearchTerm, setSocioVendedorSearchTerm] = useState("");
  const [ejecutivoCartaSearchTerm, setEjecutivoCartaSearchTerm] = useState("");
  const [gestorPopoverOpen, setGestorPopoverOpen] = useState(false);
  const [socioVendedorPopoverOpen, setSocioVendedorPopoverOpen] = useState(false);
  const [ejecutivoCartaPopoverOpen, setEjecutivoCartaPopoverOpen] = useState(false);
  
  // Lista de usuarios/ejecutivos para el formulario
  const [usuariosEjecutivos, setUsuariosEjecutivos] = useState([]);
  
  // Combobox de ejecutivo (popover open state)
  const [ejecutivoOpen, setEjecutivoOpen] = useState(false);

  // User tipo from Carta de Equipos
  const [userTipo, setUserTipo] = useState(null);
  const [isAdminOrSupervisor, setIsAdminOrSupervisor] = useState(false);
  const [canDelete, setCanDelete] = useState(true);
  const [showDeleteImportModal, setShowDeleteImportModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // ===== Cambios en Lote (bulk update) — solo admin =====
  const [bulkOpciones, setBulkOpciones] = useState({ campos: [], filtros: [], opciones: {} });
  const [bulkOpcionesLoaded, setBulkOpcionesLoaded] = useState(false);
  const [bulkCampo, setBulkCampo] = useState("");
  const [bulkFiltros, setBulkFiltros] = useState({});
  const [bulkPolizas, setBulkPolizas] = useState(null); // null = aún no procesado
  const [bulkTotal, setBulkTotal] = useState(0);
  const [bulkTruncado, setBulkTruncado] = useState(false);
  const [bulkSelected, setBulkSelected] = useState({});
  const [bulkNuevoValor, setBulkNuevoValor] = useState("");
  const [bulkLoadingPreview, setBulkLoadingPreview] = useState(false);
  const [bulkApplying, setBulkApplying] = useState(false);

  const fetchBulkOpciones = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}${crApi()}/control-renovaciones/bulk-update/opciones`, getAuthHeaders());
      if (!res.ok) return;
      const data = await res.json();
      setBulkOpciones(data);
      setBulkOpcionesLoaded(true);
    } catch (e) { /* ignore */ }
  }, [API_URL, getAuthHeaders]);

  const handleBulkFiltroChange = (key, value) => {
    setBulkFiltros((prev) => {
      const next = { ...prev };
      if (value) next[key] = value; else delete next[key];
      return next;
    });
    setBulkPolizas(null);
    setBulkSelected({});
  };

  const handleBulkProcesar = async () => {
    if (Object.keys(bulkFiltros).length === 0) { toast.error("Selecciona al menos un filtro para buscar las pólizas"); return; }
    setBulkLoadingPreview(true);
    setBulkPolizas(null);
    setBulkSelected({});
    try {
      const res = await fetch(`${API_URL}${crApi()}/control-renovaciones/bulk-update/preview`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ filtros: bulkFiltros }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || "No se pudieron cargar las pólizas"); return; }
      const items = data.items || [];
      setBulkPolizas(items);
      setBulkTotal(data.total || 0);
      setBulkTruncado(!!data.truncado);
      const sel = {};
      items.forEach((p) => { sel[p.id] = true; });
      setBulkSelected(sel);
      if (items.length === 0) toast.info("No se encontraron pólizas con esos filtros");
    } catch (e) {
      toast.error("Error al procesar los filtros");
    } finally {
      setBulkLoadingPreview(false);
    }
  };

  const bulkSelectedIds = () => Object.keys(bulkSelected).filter((id) => bulkSelected[id]);

  const bulkToggleAll = (checked) => {
    const sel = {};
    if (checked) (bulkPolizas || []).forEach((p) => { sel[p.id] = true; });
    setBulkSelected(sel);
  };

  const handleBulkExecute = async () => {
    const ids = bulkSelectedIds();
    if (!bulkCampo) { toast.error("Elige primero el campo a cambiar"); return; }
    if (ids.length === 0) { toast.error("Selecciona al menos una póliza"); return; }
    if (!bulkNuevoValor) { toast.error("Elige el nuevo valor a asignar"); return; }
    const label = (bulkOpciones.campos.find((c) => c.key === bulkCampo) || {}).label || bulkCampo;
    if (!window.confirm(`¿Cambiar "${label}" a "${bulkNuevoValor}" en ${ids.length} póliza(s)?`)) return;
    setBulkApplying(true);
    try {
      const res = await fetch(`${API_URL}${crApi()}/control-renovaciones/bulk-update/execute`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ ids, campo: bulkCampo, nuevo_valor: bulkNuevoValor }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || "No se pudo aplicar el cambio"); return; }
      toast.success(data.message || "Cambios aplicados", { duration: 8000 });
      setBulkPolizas(null);
      setBulkSelected({});
      setBulkNuevoValor("");
      setBulkOpcionesLoaded(false); // recargar opciones (aparecen nuevos valores)
      try { fetchRenovaciones(); } catch (e) { /* ignore */ }
    } catch (e) {
      toast.error("Error al aplicar los cambios");
    } finally {
      setBulkApplying(false);
    }
  };

  // Colapsar/expandir secciones de métricas (persistido en localStorage)
  const [collapsedSections, setCollapsedSections] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("cr_collapsed_sections") || "{}");
    } catch {
      return {};
    }
  });
  const toggleSection = (key) => {
    setCollapsedSections((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem("cr_collapsed_sections", JSON.stringify(next)); } catch (e) {}
      return next;
    });
  };

  // Auditoría de Jerarquía (admin-only)
  const [showScopeDialog, setShowScopeDialog] = useState(false);
  const [scopeUsersList, setScopeUsersList] = useState([]);
  const [scopeUsersLoading, setScopeUsersLoading] = useState(false);
  const [scopeSelectedUserId, setScopeSelectedUserId] = useState("");
  const [scopeData, setScopeData] = useState(null);
  const [scopeLoading, setScopeLoading] = useState(false);
  const [scopeSearchQuery, setScopeSearchQuery] = useState("");
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Bulk selection (seleccionar / eliminar masivamente)
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Clone to group state
  const [showCloneDialog, setShowCloneDialog] = useState(false);
  const [cloneLoading, setCloneLoading] = useState(false);
  const [grupoRelacionados, setGrupoRelacionados] = useState(null);
  const [selectedCloneIds, setSelectedCloneIds] = useState([]);
  const [expandedClients, setExpandedClients] = useState({});

  // ===== Cálculo Prima Prorrata / Prima Anual =====
  const [showPrimaProrrataDialog, setShowPrimaProrrataDialog] = useState(false);
  const [primaProrrataMonedaConfirmada, setPrimaProrrataMonedaConfirmada] = useState(false);
  const [primaProrrata, setPrimaProrrata] = useState({
    moneda: "RD$",
    tipoCalculo: "prorrata", // "prorrata" | "anual"
    fechaInicial: "",
    fechaFinal: "",
    primaAnual: "",
    primaProrrataMonto: "",
  });

  // ===== Auditar Fechas (admin only) =====
  const [showAuditDialog, setShowAuditDialog] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditResult, setAuditResult] = useState(null);
  const [repairLoading, setRepairLoading] = useState(false);
  const [repairResult, setRepairResult] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    moneda: "RD$",
    sucursal: "",
    compania: "",
    cliente: "",
    poliza: "",
    ramo: "",
    status: "PENDIENTE",
    confirmacion_recibido: "NO",
    es_emision: false,
    completada: false,
    vence_en: "",
    fecha_vencimiento: "",
    prima: 0,
    ejecutivo: "",
    clase_cliente: "PERSONAL",
    observaciones: "",
    linea: "",
    grupo_cliente: "",
    supervisor: "",
    gestor_cobros: "",
    vendedor: "",
    tipo_poliza: "",
    enviado_por_email: false,
    carpeta_entregada: "NO",
    carpeta_fecha_entrega_interna: "",
    carpeta_entregado_a: "",
    carpeta_comentario: "",
    carpeta_fecha_entrega_cliente: "",
    carpeta_comentario_cliente: "",
    validaciones_corp: {
      marbete_digital_enviado: false,
      marbete_fisico_enviado: false,
      facturado_korrenetz: false,
      enviado_al_cliente: false,
      confirmacion_recepcion: false,
      visita: false,
      fecha_visita: "",
      lleva_carpeta: false,
      presentacion: false,
      checklist: false
    }
  });

  // Fetch functions

  // El campo se exige si el ejecutivo es Individual/Pymes, si el editor es
  // admin/supervisor, o si el editor es Corporativo/Empresarial (también deben
  // poder completar "Recibida en oficina" / "Facturado en Korrenetz").
  const showFechaFields = ejecutivoIsIndividualPymes || isAdminOrSupervisor || userTipo === "Corporativo/Empresarial";

  // Campos corporativos (Supervisor, Gestor de Cobros, Vendedor, Tipo de Póliza, Línea):
  // solo visibles/editables para Admin y usuarios con TIPO = Corporativo/Empresarial.
  const showCorpFields = isAdmin || userTipo === "Corporativo/Empresarial";
  // Columna/checkbox "Completado": visible solo para supervisores, admin y
  // usuarios con tipo Corporativo/Empresarial (Carta de Equipos).
  const showCompletado = isAdminOrSupervisor || userTipo === "Corporativo/Empresarial";
  // Validaciones Corporativas y Carpeta Entregada: SOLO usuarios Corporativo/Empresarial (y admin).
  // Los usuarios Individual/Pymes y Personales no deben ver estas validaciones.
  const showValidacionesCorp = isAdmin || userTipo === "Corporativo/Empresarial";

  // ====== Orden de columnas de la grilla (personalizable por usuario) ======
  const [columnOrder, setColumnOrder] = useState(DEFAULT_COLUMN_ORDER);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [showColumnsMenu, setShowColumnsMenu] = useState(false);
  const dragColRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}${crApi()}/control-renovaciones/preferencias-columnas`, getAuthHeaders());
        if (res.ok) {
          const d = await res.json();
          if (Array.isArray(d.orden) && d.orden.length > 0) setColumnOrder(d.orden);
          if (Array.isArray(d.ocultas)) setHiddenColumns(d.ocultas);
        }
      } catch (_) {}
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveColumnPrefs = async (orden, ocultas) => {
    setColumnOrder(orden);
    setHiddenColumns(ocultas);
    try {
      await fetch(`${API_URL}${crApi()}/control-renovaciones/preferencias-columnas`, {
        method: "PUT",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ orden, ocultas }),
      });
    } catch (_) {}
  };
  const saveColumnOrder = (orden) => saveColumnPrefs(orden, hiddenColumns);

  const toggleColumnHidden = (key) => {
    const next = hiddenColumns.includes(key)
      ? hiddenColumns.filter((k) => k !== key)
      : [...hiddenColumns, key];
    if (DEFAULT_COLUMN_ORDER.filter((k) => !next.includes(k)).length === 0) {
      toast.error("Debe quedar al menos una columna visible");
      return;
    }
    saveColumnPrefs(columnOrder, next);
  };

  const resetColumnOrder = async () => {
    setColumnOrder(DEFAULT_COLUMN_ORDER);
    setHiddenColumns([]);
    try {
      await fetch(`${API_URL}${crApi()}/control-renovaciones/preferencias-columnas`, {
        method: "DELETE",
        headers: getAuthHeaders().headers,
      });
    } catch (_) {}
    toast.success("Orden de columnas restablecido al orden por defecto");
  };

  const onColDragStart = (key) => () => { dragColRef.current = key; };
  const onColDragOver = (e) => { e.preventDefault(); };
  const onColDrop = (key) => (e) => {
    e.preventDefault();
    const from = dragColRef.current;
    dragColRef.current = null;
    if (!from || from === key) return;
    const next = [...columnOrder];
    const fi = next.indexOf(from);
    const ti = next.indexOf(key);
    if (fi < 0 || ti < 0) return;
    next.splice(fi, 1);
    next.splice(ti, 0, from);
    saveColumnOrder(next);
  };

  useEffect(() => {
    if (!showNewDialog) return;
    const ejecutivoName = (formData.ejecutivo || "").trim();
    if (!ejecutivoName) {
      setEjecutivoIsIndividualPymes(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingEjecutivoTipo(true);
      try {
        const res = await fetch(
          `${API_URL}${crApi()}/carta-equipos/tipo-by-name?nombre=${encodeURIComponent(ejecutivoName)}`,
          getAuthHeaders(),
        );
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) setEjecutivoIsIndividualPymes(!!data.is_individual_pymes);
        } else if (!cancelled) {
          setEjecutivoIsIndividualPymes(false);
        }
      } catch (_) {
        if (!cancelled) setEjecutivoIsIndividualPymes(false);
      }
      if (!cancelled) setLoadingEjecutivoTipo(false);
    })();
    return () => { cancelled = true; };
  }, [showNewDialog, formData.ejecutivo]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRenovaciones = useCallback(async () => {
    try {
      const skip = (currentPage - 1) * pageSize;
      let url = `${API_URL}${crApi()}/control-renovaciones/?limit=${pageSize}&skip=${skip}`;
      if (filterStatus) url += `&status=${filterStatus}`;
      if (filterClase) url += `&clase_cliente=${filterClase}`;
      if (filterSucursal) url += `&sucursal=${filterSucursal}`;
      if (filterVence) url += `&vence_en=${encodeURIComponent(filterVence)}`;
      if (filterEjecutivo) url += `&ejecutivo=${encodeURIComponent(filterEjecutivo)}`;
      if (filterCompania) url += `&compania=${encodeURIComponent(filterCompania)}`;
      if (filterFechaDesde) url += `&fecha_desde=${filterFechaDesde}`;
      if (filterFechaHasta) url += `&fecha_hasta=${filterFechaHasta}`;
      if (searchTerm) url += `&busqueda=${encodeURIComponent(searchTerm)}`;
      if (filterTrabajadasMes) url += `&trabajadas_mes=true`;
      if (filterNoRenovadas) url += `&no_renovadas=true`;
      if (filterCarpetaPendiente) url += `&carpeta_pendiente=true`;
      if (filterSupervisor) url += `&supervisor=${encodeURIComponent(filterSupervisor)}`;
      if (filterChecklist) url += `&checklist=${filterChecklist}`;
      if (filterHastaMesActual) url += `&hasta_mes_actual=true`;
      if (vistaSupervisor) url += `&vista_supervisor_id=${encodeURIComponent(vistaSupervisor)}`;
      if (activeFilterColumn === "ramo" && activeFilterValue) url += `&ramo=${encodeURIComponent(activeFilterValue)}`;
      if (activeFilterColumn === "grupo_cliente" && activeFilterValue) url += `&grupo_cliente=${encodeURIComponent(activeFilterValue)}`;
      if (activeFilterColumn === "completada" && activeFilterValue) url += `&completada=${encodeURIComponent(activeFilterValue)}`;

      const res = await fetch(url, getAuthHeaders());
      const data = await res.json();
      setRenovaciones(data.items || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error("Error fetching renovaciones:", error);
      toast.error("Error al cargar las renovaciones");
    }
  }, [API_URL, getAuthHeaders, currentPage, pageSize, filterStatus, filterClase, filterSucursal, filterVence, filterEjecutivo, filterCompania, filterFechaDesde, filterFechaHasta, searchTerm, filterTrabajadasMes, filterNoRenovadas, filterCarpetaPendiente, filterSupervisor, filterChecklist, filterHastaMesActual, vistaSupervisor, activeFilterColumn, activeFilterValue]);

  const fetchDashboard = useCallback(async () => {
    try {
      let url = `${API_URL}${crApi()}/control-renovaciones/dashboard`;
      const params = [];
      if (filterVence) params.push(`vence_en=${encodeURIComponent(filterVence)}`);
      if (filterFechaDesde) params.push(`fecha_desde=${filterFechaDesde}`);
      if (filterFechaHasta) params.push(`fecha_hasta=${filterFechaHasta}`);
      if (filterHastaMesActual) params.push(`hasta_mes_actual=true`);
      if (vistaSupervisor) params.push(`vista_supervisor_id=${encodeURIComponent(vistaSupervisor)}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const res = await fetch(url, getAuthHeaders());
      const data = await res.json();
      setDashboard(data);
      
      // Set vence options from dashboard response (already formatted as MES AÑO)
      if (data.vence_options) {
        setVenceOptions(data.vence_options);
      }
      
      // Set ejecutivo options from dashboard response
      if (data.ejecutivo_options) {
        setEjecutivoOptions(data.ejecutivo_options);
      }
      if (data.compania_options) {
        setCompaniaOptions(data.compania_options);
      }
      if (data.supervisor_options) {
        setSupervisorOptions(data.supervisor_options);
      }
      
      // Set import permission based on user role
      if (data.is_admin_or_supervisor !== undefined) {
        setCanImport(data.is_admin_or_supervisor);
      }
      if (data.can_delete !== undefined) {
        setCanDelete(data.can_delete);
      }
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    }
  }, [API_URL, getAuthHeaders, filterVence, filterFechaDesde, filterFechaHasta, filterHastaMesActual, vistaSupervisor]);

  const fetchVistaSupervisorOpciones = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}${crApi()}/control-renovaciones/vista-supervisor/opciones`, getAuthHeaders());
      if (res.ok) {
        const data = await res.json();
        setVistaSupervisorOpciones(data.opciones || []);
        if (data.por_delegacion && (data.opciones || []).length > 0 && !avisoCoberturaRef.current) {
          avisoCoberturaRef.current = true;
          const nombres = (data.opciones || []).map((s) => s.name).join(", ");
          toast.info(`Tienes una Cobertura de Supervisor activa: ${nombres}. Usa el filtro "Vista Supervisor" para ver su información.`, { duration: 9000 });
        }
      }
    } catch (error) {
      console.error("Error fetching vista supervisor opciones:", error);
    }
  }, [API_URL, getAuthHeaders]);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}${crApi()}/control-renovaciones/config/all`, getAuthHeaders());
      const data = await res.json();
      setConfig(data);
    } catch (error) {
      console.error("Error fetching config:", error);
    }
  }, [API_URL, getAuthHeaders]);

  // Auditoría de Jerarquía (admin-only)
  const fetchUsersForScope = useCallback(async () => {
    setScopeUsersLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/users`, getAuthHeaders());
      const list = Array.isArray(res.data) ? res.data : (res.data?.users || []);
      setScopeUsersList(list.filter(u => u.is_active !== false));
    } catch (e) {
      toast.error("Error cargando usuarios");
    } finally {
      setScopeUsersLoading(false);
    }
  }, [API_URL, getAuthHeaders]);

  const fetchScopeForUser = useCallback(async (uid) => {
    if (!uid) return;
    setScopeLoading(true);
    setScopeData(null);
    try {
      const res = await axios.get(`${API_URL}${crApi()}/control-renovaciones/admin/scope-jerarquico/${uid}`, getAuthHeaders());
      setScopeData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error cargando scope");
    } finally {
      setScopeLoading(false);
    }
  }, [API_URL, getAuthHeaders]);


  // Fetch usuarios/ejecutivos para el formulario de edición
  const fetchUsuariosEjecutivos = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/users`, getAuthHeaders());
      const data = await res.json();
      // Filtrar solo usuarios activos y ordenar por nombre
      const usuarios = (data.users || data || [])
        .filter(u => u.activo !== false)
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setUsuariosEjecutivos(usuarios);
    } catch (error) {
      console.error("Error fetching usuarios:", error);
    }
  }, [API_URL, getAuthHeaders]);

  // Fetch user tipo from Carta de Equipos
  const fetchUserTipo = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}${crApi()}/control-renovaciones/user-tipo`, getAuthHeaders());
      if (res.ok) {
        const data = await res.json();
        setUserTipo(data.tipo);
        setIsAdminOrSupervisor(data.is_admin || data.is_supervisor);
        setIsAdmin(data.is_admin || false);
      }
    } catch (error) {
      console.error("Error fetching user tipo:", error);
    }
  }, [API_URL, getAuthHeaders]);

  // Map de ejecutivo (normalizado) -> tipo (desde Carta de Equipos). Usado para
  // ocultar acciones específicas como "Carta de Equipos" a ejecutivos de tipo
  // Salud Local / Salud Internacional (no aplican por ahora).
  const [ejecutivoTipoMap, setEjecutivoTipoMap] = useState({});
  const fetchEjecutivoTipoMap = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}${crApi()}/carta-equipos/contactos?activo=true`, getAuthHeaders());
      if (!res.ok) return;
      const data = await res.json();
      const map = {};
      for (const c of (data.contactos || [])) {
        const key = (c.nombre || "").trim().toLowerCase();
        if (key) map[key] = c.tipo || "";
      }
      setEjecutivoTipoMap(map);
    } catch (e) { /* silencioso */ }
  }, [API_URL, getAuthHeaders]);

  // Devuelve true si el ejecutivo de la fila pertenece a un tipo "Salud …"
  // para los cuales se debe ocultar el botón "Carta de Equipos".
  const isEjecutivoSalud = useCallback((ejecutivoName) => {
    if (!ejecutivoName) return false;
    const tipo = ejecutivoTipoMap[ejecutivoName.trim().toLowerCase()];
    if (!tipo) return false;
    return /^salud\s+(local|internacional)$/i.test(tipo.trim());
  }, [ejecutivoTipoMap]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchRenovaciones(), fetchDashboard(), fetchConfig(), fetchUsuariosEjecutivos(), fetchUserTipo(), fetchEjecutivoTipoMap(), fetchVistaSupervisorOpciones()]);
      setLoading(false);
    };
    loadData();
  }, [fetchRenovaciones, fetchDashboard, fetchConfig, fetchUsuariosEjecutivos]);

  // Al cambiar cualquier filtro/búsqueda o el tamaño de página, volver a la página 1.
  // (No incluir currentPage en deps para no provocar bucle.)
  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize, filterStatus, filterClase, filterSucursal, filterVence, filterEjecutivo, filterCompania, filterFechaDesde, filterFechaHasta, searchTerm, filterTrabajadasMes, filterNoRenovadas, filterCarpetaPendiente, filterSupervisor, filterChecklist, filterHastaMesActual, vistaSupervisor]);

  // Handle form submit
  const handleSubmit = async () => {
    // Validation for required fields (only on create, not edit)
    if (!editMode) {
      const requiredFields = [
        { key: "moneda", label: "Moneda" },
        { key: "sucursal", label: "Sucursal" },
        { key: "compania", label: "Compañía" },
        { key: "cliente", label: "Cliente" },
        { key: "poliza", label: "Póliza" },
        { key: "ramo", label: "Ramo" },
        { key: "vence_en", label: "Vence En / Fecha Emisión" },
        { key: "ejecutivo", label: "Ejecutivo" },
        { key: "clase_cliente", label: "Clase Cliente" },
      ];
      const missing = requiredFields.filter(f => !formData[f.key] || formData[f.key].toString().trim() === "");
      if (missing.length > 0) {
        toast.error(`Campos obligatorios faltantes: ${missing.map(f => f.label).join(", ")}`);
        return;
      }
      // Campos corporativos obligatorios (solo Admin / Corporativo-Empresarial)
      if (showCorpFields) {
        const corpRequired = [
          { key: "supervisor", label: "Supervisor" },
          { key: "gestor_cobros", label: "Gestor de Cobros" },
          { key: "vendedor", label: "Vendedor" },
          { key: "tipo_poliza", label: "Tipo de Póliza" },
          { key: "linea", label: "Línea" },
        ];
        const corpMissing = corpRequired.filter(f => !formData[f.key] || formData[f.key].toString().trim() === "");
        if (corpMissing.length > 0) {
          toast.error(`Campos obligatorios (Corporativo/Empresarial): ${corpMissing.map(f => f.label).join(", ")}`);
          return;
        }
      }
    } else {
      if (!formData.cliente || !formData.poliza) {
        toast.error("Cliente y Póliza son requeridos");
        return;
      }
    }

    // === Validación específica de los nuevos campos al cambiar status ===
    // Solo se aplica si el ejecutivo es Individual/Pymes (según Carta de Equipos)
    const statusUpper = (formData.status || "").toUpperCase();
    const todayIso = new Date().toISOString().split("T")[0];
    if (showFechaFields && statusUpper === "RECIBIDA") {
      const d = (formData.fecha_recibida_oficina || "").trim();
      if (!d) {
        toast.error("'Recibida en oficina' es obligatorio cuando el status es RECIBIDA");
        return;
      }
      if (d > todayIso) {
        toast.error("'Recibida en oficina' no puede ser mayor a la fecha de hoy");
        return;
      }
    }
    if (showFechaFields && statusUpper === "RENOVADA") {
      const d = (formData.fecha_facturado_korrenetz || "").trim();
      if (!d) {
        toast.error("'Facturado en Korrenetz' es obligatorio cuando el status es RENOVADA");
        return;
      }
      if (d > todayIso) {
        toast.error("'Facturado en Korrenetz' no puede ser mayor a la fecha de hoy");
        return;
      }
    }
    // === Marbete multianual: si se marcó Sí, el fin de vigencia es obligatorio ===
    const vcMb = formData.validaciones_corp || {};
    if (showValidacionesCorp && (vcMb.marbete_digital_enviado || vcMb.marbete_fisico_enviado) && vcMb.marbete_multianual &&
        !(vcMb.marbete_multianual_vencimiento || "").trim()) {
      toast.error("Indique el fin de vigencia de los marbetes multianuales");
      return;
    }

    // === Validaciones obligatorias al pasar a RENOVADA (ejecutivo Individual/Pymes) ===
    const oldStatusUpper = (editMode && selectedRenovacion ? (selectedRenovacion.status || "") : "").toUpperCase();
    if (editMode && !crEsPersonas() && ejecutivoIsIndividualPymes && statusUpper === "RENOVADA" && oldStatusUpper !== "RENOVADA" && !validacionesRef.current) {
      setValidacionesItems(selectedRenovacion?.validaciones_renovacion?.items || {});
      setValidacionesFaltantes([]);
      setShowValidacionesModal(true);
      return;
    }

    try {
      const url = editMode
        ? `${API_URL}${crApi()}/control-renovaciones/${selectedRenovacion.id}`
        : `${API_URL}${crApi()}/control-renovaciones/`;

      // Carpeta entregada = SI → exigir fecha interna, entregado a y comentario
      if (editMode && showValidacionesCorp && (formData.carpeta_entregada || "").toUpperCase() === "SI") {
        if (!(formData.carpeta_fecha_entrega_interna || "").trim()) {
          toast.error("'Fecha de entrega interna' es obligatoria cuando la carpeta fue entregada");
          return;
        }
        if (!(formData.carpeta_entregado_a || "").trim()) {
          toast.error("'Entregado a' es obligatorio cuando la carpeta fue entregada");
          return;
        }
        if (!(formData.carpeta_comentario || "").trim()) {
          toast.error("'Comentario' es obligatorio cuando la carpeta fue entregada");
          return;
        }
      }

      // For non-admin/non-importer users editing, only send fields the banner explicitly
      // allows ("Solo puedes modificar Status y Confirmación Recibido"). This prevents
      // the disabled `vence_en` input (which can be empty if the original was legacy text
      // like "MARZO 2026") from accidentally wiping the date in DB and making the
      // policy disappear from the grid.
      let payload = formData;
      if (validacionesRef.current) {
        payload = { ...payload, validaciones_renovacion: { items: validacionesRef.current } };
      }
      if (editMode && !canImport) {
        payload = {
          status: formData.status,
          confirmacion_recibido: formData.confirmacion_recibido,
          observaciones: formData.observaciones,
          fecha_recibida_oficina: formData.fecha_recibida_oficina,
          fecha_facturado_korrenetz: formData.fecha_facturado_korrenetz,
          enviado_por_email: formData.enviado_por_email,
          ...(validacionesRef.current ? { validaciones_renovacion: { items: validacionesRef.current } } : {}),
          // Checkboxes de validaciones (Marbete entregado, Facturado, Visita, etc.):
          // los usuarios Corporativo/Empresarial pueden cotejarlos y deben persistir.
          validaciones_corp: formData.validaciones_corp,
          completada: formData.completada,
          // Carpeta entregada y su detalle
          carpeta_entregada: formData.carpeta_entregada,
          carpeta_fecha_entrega_interna: formData.carpeta_fecha_entrega_interna,
          carpeta_entregado_a: formData.carpeta_entregado_a,
          carpeta_comentario: formData.carpeta_comentario,
          carpeta_fecha_entrega_cliente: formData.carpeta_fecha_entrega_cliente,
          carpeta_comentario_cliente: formData.carpeta_comentario_cliente,
        };
      }

      const res = await fetch(url, {
        method: editMode ? "PUT" : "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        validacionesRef.current = null;
        toast.success(editMode ? "Renovación actualizada" : "Renovación creada");
        setShowNewDialog(false);
        resetForm();
        fetchRenovaciones();
        fetchDashboard();
      } else {
        // Mostrar el detalle del error del backend (ej: validación obligatoria de fechas)
        let errorMsg = "Error al guardar";
        try {
          const errData = await res.json();
          if (errData?.detail) errorMsg = errData.detail;
        } catch (_) { /* noop */ }
        // Cobertura de Supervisor: el backend exige nota de soporte del cambio
        if (typeof errorMsg === "string" && errorMsg.startsWith("NOTA_DELEGACION_REQUERIDA")) {
          const nota = window.prompt("Cobertura de Supervisor: estás modificando una póliza del supervisor que cubres.\n\nEscribe la nota de soporte que explique este cambio (obligatoria para guardar):");
          if (nota && nota.trim()) {
            const res2 = await fetch(url, {
              method: "PUT",
              headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
              body: JSON.stringify({ ...payload, nota_delegacion: nota.trim() })
            });
            if (res2.ok) {
              validacionesRef.current = null;
              toast.success("Renovación actualizada (nota de soporte registrada)");
              setShowNewDialog(false);
              resetForm();
              fetchRenovaciones();
              fetchDashboard();
              return;
            }
            let e2 = "Error al guardar";
            try { const d2 = await res2.json(); if (d2?.detail) e2 = d2.detail; } catch (_) { /* noop */ }
            toast.error(e2);
            return;
          }
          toast.error("Cambio no guardado: la nota de soporte es obligatoria");
          return;
        }
        toast.error(errorMsg);
      }
    } catch (error) {
      toast.error("Error al guardar");
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm("¿Está seguro de eliminar esta renovación?")) return;

    try {
      const res = await fetch(`${API_URL}${crApi()}/control-renovaciones/${id}`, {
        method: "DELETE",
        ...getAuthHeaders()
      });

      if (res.ok) {
        toast.success("Renovación eliminada");
        fetchRenovaciones();
        fetchDashboard();
      }
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  // ===== Bulk selection helpers =====
  const toggleSelectOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allVisibleSelected = () =>
    sortedRenovaciones.length > 0 &&
    sortedRenovaciones.every((r) => selectedIds.has(r.id));

  const someVisibleSelected = () =>
    sortedRenovaciones.some((r) => selectedIds.has(r.id)) && !allVisibleSelected();

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected()) {
        // unselect all visible
        sortedRenovaciones.forEach((r) => next.delete(r.id));
      } else {
        sortedRenovaciones.forEach((r) => next.add(r.id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`¿Está seguro de eliminar ${ids.length} renovación(es)? Esta acción no se puede deshacer.`)) return;
    setBulkDeleting(true);
    try {
      const res = await fetch(`${API_URL}${crApi()}/control-renovaciones/bulk-delete`, {
        method: "POST",
        ...getAuthHeaders(),
        headers: {
          ...(getAuthHeaders().headers || {}),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      toast.success(data.message || `${ids.length} renovaciones eliminadas`);
      clearSelection();
      fetchRenovaciones();
      fetchDashboard();
    } catch (error) {
      toast.error(error.message || "Error al eliminar masivamente");
    } finally {
      setBulkDeleting(false);
    }
  };

  // === CARTA DE EQUIPOS FUNCTIONS ===
  const openCartaEquiposDialog = async (renovacion) => {
    setCartaEquiposRenovacion(renovacion);
    setSelectedGestor("");
    setSelectedSocioVendedor("");
    setSelectedEjecutivoCarta("");
    // Reset search terms
    setGestorSearchTerm("");
    setSocioVendedorSearchTerm("");
    
    try {
      // Fetch gestores de cobros
      const gestoresRes = await fetch(`${API_URL}${crApi()}/carta-equipos/gestores`, getAuthHeaders());
      const gestoresData = await gestoresRes.json();
      const gestoresList = gestoresData.gestores || [];
      setGestoresCobros(gestoresList);
      
      // Fetch socios y vendedores
      const sociosRes = await fetch(`${API_URL}${crApi()}/carta-equipos/socios-vendedores`, getAuthHeaders());
      const sociosData = await sociosRes.json();
      const sociosList = sociosData.socios_vendedores || [];
      setSociosVendedores(sociosList);

      // Pre-llenar gestor y supervisor/vendedor con la data de la renovación
      // (cargada del Excel) si existe coincidencia por nombre. Si no, queda vacío
      // para selección manual (comportamiento actual).
      const _norm = (s) => (s || "")
        .toString()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
        .toLowerCase().replace(/\s+/g, " ").trim();

      const gestorNombre = _norm(renovacion?.gestor_cobros);
      if (gestorNombre) {
        let match = gestoresList.find((g) => _norm(g.nombre) === gestorNombre);
        // Fallback: coincidencia parcial (nombres con iniciales/apellidos extra)
        if (!match) {
          match = gestoresList.find((g) => {
            const n = _norm(g.nombre);
            return n.includes(gestorNombre) || gestorNombre.includes(n);
          });
        }
        if (match) setSelectedGestor(match.id);
      }

      // El selector combina supervisores y vendedores: probamos vendedor primero,
      // luego supervisor (cualquiera que coincida con la lista).
      const candidatos = [renovacion?.vendedor, renovacion?.supervisor].map(_norm).filter(Boolean);
      for (const cand of candidatos) {
        let match = sociosList.find((s) => _norm(s.nombre) === cand);
        if (!match) {
          match = sociosList.find((s) => {
            const n = _norm(s.nombre);
            return n.includes(cand) || cand.includes(n);
          });
        }
        if (match) { setSelectedSocioVendedor(match.id); break; }
      }
      
      // Fetch ejecutivos (only for admin/supervisor)
      const userRole = (user?.role || "").toLowerCase();
      const canSelectEjecutivo = userRole === "admin" || userRole === "administrador" || userRole === "supervisor";
      if (canSelectEjecutivo) {
        const ejecutivosRes = await fetch(`${API_URL}${crApi()}/carta-equipos/ejecutivos`, getAuthHeaders());
        const ejecutivosData = await ejecutivosRes.json();
        setEjecutivosCarta(ejecutivosData.ejecutivos || []);
      } else {
        setEjecutivosCarta([]);
      }
      
      setShowCartaEquiposDialog(true);
    } catch (error) {
      toast.error("Error al cargar datos para la carta");
    }
  };

  const handleGenerateCartaEquipos = async () => {
    if (!selectedGestor || !selectedSocioVendedor) {
      toast.error("Debe seleccionar un gestor de cobros y un supervisor/vendedor");
      return;
    }

    setGeneratingCarta(true);
    try {
      const res = await fetch(`${API_URL}${crApi()}/carta-equipos/generate-pdf`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          gestor_id: selectedGestor,
          socio_vendedor_id: selectedSocioVendedor,
          ejecutivo_id: selectedEjecutivoCarta || undefined,
          renovacion_id: cartaEquiposRenovacion?.id,
          modulo: window.location.pathname.startsWith("/riesgos-personas") ? "personas" : "generales"
        })
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `carta_equipos_${cartaEquiposRenovacion?.cliente || "cliente"}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        
        toast.success("Carta generada exitosamente");
        setShowCartaEquiposDialog(false);
      } else {
        const error = await res.json();
        toast.error(error.detail || "Error al generar la carta");
      }
    } catch (error) {
      toast.error("Error al generar la carta");
    } finally {
      setGeneratingCarta(false);
    }
  };

  // Reset form
  const resetForm = () => {
    validacionesRef.current = null;
    setFormData({
      moneda: "RD$",
      sucursal: "",
      compania: "",
      cliente: "",
      poliza: "",
      ramo: "",
      status: "PENDIENTE",
      confirmacion_recibido: "NO",
      vence_en: "",
      fecha_vencimiento: "",
      prima: 0,
      ejecutivo: "",
      ejecutivo_renovaciones: "",
      clase_cliente: "PERSONAL",
      observaciones: "",
      linea: "",
      grupo_cliente: "",
      supervisor: "",
      gestor_cobros: "",
      vendedor: "",
      tipo_poliza: "",
      carpeta_entregada: "NO",
      carpeta_fecha_entrega_interna: "",
      carpeta_entregado_a: "",
      carpeta_comentario: "",
      carpeta_fecha_entrega_cliente: "",
      carpeta_comentario_cliente: "",
      validaciones_corp: {
        marbete_digital_enviado: false,
        marbete_fisico_enviado: false,
        facturado_korrenetz: false,
        enviado_al_cliente: false,
        confirmacion_recepcion: false,
        visita: false,
        fecha_visita: "",
        lleva_carpeta: false,
        presentacion: false,
        checklist: false
      }
    });
    setEditMode(false);
    setSelectedRenovacion(null);
  };

  // ====== Cálculo Prima Prorrata handlers ======
  const openPrimaProrrataDialog = () => {
    setPrimaProrrata({ moneda: "RD$", tipoCalculo: "prorrata", fechaInicial: "", fechaFinal: "", primaAnual: "", primaProrrataMonto: "" });
    setPrimaProrrataMonedaConfirmada(false);
    setShowPrimaProrrataDialog(true);
  };

  const primaProrrataDiasTranscurridos = (() => {
    const { fechaInicial, fechaFinal } = primaProrrata;
    if (!fechaInicial || !fechaFinal) return 0;
    const d1 = new Date(fechaInicial + "T00:00:00");
    const d2 = new Date(fechaFinal + "T00:00:00");
    if (isNaN(d1) || isNaN(d2)) return 0;
    const diff = Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
    return diff;
  })();

  const primaProrrataConISC = (() => {
    const anual = Number(primaProrrata.primaAnual);
    if (!anual || anual <= 0 || primaProrrataDiasTranscurridos <= 0) return 0;
    return (anual / 365) * primaProrrataDiasTranscurridos;
  })();

  const primaProrrataSinISC = primaProrrataConISC > 0 ? primaProrrataConISC / 1.16 : 0;

  // Cálculo inverso: de prima prorrata → prima anual
  const primaAnualCalculada = (() => {
    const prorrata = Number(primaProrrata.primaProrrataMonto);
    if (!prorrata || prorrata <= 0 || primaProrrataDiasTranscurridos <= 0) return 0;
    return (prorrata / primaProrrataDiasTranscurridos) * 365;
  })();

  const formatPrimaProrrataAmount = (value) => {
    const num = Number(value) || 0;
    const moneda = primaProrrata.moneda || "RD$";
    const locale = moneda === "EUR$" ? "de-DE" : moneda === "USD$" ? "en-US" : "es-DO";
    const formatted = num.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${moneda} ${formatted}`;
  };


  // Open edit dialog
  const openEditDialog = (renovacion) => {
    validacionesRef.current = null;
    setSelectedRenovacion(renovacion);
    // Lista de supervisores/vendedores (Carta de Equipos) para "Entregado a"
    if (sociosVendedores.length === 0) {
      fetch(`${API_URL}${crApi()}/carta-equipos/socios-vendedores`, getAuthHeaders())
        .then((r) => r.json())
        .then((d) => setSociosVendedores(d.socios_vendedores || []))
        .catch(() => {});
    }
    
    // Fuzzy match function: finds the best matching option from a list
    const fuzzyMatch = (value, options) => {
      if (!value || !options || options.length === 0) return value || "";
      const val = value.toLowerCase().trim();
      // Exact match first
      const exact = options.find(o => o.toLowerCase() === val);
      if (exact) return exact;
      // Best similarity match
      let bestMatch = null;
      let bestScore = 0;
      for (const opt of options) {
        const optL = opt.toLowerCase();
        // Check containment
        if (optL.includes(val) || val.includes(optL)) {
          const score = Math.min(val.length, optL.length) / Math.max(val.length, optL.length);
          if (score > bestScore) { bestScore = score; bestMatch = opt; }
        }
        // Word overlap score
        const valWords = val.split(/\s+/);
        const optWords = optL.split(/\s+/);
        const common = valWords.filter(w => optWords.some(ow => ow.includes(w) || w.includes(ow))).length;
        const wordScore = common / Math.max(valWords.length, optWords.length);
        if (wordScore > bestScore) { bestScore = wordScore; bestMatch = opt; }
      }
      return bestScore >= 0.8 ? bestMatch : value;
    };

    setFormData({
      moneda: renovacion.moneda || "RD$",
      sucursal: fuzzyMatch(renovacion.sucursal, config?.sucursales) || "",
      compania: renovacion.compania || "",
      cliente: renovacion.cliente || "",
      poliza: renovacion.poliza || "",
      ramo: fuzzyMatch(renovacion.ramo, config?.ramos) || "",
      status: fuzzyMatch(renovacion.status, config?.status_options) || "PENDIENTE",
      confirmacion_recibido: renovacion.confirmacion_recibido || "NO",
      es_emision: renovacion.es_emision || false,
      completada: renovacion.completada || false,
      vence_en: (() => {
        const iso = toISODate(renovacion.vence_en) || "";
        const diaV = parseInt(renovacion.dia_vencimiento, 10);
        if (iso && diaV >= 1 && diaV <= 31) {
          const [y, m] = iso.split("-");
          const maxDay = new Date(parseInt(y, 10), parseInt(m, 10), 0).getDate();
          return `${y}-${m}-${String(Math.min(diaV, maxDay)).padStart(2, "0")}`;
        }
        return iso;
      })(),
      fecha_vencimiento: renovacion.fecha_vencimiento || "",
      prima: renovacion.prima || 0,
      ejecutivo: renovacion.ejecutivo || "",
      ejecutivo_renovaciones: renovacion.ejecutivo_renovaciones || "",
      clase_cliente: fuzzyMatch(renovacion.clase_cliente, ["PERSONAL", "CORPORATIVO", "EMPRESARIAL"]) || "PERSONAL",
      observaciones: renovacion.observaciones || "",
      linea: renovacion.linea || "",
      grupo_cliente: renovacion.grupo_cliente || "",
      supervisor: renovacion.supervisor || "",
      gestor_cobros: renovacion.gestor_cobros || "",
      vendedor: renovacion.vendedor || "",
      tipo_poliza: renovacion.tipo_poliza || "",
      carpeta_entregada: (renovacion.carpeta_entregada || "NO").toUpperCase(),
      carpeta_fecha_entrega_interna: renovacion.carpeta_fecha_entrega_interna || "",
      carpeta_entregado_a: renovacion.carpeta_entregado_a || "",
      carpeta_comentario: renovacion.carpeta_comentario || "",
      carpeta_fecha_entrega_cliente: renovacion.carpeta_fecha_entrega_cliente || "",
      carpeta_comentario_cliente: renovacion.carpeta_comentario_cliente || "",
      validaciones_corp: {
        ...(renovacion.validaciones_corp || {
          marbete_digital_enviado: false,
          marbete_fisico_enviado: false,
          facturado_korrenetz: false,
          enviado_al_cliente: false,
          confirmacion_recepcion: false,
          visita: false,
          fecha_visita: "",
          lleva_carpeta: false,
          presentacion: false,
          checklist: false
        }),
        // Cotejo automático: si la póliza ya tiene el checklist realizado,
        // el cotejo "Checklist" se marca solo.
        ...(renovacion.checklist_hecho ? { checklist: true } : {})
      },
      // Campos obligatorios al cambiar status
      fecha_recibida_oficina: renovacion.fecha_recibida_oficina || "",
      fecha_facturado_korrenetz: renovacion.fecha_facturado_korrenetz || "",
      enviado_por_email: renovacion.enviado_por_email || false,
    });
    setEditMode(true);
    setShowNewDialog(true);
  };

  // Clone to group functions
  const openCloneDialog = async () => {
    if (!selectedRenovacion?.id) return;
    setCloneLoading(true);
    setShowCloneDialog(true);
    setSelectedCloneIds([]);
    setExpandedClients({});
    try {
      const res = await fetch(`${API_URL}${crApi()}/control-renovaciones/${selectedRenovacion.id}/grupo-relacionados`, getAuthHeaders());
      const data = await res.json();
      if (res.ok) {
        setGrupoRelacionados(data);
      } else {
        toast.error(data.detail || "Error al obtener grupo");
        setShowCloneDialog(false);
      }
    } catch {
      toast.error("Error de conexión");
      setShowCloneDialog(false);
    } finally {
      setCloneLoading(false);
    }
  };

  const toggleCloneId = (id) => {
    setSelectedCloneIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleClientPolizas = (clientName, polizas) => {
    const polizaIds = polizas.map(p => p.id);
    const allSelected = polizaIds.every(id => selectedCloneIds.includes(id));
    if (allSelected) {
      setSelectedCloneIds(prev => prev.filter(id => !polizaIds.includes(id)));
    } else {
      setSelectedCloneIds(prev => [...new Set([...prev, ...polizaIds])]);
    }
  };

  const handleCloneToGroup = async () => {
    if (selectedCloneIds.length === 0) {
      toast.error("Seleccione al menos una póliza");
      return;
    }
    setCloneLoading(true);
    try {
      const res = await fetch(`${API_URL}${crApi()}/control-renovaciones/${selectedRenovacion.id}/clonar-grupo`, {
        ...getAuthHeaders(),
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ target_ids: selectedCloneIds })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || "Información clonada exitosamente");
        setShowCloneDialog(false);
        fetchRenovaciones();
      } else {
        toast.error(data.detail || "Error al clonar");
      }
    } catch {
      toast.error("Error de conexión");
    } finally {
      setCloneLoading(false);
    }
  };


  // Handle file selection for import
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImportFile(file);
    const formDataUpload = new FormData();
    formDataUpload.append("file", file);

    try {
      const res = await fetch(`${API_URL}${crApi()}/control-renovaciones/import/preview`, {
        method: "POST",
        headers: { Authorization: getAuthHeaders().headers.Authorization },
        body: formDataUpload
      });

      if (res.ok) {
        const data = await res.json();
        setImportPreview(data);
        setColumnMapping(data.suggested_mapping || {});
        setShowImportDialog(true);
      } else {
        toast.error("Error al leer el archivo");
      }
    } catch (error) {
      toast.error("Error al procesar el archivo");
    }
  };

  // Execute import
  const handleExecuteImport = async () => {
    if (!importFile) return;

    setImporting(true);
    const formDataUpload = new FormData();
    formDataUpload.append("file", importFile);
    formDataUpload.append("mapping", JSON.stringify(columnMapping));
    formDataUpload.append("actualizar_existentes", importActualizarExistentes ? "true" : "false");

    try {
      // Need to send mapping as query params or in body differently
      const url = new URL(`${API_URL}${crApi()}/control-renovaciones/import/execute`);
      
      const res = await fetch(url, {
        method: "POST",
        headers: { 
          Authorization: getAuthHeaders().headers.Authorization,
        },
        body: formDataUpload
      });

      if (res.ok) {
        const data = await res.json();
        const msgs = [];
        if (data.imported > 0) msgs.push(`${data.imported} nuevos`);
        if (data.updated > 0) msgs.push(`${data.updated} actualizados`);
        if (data.skipped > 0) msgs.push(`${data.skipped} omitidos (ya existían)`);
        if (data.skipped_invalid > 0) msgs.push(`${data.skipped_invalid} omitidos (campos obligatorios vacíos)`);
        toast.success(`Importación completada: ${msgs.join(", ") || "sin cambios"}`);
        if (data.total_errors > 0) {
          toast.warning(`${data.total_errors} errores encontrados`);
        }
        // Si hubo registros omitidos por campos obligatorios vacíos, mostrar reporte detallado
        if (data.skipped_invalid > 0 && (data.invalid_rows || []).length > 0) {
          setImportInvalidReport({
            count: data.skipped_invalid,
            rows: data.invalid_rows,
          });
        } else {
          setShowImportDialog(false);
          setImportPreview(null);
          setImportFile(null);
        }
        fetchRenovaciones();
        fetchDashboard();
      } else {
        let detail = "Error en la importación";
        try {
          const errData = await res.json();
          if (errData.detail) detail = errData.detail;
        } catch { /* ignore */ }
        toast.error(detail, { duration: 8000 });
      }
    } catch (error) {
      toast.error("Error en la importación");
    } finally {
      setImporting(false);
    }
  };

  // Export to Excel (Individual/Pymes)
  const handleExport = async (tipo = "individual") => {
    try {
      let baseEndpoint = tipo === "corporativo" 
        ? `${API_URL}${crApi()}/control-renovaciones/export-corporativo`
        : tipo === "validaciones"
        ? `${API_URL}${crApi()}/control-renovaciones/export-validaciones`
        : `${API_URL}${crApi()}/control-renovaciones/export/excel`;
      let url = baseEndpoint;
      const params = [];
      // Enviar TODOS los filtros activos del grid para que el Excel refleje
      // exactamente lo que el usuario ve en pantalla (individual y corporativo).
      if (filterStatus) params.push(`status=${encodeURIComponent(filterStatus)}`);
      if (filterVence) params.push(`vence_en=${encodeURIComponent(filterVence)}`);
      if (filterFechaDesde) params.push(`fecha_desde=${encodeURIComponent(filterFechaDesde)}`);
      if (filterFechaHasta) params.push(`fecha_hasta=${encodeURIComponent(filterFechaHasta)}`);
      if (filterClase) params.push(`clase_cliente=${encodeURIComponent(filterClase)}`);
      if (filterSucursal) params.push(`sucursal=${encodeURIComponent(filterSucursal)}`);
      if (filterEjecutivo) params.push(`ejecutivo=${encodeURIComponent(filterEjecutivo)}`);
      if (filterCompania) params.push(`compania=${encodeURIComponent(filterCompania)}`);
      if (filterSupervisor) params.push(`supervisor=${encodeURIComponent(filterSupervisor)}`);
      if (searchTerm) params.push(`busqueda=${encodeURIComponent(searchTerm)}`);
      if (activeFilterColumn === "ramo" && activeFilterValue) params.push(`ramo=${encodeURIComponent(activeFilterValue)}`);
      if (activeFilterColumn === "grupo_cliente" && activeFilterValue) params.push(`grupo_cliente=${encodeURIComponent(activeFilterValue)}`);
      if (activeFilterColumn === "completada" && activeFilterValue) params.push(`completada=${encodeURIComponent(activeFilterValue)}`);
      if (vistaSupervisor) params.push(`vista_supervisor_id=${encodeURIComponent(vistaSupervisor)}`);
      if (params.length) url += `?${params.join("&")}`;

      const res = await fetch(url, getAuthHeaders());
      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = tipo === "corporativo" ? "renovaciones_corporativo.xlsx" : tipo === "validaciones" ? "validaciones_renovadas.xlsx" : "control_renovaciones.xlsx";
      a.click();
      setShowExportMenu(false);
      // El backend ya guardó el reporte en el Historial de Reporte antes de
      // devolver el archivo; refrescamos esa pestaña para reflejarlo.
      setReporteRefreshKey((k) => k + 1);
    } catch (error) {
      toast.error("Error al exportar");
    }
  };

  // Descargar plantilla Excel (solo admin/supervisor)
  const handleExportPlantilla = async () => {
    try {
      const res = await fetch(`${API_URL}${crApi()}/control-renovaciones/export/plantilla`, getAuthHeaders());
      if (!res.ok) {
        let detail = "No se pudo descargar la plantilla";
        try { const err = await res.json(); if (err.detail) detail = err.detail; } catch { /* ignore */ }
        toast.error(detail);
        return;
      }
      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = "plantilla_control_renovaciones.xlsx";
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success("Plantilla descargada");
    } catch (error) {
      toast.error("Error al descargar la plantilla");
    }
  };

  // Generar Carta de Marbetes
  const handleGenerarCartaMarbetes = async (renovacion) => {
    try {
      const res = await fetch(
        `${API_URL}${crApi()}/control-renovaciones/${renovacion.id}/carta-marbetes`,
        getAuthHeaders()
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Error al generar carta");
      }
      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      // Get filename from Content-Disposition header or generate one
      const contentDisposition = res.headers.get("Content-Disposition");
      let filename = `Carta_Marbete_${renovacion.cliente?.substring(0, 20) || "cliente"}.docx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }
      a.download = filename;
      a.click();
      toast.success("Carta de marbetes generada exitosamente");
    } catch (error) {
      console.error("Error generating carta:", error);
      toast.error(error.message || "Error al generar carta de marbetes");
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      "RENOVADA": "bg-green-100 text-green-800",
      "PENDIENTE": "bg-yellow-100 text-yellow-800",
      "RECIBIDA": "bg-blue-100 text-blue-800",
      "EN COTIZACIÓN/LICITACIÓN": "bg-cyan-100 text-cyan-800",
      "CANCELADA X PAGO": "bg-red-100 text-red-800",
      "DESCONTINUADA": "bg-gray-100 text-gray-800",
      "CANCELADA X FALTA DE PAGO": "bg-orange-100 text-orange-800",
      "BALANCE PENDIENTE": "bg-purple-100 text-purple-800"
    };
    return colors[status] || "bg-slate-100 text-slate-800";
  };

  // Format currency
  const formatCurrency = (value, currency = "RD$") => {
    return `${currency} ${(value || 0).toLocaleString("es-DO", { minimumFractionDigits: 2 })}`;
  };

  // Convert text to Title Case for display
  const toTitleCase = (text) => {
    if (!text || typeof text !== 'string') return text;
    
    // Words that should stay lowercase (Spanish prepositions/articles)
    const lowercaseWords = ['de', 'del', 'la', 'las', 'los', 'el', 'en', 'y', 'a', 'e', 'o', 'u', 'para', 'por', 'con', 'sin'];
    
    // Business suffixes that should stay uppercase
    const businessSuffixes = ['srl', 'sa', 'sas', 'llc', 'inc', 'corp', 'ltd', 'cia', 'ca'];
    
    // Check if text is mostly uppercase (needs conversion)
    const upperCount = (text.match(/[A-Z]/g) || []).length;
    const lowerCount = (text.match(/[a-z]/g) || []).length;
    
    if (upperCount + lowerCount > 0 && upperCount / (upperCount + lowerCount) < 0.7) {
      return text; // Already in mixed case, don't convert
    }
    
    return text.split(' ').map((word, index) => {
      const cleanWord = word.replace(/[^a-záéíóúñüA-ZÁÉÍÓÚÑÜ]/gi, '');
      const punctuation = word.slice(cleanWord.length);
      
      // Check if business suffix
      if (businessSuffixes.includes(cleanWord.toLowerCase())) {
        return cleanWord.toUpperCase() + punctuation;
      }
      
      // Check if lowercase word (but capitalize if first word)
      if (lowercaseWords.includes(cleanWord.toLowerCase()) && index > 0) {
        return cleanWord.toLowerCase() + punctuation;
      }
      
      // Standard title case
      return cleanWord.charAt(0).toUpperCase() + cleanWord.slice(1).toLowerCase() + punctuation;
    }).join(' ');
  };

  // Format date to "Mes Año" (e.g., "Febrero 2026") - Title Case
  const MESES_ES = {
    1: "Enero", 2: "Febrero", 3: "Marzo", 4: "Abril",
    5: "Mayo", 6: "Junio", 7: "Julio", 8: "Agosto",
    9: "Septiembre", 10: "Octubre", 11: "Noviembre", 12: "Diciembre"
  };

  // Convierte distintos formatos de fecha conocidos a ISO (YYYY-MM-DD) para el input type="date"
  const toISODate = (value) => {
    if (!value) return "";
    const str = String(value).trim();
    // Ya ISO
    let m = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (m) {
      const yyyy = m[1];
      const mm = String(parseInt(m[2], 10)).padStart(2, "0");
      const dd = String(parseInt(m[3], 10)).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    }
    // DD/MM/YYYY o DD-MM-YYYY
    m = str.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
    if (m) {
      const dd = String(parseInt(m[1], 10)).padStart(2, "0");
      const mm = String(parseInt(m[2], 10)).padStart(2, "0");
      return `${m[3]}-${mm}-${dd}`;
    }
    // Formato español "MES AÑO" o "Mes Año" (ej: "MARZO 2026", "Enero 2025")
    // Convierte a ISO con día 1 por defecto.
    const upperStr = str.toUpperCase();
    for (const [num, name] of Object.entries(MESES_ES)) {
      if (upperStr.startsWith(name.toUpperCase())) {
        const parts = str.split(/\s+/);
        if (parts.length >= 2) {
          const year = parts[1].match(/\d{4}/)?.[0];
          if (year) {
            const mm = String(parseInt(num, 10)).padStart(2, "0");
            return `${year}-${mm}-01`;
          }
        }
      }
    }
    // Formato romano: "3/IV/2026" u otros legacy → dejar vacío para forzar re-selección
    return "";
  };

  const formatVenceDate = (dateValue) => {
    if (!dateValue) return "-";
    
    const dateStr = String(dateValue).trim();
    
    // Check if already in Title Case format (e.g., "Febrero 2026")
    for (const monthName of Object.values(MESES_ES)) {
      if (dateStr.toLowerCase().startsWith(monthName.toLowerCase())) {
        // Return with proper Title Case
        const parts = dateStr.split(' ');
        if (parts.length >= 2) {
          return `${monthName} ${parts[1]}`;
        }
        return dateStr;
      }
    }
    
    // Try to parse different date formats
    const cleanDate = dateStr.split(' ')[0]; // Remove time if present
    
    // Try YYYY-MM-DD format
    let match = cleanDate.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (match) {
      const [, year, month] = match;
      const monthNum = parseInt(month, 10);
      if (monthNum >= 1 && monthNum <= 12) {
        return `${MESES_ES[monthNum]} ${year}`;
      }
    }
    
    // Try DD-MM-YYYY or DD/MM/YYYY format
    match = cleanDate.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
    if (match) {
      const [, , month, year] = match;
      const monthNum = parseInt(month, 10);
      if (monthNum >= 1 && monthNum <= 12) {
        return `${MESES_ES[monthNum]} ${year}`;
      }
    }
    
    return dateValue; // Return original if can't parse
  };

  // Extraer el día del vencimiento - usa el campo dia_vencimiento si existe, sino intenta extraer de la fecha
  const extractDayFromVence = (record) => {
    // Si tiene dia_vencimiento guardado del backend, usarlo
    if (record.dia_vencimiento) {
      return record.dia_vencimiento.toString();
    }
    
    // Fallback: intentar extraer de fecha_vencimiento o vence_en
    const dateValue = record.fecha_vencimiento || record.vence_en;
    if (!dateValue) return "-";
    
    const dateStr = String(dateValue).trim();
    
    // Try YYYY-MM-DD format
    let match = dateStr.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (match) {
      return parseInt(match[3], 10).toString();
    }
    
    // Try DD-MM-YYYY or DD/MM/YYYY format
    match = dateStr.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
    if (match) {
      return parseInt(match[1], 10).toString();
    }
    
    return "-";
  };

  // Función para ordenar
  const handleSort = (field) => {
    if (sortField === field) {
      // Si ya estamos ordenando por este campo, cambiar dirección
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Nuevo campo, ordenar ascendente
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Renderizar icono de ordenamiento
  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-3 h-3 ml-1 opacity-50" />;
    }
    return sortDirection === "asc" 
      ? <ArrowUp className="w-3 h-3 ml-1" />
      : <ArrowDown className="w-3 h-3 ml-1" />;
  };

  // Client-side filter for columns not handled by backend
  const clientFilteredRenovaciones = renovaciones.filter(r => {
    if (activeFilterColumn === "ramo" && activeFilterValue) {
      return (r.ramo || "").toLowerCase() === activeFilterValue.toLowerCase();
    }
    if (activeFilterColumn === "grupo_cliente" && activeFilterValue) {
      return (r.grupo_cliente || "").toLowerCase() === activeFilterValue.toLowerCase();
    }
    if (activeFilterColumn === "completada" && activeFilterValue) {
      return !!r.completada === (activeFilterValue.toLowerCase() === "si");
    }
    return true;
  });

  // Ordenar renovaciones
  const sortedRenovaciones = [...clientFilteredRenovaciones].sort((a, b) => {
    if (!sortField) return 0;
    
    let valA, valB;
    
    switch (sortField) {
      case "dia":
        // Usar dia_vencimiento del registro si existe, sino extraer
        valA = a.dia_vencimiento || parseInt(extractDayFromVence(a)) || 0;
        valB = b.dia_vencimiento || parseInt(extractDayFromVence(b)) || 0;
        break;
      case "prima":
        valA = a.prima || 0;
        valB = b.prima || 0;
        break;
      case "cliente":
      case "poliza":
      case "ramo":
      case "moneda":
      case "sucursal":
      case "status":
      case "vence_en":
      case "ejecutivo":
      case "compania":
      case "clase_cliente":
      case "grupo_cliente":
        valA = (a[sortField] || "").toString().toLowerCase();
        valB = (b[sortField] || "").toString().toLowerCase();
        break;
      default:
        valA = a[sortField] || "";
        valB = b[sortField] || "";
    }
    
    if (valA < valB) return sortDirection === "asc" ? -1 : 1;
    if (valA > valB) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  // ====== Definición de columnas de la grilla (reordenables por usuario) ======
  const columnasGrilla = {
    cliente: {
      label: "Cliente", sort: "cliente",
      cell: (r) => (
        <td key="cliente" className="p-3 text-sm font-medium">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span>{toTitleCase(r.cliente) || "-"}</span>
            {r.entregado_cliente && (
              <Badge
                className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px] px-1.5"
                data-testid={`badge-entregado-${r.id}`}
                title={r.metodo_entrega === "email" ? "Entregado por Email" : "Entregado por Mensajería"}
              >
                Entregado al Cliente
              </Badge>
            )}
            {r.checklist_hecho && (
              <Badge
                className="bg-sky-100 text-sky-800 border-sky-300 text-[10px] px-1.5"
                data-testid={`badge-checklist-${r.id}`}
                title={`Esta póliza (${r.poliza || "-"}) ya tiene el checklist de coberturas realizado`}
              >
                ✓ Checklist
              </Badge>
            )}
            {r.nota_delegacion_ultima && (
              <Badge
                className="bg-violet-100 text-violet-800 border-violet-300 text-[10px] px-1.5 cursor-help"
                data-testid={`badge-nota-delegacion-${r.id}`}
                title={r.nota_delegacion_ultima}
              >
                Nota de cobertura
              </Badge>
            )}
            {r.checklist_no_requerido && (
              <Badge
                className="bg-amber-100 text-amber-800 border-amber-300 text-[10px] px-1.5"
                data-testid={`badge-checklist-na-${r.id}`}
                title={r.checklist_na_por_status ? `El estatus "${r.status || "-"}" no aplica checklist` : `El ramo "${r.ramo || "-"}" está configurado como que no requiere checklist`}
              >
                Checklist No Requerido
              </Badge>
            )}
          </div>
        </td>
      )
    },
    poliza: { label: "Póliza", sort: "poliza", cell: (r) => <td key="poliza" className="p-3 text-sm">{r.poliza || "-"}</td> },
    ramo: { label: "Ramo", sort: "ramo", cell: (r) => <td key="ramo" className="p-3 text-sm">{toTitleCase(r.ramo) || "-"}</td> },
    moneda: { label: "Moneda", sort: "moneda", cell: (r) => <td key="moneda" className="p-3 text-sm">{r.moneda}</td> },
    sucursal: { label: "Sucursal", sort: "sucursal", cell: (r) => <td key="sucursal" className="p-3 text-sm">{toTitleCase(r.sucursal) || "-"}</td> },
    status: {
      label: "Status", sort: "status",
      cell: (r) => (
        <td key="status" className="p-3">
          <div className="flex items-center gap-1">
            <Badge className={getStatusColor(r.status)}>{r.status}</Badge>
            {r.es_emision && (
              <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[10px] px-1.5">EMI</Badge>
            )}
          </div>
        </td>
      )
    },
    vence: { label: "Vence", sort: "vence_en", cell: (r) => <td key="vence" className="p-3 text-sm">{formatVenceDate(r.vence_en)}</td> },
    dia: { label: "Día", sort: "dia", center: true, cell: (r) => <td key="dia" className="p-3 text-sm text-center font-semibold">{extractDayFromVence(r)}</td> },
    valor_asegurado: {
      label: "Valor Asegurado", sort: "valor_asegurado", testId: "th-valor-asegurado",
      cell: (r) => <td key="valor_asegurado" className="p-3 text-sm" data-testid={`td-valor-asegurado-${r.id}`}>{(r.valor_asegurado !== undefined && r.valor_asegurado !== null && r.valor_asegurado !== "") ? formatCurrency(r.valor_asegurado, r.moneda) : "-"}</td>
    },
    prima: { label: "Prima", sort: "prima", cell: (r) => <td key="prima" className="p-3 text-sm font-medium">{formatCurrency(r.prima, r.moneda)}</td> },
    balance: {
      label: "Balance", sort: "balance", testId: "th-balance",
      cell: (r) => <td key="balance" className="p-3 text-sm" data-testid={`td-balance-${r.id}`}>{(r.balance !== undefined && r.balance !== null && r.balance !== "") ? formatCurrency(r.balance, r.moneda) : "-"}</td>
    },
    ejecutivo: { label: "Ejecutivo", sort: "ejecutivo", cell: (r) => <td key="ejecutivo" className="p-3 text-sm">{toTitleCase(r.ejecutivo)}</td> },
    compania: { label: "Compañía", sort: "compania", cell: (r) => <td key="compania" className="p-3 text-sm">{toTitleCase(r.compania) || "-"}</td> },
    clase: {
      label: "Clase", sort: "clase_cliente", visible: !showCompletado,
      cell: (r) => (
        <td key="clase" className="p-3">
          <Badge variant="outline">{toTitleCase(r.clase_cliente)}</Badge>
        </td>
      )
    },
    grupo: { label: "Grupo", sort: "grupo_cliente", cell: (r) => <td key="grupo" className="p-3 text-sm">{toTitleCase(r.grupo_cliente) || "-"}</td> },
    carpeta_entregada: {
      label: "Carpeta Entregada", sort: "carpeta_entregada", center: true, testId: "th-carpeta-entregada",
      cell: (r) => (
        <td key="carpeta_entregada" className="p-3 text-sm text-center" data-testid={`td-carpeta-entregada-${r.id}`}>
          {(r.carpeta_entregada || "").toUpperCase() === "SI" ? (
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px] px-1.5" title={`Entregada a: ${r.carpeta_entregado_a || "-"} · Fecha: ${r.carpeta_fecha_entrega_interna || "-"}`}>SI</Badge>
          ) : ((r.status || "").toUpperCase() === "RENOVADA" && r.validaciones_corp?.lleva_carpeta) ? (
            <Badge className="bg-amber-100 text-amber-800 border-amber-400 text-[10px] px-1.5 inline-flex items-center gap-1" title="Póliza renovada que lleva carpeta y aún no ha sido entregada" data-testid={`alerta-carpeta-pendiente-${r.id}`}>
              <AlertTriangle className="w-3 h-3" />
              Pendiente
            </Badge>
          ) : (
            <Badge className="bg-slate-100 text-slate-600 border-slate-300 text-[10px] px-1.5">NO</Badge>
          )}
        </td>
      )
    },
    marbete: {
      label: "Marbete Multi Anual", sort: null, center: true, testId: "th-marbete-multianual",
      cell: (r) => (
        <td key="marbete" className="p-3 text-sm text-center" data-testid={`td-marbete-multianual-${r.id}`}>
          {!esRamoAutoStr(r.ramo) ? (
            <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-[10px] px-1.5" title="Solo aplica a pólizas de Automóvil/Vehículos">N/A</Badge>
          ) : (r.validaciones_corp?.marbete_multianual && (r.validaciones_corp?.marbete_digital_enviado || r.validaciones_corp?.marbete_fisico_enviado)) ? (
            <Badge className="bg-sky-100 text-sky-800 border-sky-300 text-[10px] px-1.5" title="Marbete multianual — fin de vigencia">
              {r.validaciones_corp?.marbete_multianual_vencimiento || "Si"}
            </Badge>
          ) : (
            <Badge className="bg-slate-100 text-slate-600 border-slate-300 text-[10px] px-1.5">No</Badge>
          )}
        </td>
      )
    },
    completado: {
      label: "Completado", sort: "completada", visible: showCompletado, testId: "th-completado",
      cell: (r) => (
        <td key="completado" className="p-3" data-testid={`td-completado-${r.id}`}>
          <Badge className={r.completada ? "bg-green-100 text-green-800 border-green-300" : "bg-slate-100 text-slate-600 border-slate-300"}>
            {r.completada ? "Si" : "No"}
          </Badge>
        </td>
      )
    },
    supervisor: {
      label: "Supervisor", sort: "supervisor", visible: showCorpFields, testId: "th-supervisor",
      cell: (r) => <td key="supervisor" className="p-3 text-sm" data-testid={`td-supervisor-${r.id}`}>{toTitleCase(r.supervisor) || "-"}</td>
    },
    tipo_poliza: {
      label: "Tipo de Póliza", sort: "tipo_poliza", visible: showCorpFields, testId: "th-tipo-poliza",
      cell: (r) => <td key="tipo_poliza" className="p-3 text-sm" data-testid={`td-tipo-poliza-${r.id}`}>{r.tipo_poliza || "-"}</td>
    },
  };
  if (esPersonas) {
    delete columnasGrilla.valor_asegurado;
    delete columnasGrilla.carpeta_entregada;
    delete columnasGrilla.marbete;
  }
  const columnasVisibles = columnOrder
    .filter((k) => columnasGrilla[k] && columnasGrilla[k].visible !== false && !hiddenColumns.includes(k))
    .map((k) => ({ key: k, ...columnasGrilla[k] }));

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-6 pt-0 md:pt-0 overflow-x-clip" data-testid="control-renovaciones-page">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        {/* STICKY HEADER - se mantiene fijo al hacer scroll */}
        <div className="sticky top-0 z-30 bg-slate-50 dark:bg-slate-900 -mx-4 md:-mx-6 px-4 md:px-6 pt-14 md:pt-6 pb-2 border-b border-slate-200 dark:border-slate-700" data-testid="control-renovaciones-sticky-header">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-600 rounded-xl">
              <RefreshCw className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Control de Renovaciones{crApi().includes("riesgos-personas") ? " — Riesgos de Personas" : ""}</h1>
              <p className="text-sm text-slate-500">Gestión y seguimiento de renovaciones de pólizas</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { fetchRenovaciones(); fetchDashboard(); }}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
            {canImport && (
              <>
                <Button variant="outline" onClick={handleExportPlantilla} data-testid="export-plantilla-btn">
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Exportar Plantilla
                </Button>
                <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                  <Upload className="w-4 h-4 mr-2" />
                  Importar Excel
                </Button>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                />
              </>
            )}
            {isAdmin && (
              <>
                <Button variant="outline" className="text-sky-700 border-sky-200 hover:bg-sky-50" onClick={() => restauraFileRef.current?.click()} data-testid="restaurar-estatus-btn">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Restaurar Estatus
                </Button>
                <input
                  type="file"
                  ref={restauraFileRef}
                  className="hidden"
                  accept=".xlsx,.xls"
                  onChange={handleRestauraFileSelect}
                />
              </>
            )}
            {isAdmin && (
              <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setShowDeleteImportModal(true)} data-testid="delete-import-data-btn">
                <Trash2 className="w-4 h-4 mr-2" />
                Eliminar Data
              </Button>
            )}
            {isAdmin && (
              <Button variant="outline" className="text-amber-600 border-amber-200 hover:bg-amber-50" 
                onClick={async () => {
                try {
                  toast.info("Normalizando datos...");
                  const res = await axios.post(`${API_URL}${crApi()}/control-renovaciones/normalizar-datos`, {}, {
                    ...getAuthHeaders(),
                    timeout: 120000
                  });
                  toast.success(`${res.data.message}`);
                  fetchRenovaciones();
                } catch (err) {
                  console.error("Normalizar error:", err);
                  const detail = err.response?.data?.detail || err.message || "Error desconocido";
                  toast.error(`Error al normalizar: ${detail}`);
                }
              }}>
                <Settings className="w-4 h-4 mr-2" />
                Normalizar
              </Button>
            )}
            {isAdmin && (
              <Button
                variant="outline"
                className="text-blue-600 border-blue-200 hover:bg-blue-50"
                data-testid="audit-fechas-btn"
                onClick={async () => {
                  setShowAuditDialog(true);
                  setAuditLoading(true);
                  setAuditResult(null);
                  setRepairResult(null);
                  try {
                    const res = await axios.get(`${API_URL}${crApi()}/control-renovaciones/auditoria/fechas-faltantes`, {
                      ...getAuthHeaders(),
                      timeout: 60000,
                    });
                    setAuditResult(res.data);
                  } catch (err) {
                    const detail = err.response?.data?.detail || err.message || "Error desconocido";
                    toast.error(`Error al auditar: ${detail}`);
                    setShowAuditDialog(false);
                  } finally {
                    setAuditLoading(false);
                  }
                }}
              >
                <CalendarCheck className="w-4 h-4 mr-2" />
                Auditar Fechas
              </Button>
            )}
            {isAdmin && (
              <Button
                variant="outline"
                className="text-purple-700 border-purple-300 hover:bg-purple-50"
                data-testid="audit-jerarquia-btn"
                onClick={() => { setShowScopeDialog(true); fetchUsersForScope(); }}
                title="Auditar qué ve cada usuario según su jerarquía"
              >
                <Users className="w-4 h-4 mr-2" />
                Auditar Jerarquía
              </Button>
            )}
            {isAdminOrSupervisor ? (
              <div className="relative">
                <Button variant="outline" onClick={() => setShowExportMenu(!showExportMenu)} data-testid="export-dropdown-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Exportar
                  <ChevronDown className="w-4 h-4 ml-1" />
                </Button>
                {showExportMenu && (
                  <div className="absolute right-0 top-full mt-1 bg-slate-800 border border-slate-700 rounded-lg shadow-lg z-50 min-w-[240px]">
                    <button
                      className="w-full text-left px-4 py-2.5 text-sm text-slate-200 hover:bg-slate-700 rounded-t-lg"
                      onClick={() => handleExport("individual")}
                      data-testid="export-individual-btn"
                    >
                      Reporte Individual/Pymes
                    </button>
                    <button
                      className="w-full text-left px-4 py-2.5 text-sm text-slate-200 hover:bg-slate-700"
                      onClick={() => handleExport("corporativo")}
                      data-testid="export-corporativo-btn"
                    >
                      Reporte Corporativo/Empresarial
                    </button>
                    <button
                      className="w-full text-left px-4 py-2.5 text-sm text-slate-200 hover:bg-slate-700 rounded-b-lg"
                      onClick={() => handleExport("validaciones")}
                      data-testid="export-validaciones-btn"
                    >
                      Reporte de Validaciones (Renovadas)
                    </button>
                  </div>
                )}
              </div>
            ) : userTipo === "Corporativo/Empresarial" ? (
              <Button variant="outline" onClick={() => handleExport("corporativo")} data-testid="export-corp-btn">
                <Download className="w-4 h-4 mr-2" />
                Exportar
              </Button>
            ) : (
              <Button variant="outline" onClick={() => handleExport("individual")} data-testid="export-btn">
                <Download className="w-4 h-4 mr-2" />
                Exportar
              </Button>
            )}
            {canCreateRenovacion && (
              <Button onClick={() => { resetForm(); setShowNewDialog(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Nueva Renovación
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => openPrimaProrrataDialog()}
              data-testid="prima-prorrata-btn"
              className="border-emerald-400 text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
            >
              <Calculator className="w-4 h-4 mr-2" />
              Cálculo Prima Prorrata
            </Button>
          </div>
        </div>

        {/* Main Tabs */}
          <TabsList className="w-full justify-start mb-0 bg-white dark:bg-slate-800 border p-1 rounded-lg">
            <TabsTrigger value="renovaciones" className="flex items-center gap-2 data-[state=active]:bg-blue-100 data-[state=active]:text-blue-700">
              <RefreshCw className="w-4 h-4" />
              Renovaciones
            </TabsTrigger>
            <TabsTrigger value="plantillas" className="flex items-center gap-2 data-[state=active]:bg-blue-100 data-[state=active]:text-blue-700">
              <ClipboardList className="w-4 h-4" />
              Plantillas
            </TabsTrigger>
            <TabsTrigger value="historial" className="flex items-center gap-2 data-[state=active]:bg-blue-100 data-[state=active]:text-blue-700">
              <History className="w-4 h-4" />
              Historial Checklist
            </TabsTrigger>
            <TabsTrigger value="historial-reporte" className="flex items-center gap-2 data-[state=active]:bg-blue-100 data-[state=active]:text-blue-700">
              <FileText className="w-4 h-4" />
              Historial de Reporte
            </TabsTrigger>
            {isAdmin && (
            <TabsTrigger value="configuracion" className="flex items-center gap-2 data-[state=active]:bg-blue-100 data-[state=active]:text-blue-700">
              <Settings className="w-4 h-4" />
              Configuración
            </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger
                value="cambios-lote"
                onClick={() => { if (!bulkOpcionesLoaded) fetchBulkOpciones(); }}
                className="flex items-center gap-2 data-[state=active]:bg-blue-100 data-[state=active]:text-blue-700"
                data-testid="tab-cambios-lote"
              >
                <Layers className="w-4 h-4" />
                Cambios en Lote
              </TabsTrigger>
            )}
          </TabsList>
        </div>
        {/* FIN STICKY HEADER */}

          {/* Renovaciones Tab Content */}
          <TabsContent value="renovaciones" className="mt-4">
            {/* Vence Filter - Main filter for statistics */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-slate-500" />
                <Label className="font-medium">Filtrar por Vence:</Label>
              </div>
              <Select value={filterVence || "ALL"} onValueChange={(v) => setFilterVence(v === "ALL" ? "" : v)}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Todos los períodos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todos los períodos</SelectItem>
                  {venceOptions.map((v) => (
                    <SelectItem key={v} value={v}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <label className="flex items-center gap-2 cursor-pointer h-10 px-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900" htmlFor="chk-hasta-mes-actual" title="Muestra las renovaciones que vencen en el mes actual y todos los meses anteriores">
                <input
                  type="checkbox"
                  id="chk-hasta-mes-actual"
                  className="w-4 h-4 accent-indigo-600"
                  checked={filterHastaMesActual}
                  onChange={(e) => setFilterHastaMesActual(e.target.checked)}
                  data-testid="filter-hasta-mes-actual"
                />
                <span className="text-sm font-medium text-slate-600 dark:text-slate-300 whitespace-nowrap">Hasta el mes actual</span>
              </label>
              {vistaSupervisorOpciones.length > 0 && (
                <div className="flex items-center gap-2">
                  <Label className="font-medium whitespace-nowrap">Vista Supervisor:</Label>
                  <Select value={vistaSupervisor || "PROPIA"} onValueChange={(v) => setVistaSupervisor(v === "PROPIA" ? "" : v)}>
                    <SelectTrigger className="w-52" data-testid="vista-supervisor-select">
                      <SelectValue placeholder="Mi vista" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PROPIA">Mi vista</SelectItem>
                      {vistaSupervisorOpciones.map((s) => (
                        <SelectItem key={s.id} value={s.id} data-testid={`vista-supervisor-opcion-${s.id}`}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {vistaSupervisor && (
                <Badge variant="outline" className="bg-amber-50 text-amber-700" data-testid="badge-vista-supervisor">
                  Viendo: {vistaSupervisorOpciones.find((s) => s.id === vistaSupervisor)?.name || "Supervisor"}
                </Badge>
              )}
              {filterHastaMesActual && (
                <Badge variant="outline" className="bg-indigo-50 text-indigo-700" data-testid="badge-filter-hasta-mes">
                  Mostrando: mes actual hacia atrás
                </Badge>
              )}
              {filterVence && (
                <Badge variant="outline" className="bg-blue-50 text-blue-700">
                  Mostrando: {filterVence}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Dashboard Summary */}
        {dashboard && (
          <>
            {/* Main Stats Cards - Row 1 */}
            <div className="kpi-carousel grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
              {/* 1. Total Transacciones a Renovar (antes "Total Transacciones") */}
              <Card 
                className="bg-gradient-to-br from-blue-500 to-blue-600 text-white cursor-pointer hover:from-blue-600 hover:to-blue-700 transition-all"
                onClick={() => {
                  // Clear all filters to show all transactions
                  setFilterStatus("");
                  setFilterClase("");
                  setFilterSucursal("");
                  setFilterEjecutivo("");
                  setFilterTrabajadasMes(false);
                  setFilterNoRenovadas(false);
                  toast.info("Mostrando todas las transacciones");
                }}
                data-testid="card-total-transacciones-renovar"
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-blue-100 text-sm">Total Transacciones a Renovar</p>
                      <p className="text-2xl font-bold">{dashboard.total_polizas}</p>
                    </div>
                    <div className="flex flex-col items-end">
                      <FileSpreadsheet className="w-8 h-8 text-blue-200" />
                      {dashboard.variacion_mes_anterior && (
                        <div className={`flex items-center gap-1 text-xs mt-1 px-2 py-0.5 rounded-full ${
                          dashboard.variacion_mes_anterior.total_polizas > 0 
                            ? 'bg-green-500/30 text-green-100' 
                            : dashboard.variacion_mes_anterior.total_polizas < 0 
                              ? 'bg-red-500/30 text-red-100' 
                              : 'bg-gray-500/30 text-gray-100'
                        }`}>
                          {dashboard.variacion_mes_anterior.total_polizas > 0 ? (
                            <TrendingUp className="w-3 h-3" />
                          ) : dashboard.variacion_mes_anterior.total_polizas < 0 ? (
                            <TrendingUp className="w-3 h-3 rotate-180" />
                          ) : null}
                          <span>{dashboard.variacion_mes_anterior.total_polizas > 0 ? '+' : ''}{dashboard.variacion_mes_anterior.total_polizas}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="border-t border-blue-400/30 pt-2 mt-2">
                    <p className="text-xs text-blue-200 mb-1">Prima Total del Período</p>
                    <div className="flex flex-col gap-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-blue-200">RD$:</span>
                        <span className="font-semibold">{(dashboard.prima_total?.["RD$"] || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-blue-200">US$:</span>
                        <span className="font-semibold">{(dashboard.prima_total?.["US$"] || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                    {dashboard.variacion_mes_anterior && (
                      <p className="text-xs text-blue-300 mt-2">
                        vs {dashboard.variacion_mes_anterior.mes_comparado}: {dashboard.variacion_mes_anterior.prev_total_polizas}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* 2. Total Trabajado */}
              <Card 
                className="bg-gradient-to-br from-green-500 to-green-600 text-white cursor-pointer hover:from-green-600 hover:to-green-700 transition-all"
                onClick={() => {
                  // Show all worked policies (status != PENDIENTE and != RECIBIDA)
                  setFilterStatus("");
                  setFilterTrabajadasMes(false);
                  setFilterNoRenovadas(false);
                  setFilterStatus("RENOVADA");
                  toast.info("Mostrando pólizas trabajadas");
                }}
                data-testid="card-total-trabajado"
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-green-100 text-sm">Total Trabajado</p>
                      <p className="text-2xl font-bold">{dashboard.total_trabajado}</p>
                    </div>
                    <div className="flex flex-col items-end">
                      <CheckCircle2 className="w-8 h-8 text-green-200" />
                      {dashboard.variacion_mes_anterior && (
                        <div className={`flex items-center gap-1 text-xs mt-1 px-2 py-0.5 rounded-full ${
                          dashboard.variacion_mes_anterior.total_trabajado > 0 
                            ? 'bg-emerald-400/30 text-emerald-100' 
                            : dashboard.variacion_mes_anterior.total_trabajado < 0 
                              ? 'bg-red-500/30 text-red-100' 
                              : 'bg-gray-500/30 text-gray-100'
                        }`}>
                          {dashboard.variacion_mes_anterior.total_trabajado > 0 ? (
                            <TrendingUp className="w-3 h-3" />
                          ) : dashboard.variacion_mes_anterior.total_trabajado < 0 ? (
                            <TrendingUp className="w-3 h-3 rotate-180" />
                          ) : null}
                          <span>{dashboard.variacion_mes_anterior.total_trabajado > 0 ? '+' : ''}{dashboard.variacion_mes_anterior.total_trabajado}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="border-t border-green-400/30 pt-2 mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-green-200">% del Total</span>
                      <span className="text-base font-bold">{dashboard.porcentaje_trabajado}%</span>
                    </div>
                    <Progress 
                      value={dashboard.porcentaje_trabajado} 
                      className="h-2 mt-1 bg-green-700" 
                    />
                    <p className="text-xs text-green-200 mt-2 mb-1">Prima Total del Período</p>
                    <div className="flex flex-col gap-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-green-200">RD$:</span>
                        <span className="font-semibold">{(dashboard.trabajado_prima?.["RD$"] || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-green-200">US$:</span>
                        <span className="font-semibold">{(dashboard.trabajado_prima?.["US$"] || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                    {dashboard.variacion_mes_anterior && (
                      <p className="text-xs text-green-300 mt-2">
                        vs {dashboard.variacion_mes_anterior.mes_comparado}: {dashboard.variacion_mes_anterior.prev_total_trabajado}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* 3. Renovadas en el Mes */}
              <Card 
                className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white cursor-pointer hover:from-indigo-600 hover:to-indigo-700 transition-all"
                onClick={() => {
                  setFilterStatus("RENOVADA_O_EMISION");
                  setFilterTrabajadasMes(false);
                  setFilterNoRenovadas(false);
                  toast.info("Mostrando pólizas renovadas + emisiones");
                }}
                data-testid="card-renovadas-mes"
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-indigo-100 text-sm">Renovadas en el Mes</p>
                      <p className="text-2xl font-bold">{dashboard.renovadas_mes?.cantidad || 0}</p>
                    </div>
                    <div className="flex flex-col items-end">
                      <BarChart3 className="w-8 h-8 text-indigo-200" />
                      {dashboard.variacion_mes_anterior && (
                        <div className={`flex items-center gap-1 text-xs mt-1 px-2 py-0.5 rounded-full ${
                          dashboard.variacion_mes_anterior.renovadas > 0 
                            ? 'bg-green-500/30 text-green-100' 
                            : dashboard.variacion_mes_anterior.renovadas < 0 
                              ? 'bg-red-500/30 text-red-100' 
                              : 'bg-gray-500/30 text-gray-100'
                        }`}>
                          {dashboard.variacion_mes_anterior.renovadas > 0 ? (
                            <TrendingUp className="w-3 h-3" />
                          ) : dashboard.variacion_mes_anterior.renovadas < 0 ? (
                            <TrendingUp className="w-3 h-3 rotate-180" />
                          ) : null}
                          <span>{dashboard.variacion_mes_anterior.renovadas > 0 ? '+' : ''}{dashboard.variacion_mes_anterior.renovadas}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="border-t border-indigo-400/30 pt-2 mt-2">
                    <p className="text-xs text-indigo-200 mb-1">
                      {dashboard.renovadas_mes?.porcentaje || 0}% del total
                    </p>

                    {/* Detalle: Renovadas + Emisiones (suman para el total de la tarjeta) */}
                    <div className="flex flex-col gap-1 text-xs mb-2">
                      <div className="flex justify-between items-center" data-testid="renovadas-detalle-renovadas">
                        <span className="text-indigo-100 flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-indigo-200" />
                          Renovadas:
                        </span>
                        <span className="font-semibold">{dashboard.renovadas_mes?.renovadas_count || 0}</span>
                      </div>
                      <div className="flex justify-between items-center" data-testid="renovadas-detalle-emisiones">
                        <span className="text-indigo-100 flex items-center gap-1">
                          <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[9px] px-1.5 py-0 h-4">EMI</Badge>
                          Emisión:
                        </span>
                        <span className="font-semibold">{dashboard.renovadas_mes?.emisiones_count || 0}</span>
                      </div>
                    </div>

                    <div className="flex flex-col gap-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-indigo-200">Prima RD$:</span>
                        <span className="font-semibold">{(dashboard.renovadas_mes?.prima_rd || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-indigo-200">Prima US$:</span>
                        <span className="font-semibold">{(dashboard.renovadas_mes?.prima_us || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                    {dashboard.variacion_mes_anterior && (
                      <p className="text-xs text-indigo-300 mt-2">
                        vs {dashboard.variacion_mes_anterior.mes_comparado}: {dashboard.variacion_mes_anterior.prev_renovadas}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* 4. No Renovadas (NUEVA) — Canceladas x pago / Cancelada / Cancelada x cliente / Descontinuada */}
              <Card 
                className="bg-gradient-to-br from-rose-500 to-rose-600 text-white cursor-pointer hover:from-rose-600 hover:to-rose-700 transition-all"
                onClick={() => {
                  setFilterStatus("");
                  setFilterTrabajadasMes(false);
                  setFilterNoRenovadas(true);
                  toast.info("Mostrando pólizas no renovadas");
                }}
                data-testid="card-no-renovadas"
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-rose-100 text-sm">No Renovadas</p>
                      <p className="text-2xl font-bold">{dashboard.no_renovadas_count || 0}</p>
                    </div>
                    <div className="flex flex-col items-end">
                      <XCircle className="w-8 h-8 text-rose-200" />
                      {dashboard.variacion_mes_anterior && (
                        <div className={`flex items-center gap-1 text-xs mt-1 px-2 py-0.5 rounded-full ${
                          dashboard.variacion_mes_anterior.no_renovadas < 0 
                            ? 'bg-green-500/30 text-green-100' 
                            : dashboard.variacion_mes_anterior.no_renovadas > 0 
                              ? 'bg-amber-500/30 text-amber-100' 
                              : 'bg-gray-500/30 text-gray-100'
                        }`}>
                          {dashboard.variacion_mes_anterior.no_renovadas > 0 ? (
                            <TrendingUp className="w-3 h-3" />
                          ) : dashboard.variacion_mes_anterior.no_renovadas < 0 ? (
                            <TrendingUp className="w-3 h-3 rotate-180" />
                          ) : null}
                          <span>{dashboard.variacion_mes_anterior.no_renovadas > 0 ? '+' : ''}{dashboard.variacion_mes_anterior.no_renovadas}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="border-t border-rose-400/30 pt-2 mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-rose-200">% del Total</span>
                      <span className="text-base font-bold">{dashboard.no_renovadas_porcentaje || 0}%</span>
                    </div>
                    <p className="text-xs text-rose-200 mt-2 mb-1">Prima Total del Período</p>
                    <div className="flex flex-col gap-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-rose-200">RD$:</span>
                        <span className="font-semibold">{(dashboard.no_renovadas_prima?.["RD$"] || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-rose-200">US$:</span>
                        <span className="font-semibold">{(dashboard.no_renovadas_prima?.["US$"] || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                    {dashboard.variacion_mes_anterior && (
                      <p className="text-xs text-rose-300 mt-2">
                        vs {dashboard.variacion_mes_anterior.mes_comparado}: {dashboard.variacion_mes_anterior.prev_no_renovadas || 0}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Carpetas Pendientes de Entrega (solo Riesgos Generales) */}
              {!esPersonas && (
              <Card
                className="bg-gradient-to-br from-amber-500 to-amber-600 text-white cursor-pointer hover:from-amber-600 hover:to-amber-700 transition-all"
                onClick={() => {
                  setFilterStatus("");
                  setFilterTrabajadasMes(false);
                  setFilterNoRenovadas(false);
                  setFilterCarpetaPendiente(true);
                  toast.info("Mostrando pólizas renovadas con carpeta pendiente de entrega");
                }}
                data-testid="card-carpetas-pendientes"
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-amber-100 text-sm">Carpetas Pendientes</p>
                      <p className="text-2xl font-bold" data-testid="carpetas-pendientes-count">{dashboard.carpetas_pendientes_count || 0}</p>
                    </div>
                    <AlertTriangle className="w-8 h-8 text-amber-200" />
                  </div>
                  <div className="border-t border-amber-400/30 pt-2 mt-2">
                    <p className="text-xs text-amber-100">Renovadas que llevan carpeta y aún no ha sido entregada</p>
                    <p className="text-xs text-amber-200 mt-1">Click para filtrar la grilla</p>
                  </div>
                </CardContent>
              </Card>
              )}

              {/* 5. Pendientes de Recibir */}
              <Card 
                className="bg-gradient-to-br from-purple-500 to-purple-600 text-white cursor-pointer hover:from-purple-600 hover:to-purple-700 transition-all"
                onClick={() => {
                  setFilterStatus("PENDIENTE");
                  setFilterTrabajadasMes(false);
                  setFilterNoRenovadas(false);
                  toast.info("Mostrando pólizas pendientes");
                }}
                data-testid="card-pendientes-recibir"
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-purple-100 text-sm">Pendientes de Recibir</p>
                      <p className="text-2xl font-bold">{dashboard.pendientes_count || 0}</p>
                    </div>
                    <div className="flex flex-col items-end">
                      <Clock className="w-8 h-8 text-purple-200" />
                      {dashboard.variacion_mes_anterior && (
                        <div className={`flex items-center gap-1 text-xs mt-1 px-2 py-0.5 rounded-full ${
                          dashboard.variacion_mes_anterior.pendientes < 0 
                            ? 'bg-green-500/30 text-green-100' 
                            : dashboard.variacion_mes_anterior.pendientes > 0 
                              ? 'bg-amber-500/30 text-amber-100' 
                              : 'bg-gray-500/30 text-gray-100'
                        }`}>
                          {dashboard.variacion_mes_anterior.pendientes > 0 ? (
                            <TrendingUp className="w-3 h-3" />
                          ) : dashboard.variacion_mes_anterior.pendientes < 0 ? (
                            <TrendingUp className="w-3 h-3 rotate-180" />
                          ) : null}
                          <span>{dashboard.variacion_mes_anterior.pendientes > 0 ? '+' : ''}{dashboard.variacion_mes_anterior.pendientes}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="border-t border-purple-400/30 pt-2 mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-purple-200">% del Total</span>
                      <span className="text-base font-bold">{dashboard.pendientes_porcentaje || 0}%</span>
                    </div>
                    <p className="text-xs text-purple-200 mt-2 mb-1">Prima Total del Período</p>
                    <div className="flex flex-col gap-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-purple-200">RD$:</span>
                        <span className="font-semibold">{(dashboard.pendientes_prima?.["RD$"] || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-purple-200">US$:</span>
                        <span className="font-semibold">{(dashboard.pendientes_prima?.["US$"] || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                    {dashboard.variacion_mes_anterior && (
                      <p className="text-xs text-purple-300 mt-2">
                        vs {dashboard.variacion_mes_anterior.mes_comparado}: {dashboard.variacion_mes_anterior.prev_pendientes}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* 6. Recibidas y Pendiente de Trabajar */}
              <Card 
                className="bg-gradient-to-br from-amber-500 to-amber-600 text-white cursor-pointer hover:from-amber-600 hover:to-amber-700 transition-all"
                onClick={() => {
                  setFilterStatus("RECIBIDA");
                  setFilterTrabajadasMes(false);
                  setFilterNoRenovadas(false);
                  toast.info("Mostrando pólizas recibidas");
                }}
                data-testid="card-recibidas-pendiente-trabajar"
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-amber-100 text-sm">Recibidas y Pendiente de Trabajar</p>
                      <p className="text-2xl font-bold">{dashboard.recibidas_count || 0}</p>
                    </div>
                    <div className="flex flex-col items-end">
                      <Inbox className="w-8 h-8 text-amber-200" />
                      {dashboard.variacion_mes_anterior && (
                        <div className={`flex items-center gap-1 text-xs mt-1 px-2 py-0.5 rounded-full ${
                          dashboard.variacion_mes_anterior.recibidas > 0 
                            ? 'bg-green-500/30 text-green-100' 
                            : dashboard.variacion_mes_anterior.recibidas < 0 
                              ? 'bg-red-500/30 text-red-100' 
                              : 'bg-gray-500/30 text-gray-100'
                        }`}>
                          {dashboard.variacion_mes_anterior.recibidas > 0 ? (
                            <TrendingUp className="w-3 h-3" />
                          ) : dashboard.variacion_mes_anterior.recibidas < 0 ? (
                            <TrendingUp className="w-3 h-3 rotate-180" />
                          ) : null}
                          <span>{dashboard.variacion_mes_anterior.recibidas > 0 ? '+' : ''}{dashboard.variacion_mes_anterior.recibidas}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="border-t border-amber-400/30 pt-2 mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-amber-200">% del Total</span>
                      <span className="text-base font-bold">{dashboard.recibidas_porcentaje || 0}%</span>
                    </div>
                    <p className="text-xs text-amber-200 mt-2 mb-1">Prima Total del Período</p>
                    <div className="flex flex-col gap-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-amber-200">RD$:</span>
                        <span className="font-semibold">{(dashboard.recibidas_prima?.["RD$"] || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-amber-200">US$:</span>
                        <span className="font-semibold">{(dashboard.recibidas_prima?.["US$"] || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                    {dashboard.variacion_mes_anterior && (
                      <p className="text-xs text-amber-300 mt-2">
                        vs {dashboard.variacion_mes_anterior.mes_comparado}: {dashboard.variacion_mes_anterior.prev_recibidas}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* 7. Con Checklist */}
              <Card
                className="bg-gradient-to-br from-sky-500 to-sky-600 text-white cursor-pointer hover:from-sky-600 hover:to-sky-700 transition-all"
                onClick={() => {
                  setFilterChecklist("con");
                  toast.info("Mostrando pólizas con checklist realizado");
                }}
                data-testid="card-con-checklist"
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sky-100 text-sm">Con Checklist</p>
                      <p className="text-2xl font-bold">{dashboard.checklist_con_count || 0}</p>
                    </div>
                    <ClipboardCheck className="w-8 h-8 text-sky-200" />
                  </div>
                  <p className="text-xs text-sky-200 mt-2">Pólizas con checklist de coberturas realizado. Click para filtrar.</p>
                </CardContent>
              </Card>

              {/* 8. Sin Checklist */}
              <Card
                className="bg-gradient-to-br from-slate-500 to-slate-600 text-white cursor-pointer hover:from-slate-600 hover:to-slate-700 transition-all"
                onClick={() => {
                  setFilterChecklist("sin");
                  toast.info("Mostrando pólizas sin checklist");
                }}
                data-testid="card-sin-checklist"
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-100 text-sm">Sin Checklist</p>
                      <p className="text-2xl font-bold">{dashboard.checklist_sin_count || 0}</p>
                    </div>
                    <ClipboardList className="w-8 h-8 text-slate-200" />
                  </div>
                  <p className="text-xs text-slate-200 mt-2">Pólizas pendientes de checklist. Click para filtrar.</p>
                </CardContent>
              </Card>

              {/* 9. Checklist N/A (ramos que no requieren checklist) — solo Riesgos Generales */}
              {!esPersonas && (
              <Card
                className="bg-gradient-to-br from-amber-500 to-amber-600 text-white cursor-pointer hover:from-amber-600 hover:to-amber-700 transition-all"
                onClick={() => {
                  setFilterChecklist("na");
                  toast.info("Mostrando pólizas cuyo ramo no requiere checklist");
                }}
                data-testid="card-checklist-na"
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-amber-100 text-sm">Checklist N/A</p>
                      <p className="text-2xl font-bold">{dashboard.checklist_na_count || 0}</p>
                    </div>
                    <ClipboardList className="w-8 h-8 text-amber-200" />
                  </div>
                  <p className="text-xs text-amber-200 mt-2">Pólizas de ramos que no requieren checklist. Click para filtrar.</p>
                </CardContent>
              </Card>
              )}
            </div>
            {/* KPI Cumplimiento de Validaciones (Individual/Pymes) — Solo Supervisores/Admin */}
            {canImport && dashboard.cumplimiento_validaciones && dashboard.cumplimiento_validaciones.total_renovadas_pymes > 0 && (
              <Card className="mb-6" data-testid="kpi-cumplimiento-validaciones">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                      <p className="text-sm font-medium flex items-center gap-2">
                        <ClipboardList className="w-4 h-4 text-emerald-600" />
                        {crEsPersonas() ? "Cumplimiento de Validaciones" : "Validación de Checklist"}
                        <Badge variant="outline" className="text-xs">Solo Supervisores/Admin</Badge>
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {dashboard.cumplimiento_validaciones.con_validaciones} de {dashboard.cumplimiento_validaciones.total_renovadas_pymes} {crEsPersonas() ? "pólizas con las validaciones realizadas" : "pólizas con el checklist realizado"}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-40 h-2.5 bg-slate-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${(dashboard.cumplimiento_validaciones.porcentaje ?? 0) >= 90 ? "bg-green-500" : (dashboard.cumplimiento_validaciones.porcentaje ?? 0) >= 60 ? "bg-yellow-500" : "bg-red-500"}`}
                          style={{ width: `${Math.min(dashboard.cumplimiento_validaciones.porcentaje ?? 0, 100)}%` }}
                        ></div>
                      </div>
                      <span className={`text-2xl font-bold ${(dashboard.cumplimiento_validaciones.porcentaje ?? 0) >= 90 ? "text-green-600" : (dashboard.cumplimiento_validaciones.porcentaje ?? 0) >= 60 ? "text-yellow-600" : "text-red-600"}`} data-testid="kpi-validaciones-porcentaje">
                        {dashboard.cumplimiento_validaciones.porcentaje ?? 0}%
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            {canImport && dashboard.por_ejecutivo && dashboard.por_ejecutivo.length > 0 && (
              <Card className="mb-6">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Rendimiento por Ejecutivo
                      <Badge variant="outline" className="ml-2 text-xs">Solo Supervisores/Admin</Badge>
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => toggleSection("ejecutivo")}
                      data-testid="toggle-section-ejecutivo"
                      title={collapsedSections.ejecutivo ? "Expandir" : "Colapsar"}
                    >
                      {collapsedSections.ejecutivo ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </Button>
                  </div>
                  {!collapsedSections.ejecutivo && (
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full bg-green-500"></span> Bueno (&lt;40% pend.)</span>
                      <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full bg-yellow-500"></span> Atención (40-70%)</span>
                      <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500"></span> Crítico (&gt;70%)</span>
                    </div>
                  )}
                </CardHeader>
                {!collapsedSections.ejecutivo && (
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-2">Ejecutivo</th>
                          <th className="text-center py-2 px-2 w-[180px]">Avance</th>
                          <th className="text-center py-2 px-2">Trabajadas</th>
                          <th className="text-center py-2 px-2">Pendientes</th>
                          <th className="text-center py-2 px-2">Recibidas</th>
                          <th className="text-center py-2 px-2">En Cotización</th>
                          <th className="text-center py-2 px-2">Total</th>
                          <th className="text-right py-2 px-2">Prima Trab. RD$</th>
                          <th className="text-right py-2 px-2">Prima Trab. US$</th>
                          <th className="text-right py-2 px-2">Prima Pend. RD$</th>
                          <th className="text-right py-2 px-2">Prima Pend. US$</th>
                          <th className="text-right py-2 px-2">Prima Recib. RD$</th>
                          <th className="text-right py-2 px-2">Prima Recib. US$</th>
                          <th className="text-right py-2 px-2">Prima Cot. RD$</th>
                          <th className="text-right py-2 px-2">Prima Cot. US$</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboard.por_ejecutivo
                          .slice(0, 10)
                          .sort((a, b) => {
                            const pctA = a.total > 0 ? (a.pendientes / a.total) * 100 : 0;
                            const pctB = b.total > 0 ? (b.pendientes / b.total) * 100 : 0;
                            return pctB - pctA;
                          })
                          .map((item, idx) => {
                          const total = item.total || 1;
                          const pctTrabajadas = Math.round((item.trabajadas / total) * 100);
                          const pctPendientes = Math.round((item.pendientes / total) * 100);
                          const semaforo = pctPendientes > 70 ? 'red' : pctPendientes > 40 ? 'yellow' : 'green';
                          const semaforoColors = {
                            red: { dot: 'bg-red-500', bg: 'bg-red-50 dark:bg-red-950/30', border: 'border-l-red-500' },
                            yellow: { dot: 'bg-yellow-500', bg: 'bg-yellow-50 dark:bg-yellow-950/30', border: 'border-l-yellow-500' },
                            green: { dot: 'bg-green-500', bg: 'bg-green-50 dark:bg-green-950/30', border: 'border-l-green-500' },
                          };
                          const colors = semaforoColors[semaforo];
                          return (
                          <tr 
                            key={item.ejecutivo} 
                            data-testid={`ejecutivo-row-${idx}`}
                            className={`border-b border-l-4 ${colors.border} hover:${colors.bg} cursor-pointer transition-colors ${idx % 2 === 0 ? colors.bg : ''}`}
                            onClick={() => {
                              setFilterEjecutivo(item.ejecutivo);
                              
                              setFilterTrabajadasMes(false);
                              toast.info(`Filtrando por ejecutivo: ${toTitleCase(item.ejecutivo)}`);
                            }}
                          >
                            <td className="py-2 px-2 font-medium">
                              <div className="flex items-center gap-2">
                                <span className={`inline-block w-2.5 h-2.5 rounded-full ${colors.dot} flex-shrink-0`} title={`${pctPendientes}% pendientes`}></span>
                                {toTitleCase(item.ejecutivo)}
                              </div>
                            </td>
                            <td className="py-2 px-2">
                              <div className="flex items-center gap-2">
                                <div className="flex-1 h-2.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-green-500 rounded-full transition-all" 
                                    style={{ width: `${pctTrabajadas}%` }}
                                  />
                                </div>
                                <span className="text-xs font-semibold text-muted-foreground w-[36px] text-right">{pctTrabajadas}%</span>
                              </div>
                            </td>
                            <td className="py-2 px-2 text-center">
                              <Badge className="bg-green-100 text-green-800">{item.trabajadas}</Badge>
                            </td>
                            <td className="py-2 px-2 text-center">
                              <Badge className={`${item.pendientes > 0 ? 'bg-yellow-100 text-yellow-800' : 'bg-slate-100 text-slate-600'}`}>
                                {item.pendientes}
                              </Badge>
                            </td>
                            <td className="py-2 px-2 text-center">
                              <Badge className={`${item.recibidas > 0 ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600'}`}>
                                {item.recibidas || 0}
                              </Badge>
                            </td>
                            <td className="py-2 px-2 text-center">
                              <Badge className={`${item.en_cotizacion > 0 ? 'bg-cyan-100 text-cyan-800' : 'bg-slate-100 text-slate-600'}`}>
                                {item.en_cotizacion || 0}
                              </Badge>
                            </td>
                            <td className="py-2 px-2 text-center font-medium">{item.total}</td>
                            <td className="py-2 px-2 text-right text-green-600">
                              {(item.prima_trabajadas?.["RD$"] || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                            </td>
                            <td className="py-2 px-2 text-right text-green-600">
                              {(item.prima_trabajadas?.["US$"] || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </td>
                            <td className="py-2 px-2 text-right text-yellow-600">
                              {(item.prima_pendientes?.["RD$"] || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                            </td>
                            <td className="py-2 px-2 text-right text-yellow-600">
                              {(item.prima_pendientes?.["US$"] || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </td>
                            <td className="py-2 px-2 text-right text-amber-600">
                              {(item.prima_recibidas?.["RD$"] || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                            </td>
                            <td className="py-2 px-2 text-right text-amber-600">
                              {(item.prima_recibidas?.["US$"] || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </td>
                            <td className="py-2 px-2 text-right text-cyan-600">
                              {(item.prima_cotizacion?.["RD$"] || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                            </td>
                            <td className="py-2 px-2 text-right text-cyan-600">
                              {(item.prima_cotizacion?.["US$"] || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        {(() => {
                          const filas = dashboard.por_ejecutivo.slice(0, 10);
                          const sum = (fn) => filas.reduce((acc, it) => acc + (fn(it) || 0), 0);
                          const fmtRD = (v) => v.toLocaleString('es-DO', { minimumFractionDigits: 2 });
                          const fmtUS = (v) => v.toLocaleString('en-US', { minimumFractionDigits: 2 });
                          return (
                            <tr className="border-t-2 border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-800 font-bold" data-testid="ejecutivo-total-general-row">
                              <td className="py-2 px-2">TOTAL GENERAL</td>
                              <td className="py-2 px-2"></td>
                              <td className="py-2 px-2 text-center" data-testid="total-trabajadas">{sum(it => it.trabajadas)}</td>
                              <td className="py-2 px-2 text-center">{sum(it => it.pendientes)}</td>
                              <td className="py-2 px-2 text-center">{sum(it => it.recibidas)}</td>
                              <td className="py-2 px-2 text-center">{sum(it => it.en_cotizacion)}</td>
                              <td className="py-2 px-2 text-center" data-testid="total-general-total">{sum(it => it.total)}</td>
                              <td className="py-2 px-2 text-right text-green-700">{fmtRD(sum(it => it.prima_trabajadas?.["RD$"]))}</td>
                              <td className="py-2 px-2 text-right text-green-700">{fmtUS(sum(it => it.prima_trabajadas?.["US$"]))}</td>
                              <td className="py-2 px-2 text-right text-yellow-700">{fmtRD(sum(it => it.prima_pendientes?.["RD$"]))}</td>
                              <td className="py-2 px-2 text-right text-yellow-700">{fmtUS(sum(it => it.prima_pendientes?.["US$"]))}</td>
                              <td className="py-2 px-2 text-right text-amber-700">{fmtRD(sum(it => it.prima_recibidas?.["RD$"]))}</td>
                              <td className="py-2 px-2 text-right text-amber-700">{fmtUS(sum(it => it.prima_recibidas?.["US$"]))}</td>
                              <td className="py-2 px-2 text-right text-cyan-700">{fmtRD(sum(it => it.prima_cotizacion?.["RD$"]))}</td>
                              <td className="py-2 px-2 text-right text-cyan-700">{fmtUS(sum(it => it.prima_cotizacion?.["US$"]))}</td>
                            </tr>
                          );
                        })()}
                      </tfoot>
                    </table>
                  </div>
                </CardContent>
                )}
              </Card>
            )}

            {/* Stats Row - Por Clase, Status, Sucursal */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {/* Por Clase de Cliente */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Por Clase de Cliente
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => toggleSection("clase")}
                      data-testid="toggle-section-clase"
                      title={collapsedSections.clase ? "Expandir" : "Colapsar"}
                    >
                      {collapsedSections.clase ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </Button>
                  </div>
                </CardHeader>
                {!collapsedSections.clase && (
                <CardContent>
                  <div className="space-y-4">
                    {Object.entries(dashboard.por_clase_cliente || {}).map(([clase, data]) => (
                      <div 
                        key={clase} 
                        className="space-y-2 pb-3 border-b last:border-0 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 p-2 rounded transition-colors"
                        onClick={() => {
                          setFilterClase(clase);
                          
                          setFilterTrabajadasMes(false);
                          toast.info(`Filtrando por clase: ${toTitleCase(clase)}`);
                        }}
                      >
                        <div className="flex justify-between text-sm">
                          <span className="font-medium">{toTitleCase(clase)}</span>
                          <span className="text-slate-500">Total: {data.total}</span>
                        </div>
                        <div className="flex flex-wrap gap-2 text-xs">
                          <Badge className="bg-green-100 text-green-800">Renovada: {data.RENOVADA}</Badge>
                          <Badge className="bg-yellow-100 text-yellow-800">Pendiente: {data.PENDIENTE}</Badge>
                          <Badge className="bg-cyan-100 text-cyan-800">En Cotización: {data.EN_COTIZACION || 0}</Badge>
                        </div>
                        {/* Prima info */}
                        <div className="grid grid-cols-3 gap-2 text-xs mt-2 bg-slate-50 dark:bg-slate-800 p-2 rounded">
                          <div>
                            <p className="text-slate-500">Prima Renovada</p>
                            <p className="text-green-600">RD$ {(data.prima_renovada?.["RD$"] || 0).toLocaleString('es-DO')}</p>
                            <p className="text-green-600">US$ {(data.prima_renovada?.["US$"] || 0).toLocaleString('en-US')}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Prima Pendiente</p>
                            <p className="text-yellow-600">RD$ {(data.prima_pendiente?.["RD$"] || 0).toLocaleString('es-DO')}</p>
                            <p className="text-yellow-600">US$ {(data.prima_pendiente?.["US$"] || 0).toLocaleString('en-US')}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Prima Cotización</p>
                            <p className="text-cyan-600">RD$ {(data.prima_cotizacion?.["RD$"] || 0).toLocaleString('es-DO')}</p>
                            <p className="text-cyan-600">US$ {(data.prima_cotizacion?.["US$"] || 0).toLocaleString('en-US')}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
                )}
              </Card>

              {/* Por Status */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <BarChart3 className="w-4 h-4" />
                      Por Status
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => toggleSection("status")}
                      data-testid="toggle-section-status"
                      title={collapsedSections.status ? "Expandir" : "Colapsar"}
                    >
                      {collapsedSections.status ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </Button>
                  </div>
                </CardHeader>
                {!collapsedSections.status && (
                <CardContent>
                  <div className="space-y-3 max-h-[440px] overflow-y-auto pr-1">
                    {dashboard.por_status?.map((item) => (
                      <div 
                        key={item.status} 
                        className="pb-2 border-b last:border-0 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 p-2 rounded transition-colors"
                        onClick={() => {
                          setFilterStatus(item.status);
                          
                          setFilterTrabajadasMes(false);
                          toast.info(`Filtrando por status: ${toTitleCase(item.status)}`);
                        }}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium">{toTitleCase(item.status)}</span>
                          <div className="flex items-center gap-2">
                            <span className="font-bold">{item.cantidad}</span>
                            <Badge variant="outline" className="text-xs">{item.porcentaje}%</Badge>
                          </div>
                        </div>
                        <div className="text-xs text-slate-500 flex gap-3">
                          <span>RD$ {(item.prima_rd || 0).toLocaleString('es-DO')}</span>
                          <span>US$ {(item.prima_us || 0).toLocaleString('en-US')}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
                )}
              </Card>

              {/* Por Sucursal */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Por Sucursal
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => toggleSection("sucursal")}
                      data-testid="toggle-section-sucursal"
                      title={collapsedSections.sucursal ? "Expandir" : "Colapsar"}
                    >
                      {collapsedSections.sucursal ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </Button>
                  </div>
                </CardHeader>
                {!collapsedSections.sucursal && (
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(dashboard.por_sucursal || {}).slice(0, 5).map(([sucursal, data]) => (
                      <div 
                        key={sucursal} 
                        className="pb-2 border-b last:border-0 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 p-2 rounded transition-colors"
                        onClick={() => {
                          setFilterSucursal(data.display_name || sucursal);
                          
                          setFilterTrabajadasMes(false);
                          toast.info(`Filtrando por sucursal: ${data.display_name || toTitleCase(sucursal)}`);
                        }}
                      >
                        <div className="flex justify-between text-sm mb-1">
                          <span className="font-medium">{data.display_name || toTitleCase(sucursal)}</span>
                          <span className="text-slate-500">Total: {data.total}</span>
                        </div>
                        <div className="flex flex-wrap gap-2 text-xs mb-2">
                          <Badge className="bg-green-100 text-green-800">Renovada: {data.RENOVADA}</Badge>
                          <Badge className="bg-yellow-100 text-yellow-800">Pendiente: {data.PENDIENTE}</Badge>
                          <Badge className="bg-cyan-100 text-cyan-800">En Cotización: {data.EN_COTIZACION || 0}</Badge>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs bg-slate-50 dark:bg-slate-800 p-2 rounded">
                          <div>
                            <p className="text-slate-500">Prima Renovada</p>
                            <p className="text-green-600">RD$ {(data.prima_renovada?.["RD$"] || 0).toLocaleString('es-DO')}</p>
                            <p className="text-green-600">US$ {(data.prima_renovada?.["US$"] || 0).toLocaleString('en-US')}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Prima Pendiente</p>
                            <p className="text-yellow-600">RD$ {(data.prima_pendiente?.["RD$"] || 0).toLocaleString('es-DO')}</p>
                            <p className="text-yellow-600">US$ {(data.prima_pendiente?.["US$"] || 0).toLocaleString('en-US')}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Prima Cotización</p>
                            <p className="text-cyan-600">RD$ {(data.prima_cotizacion?.["RD$"] || 0).toLocaleString('es-DO')}</p>
                            <p className="text-cyan-600">US$ {(data.prima_cotizacion?.["US$"] || 0).toLocaleString('en-US')}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
                )}
              </Card>
            </div>
          </>
        )}

        {/* Filters - Compact */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-3 items-end">
              {/* Search across all columns */}
              <div className="flex-1 min-w-[250px]">
                <Label className="text-xs text-slate-500">Buscar</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar en todas las columnas..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-all"
                  />
                </div>
              </div>

              {/* Filter by specific column */}
              <div className="w-44">
                <Label className="text-xs text-slate-500">Filtrar por</Label>
                <Select value={activeFilterColumn} onValueChange={(v) => { setActiveFilterColumn(v); setActiveFilterValue(""); }}>
                  <SelectTrigger data-testid="filter-column-select">
                    <SelectValue placeholder="Columna..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="status">Status</SelectItem>
                    <SelectItem value="clase_cliente">Clase</SelectItem>
                    <SelectItem value="sucursal">Sucursal</SelectItem>
                    <SelectItem value="ejecutivo">Ejecutivo</SelectItem>
                    <SelectItem value="compania">Aseguradora</SelectItem>
                    <SelectItem value="supervisor">Supervisor</SelectItem>
                    <SelectItem value="ramo">Ramo</SelectItem>
                    <SelectItem value="grupo_cliente">Grupo</SelectItem>
                    <SelectItem value="checklist">Checklist</SelectItem>
                    {showCompletado && (
                      <SelectItem value="completada">Completado</SelectItem>
                    )}
                    <SelectItem value="fecha">Fecha</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Dynamic filter value based on selected column */}
              {activeFilterColumn && activeFilterColumn !== "fecha" && activeFilterColumn === "ejecutivo" && (
                <div className="w-56">
                  <Label className="text-xs text-slate-500">Valor</Label>
                  <MultiSearchableSelect
                    values={filterEjecutivo ? filterEjecutivo.split("||") : []}
                    onValuesChange={(vals) => {
                      const joined = vals.join("||");
                      setFilterEjecutivo(joined);
                      setActiveFilterValue(joined);
                    }}
                    options={ejecutivoOptions}
                    placeholder="Todos"
                    searchPlaceholder="Buscar ejecutivo..."
                    emptyText="No encontrado."
                    data-testid="filter-ejecutivo-multiselect"
                  />
                </div>
              )}
              {activeFilterColumn && activeFilterColumn !== "fecha" && activeFilterColumn !== "ejecutivo" && (
                <div className="w-48">
                  <Label className="text-xs text-slate-500">Valor</Label>
                  <SearchableSelect
                    value={activeFilterValue}
                    onValueChange={(v) => {
                      setActiveFilterValue(v);
                      if (activeFilterColumn === "status") setFilterStatus(v);
                      else if (activeFilterColumn === "clase_cliente") setFilterClase(v);
                      else if (activeFilterColumn === "sucursal") setFilterSucursal(v);
                      else if (activeFilterColumn === "compania") setFilterCompania(v);
                      else if (activeFilterColumn === "supervisor") setFilterSupervisor(v);
                      else if (activeFilterColumn === "checklist") setFilterChecklist(v === "Con checklist" ? "con" : v === "Sin checklist" ? "sin" : v === "Checklist N/A" ? "na" : "");
                    }}
                    options={
                      activeFilterColumn === "status" ? (config?.status_options || []) :
                      activeFilterColumn === "clase_cliente" ? (config?.clases_cliente || []) :
                      activeFilterColumn === "sucursal" ? (config?.sucursales || []) :
                      activeFilterColumn === "compania" ? companiaOptions :
                      activeFilterColumn === "supervisor" ? supervisorOptions :
                      activeFilterColumn === "ramo" ? (config?.ramos || []) :
                      activeFilterColumn === "grupo_cliente" ? [...new Set(renovaciones.map(r => r.grupo_cliente).filter(Boolean))].sort() :
                      activeFilterColumn === "completada" ? ["Si", "No"] :
                      activeFilterColumn === "checklist" ? ["Con checklist", "Sin checklist", "Checklist N/A"] :
                      []
                    }
                    allLabel="Todos"
                    searchPlaceholder="Buscar..."
                    emptyText="No encontrado."
                    data-testid="filter-value-select"
                  />
                </div>
              )}

              {activeFilterColumn === "fecha" && (
                <>
                  <div className="w-36">
                    <Label className="text-xs text-slate-500">Desde</Label>
                    <Input type="date" value={filterFechaDesde} onChange={(e) => setFilterFechaDesde(e.target.value)} className="h-10" />
                  </div>
                  <div className="w-36">
                    <Label className="text-xs text-slate-500">Hasta</Label>
                    <Input type="date" value={filterFechaHasta} onChange={(e) => setFilterFechaHasta(e.target.value)} className="h-10" />
                  </div>
                </>
              )}

              <Button
                variant={filterCarpetaPendiente ? "default" : "outline"}
                size="sm"
                className={filterCarpetaPendiente ? "bg-amber-500 hover:bg-amber-600 text-white" : "text-amber-700 border-amber-300 hover:bg-amber-50"}
                onClick={() => setFilterCarpetaPendiente(prev => !prev)}
                data-testid="filter-carpeta-pendiente-btn"
              >
                <AlertTriangle className="w-3.5 h-3.5 mr-1" />
                Carpetas Pendientes
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setSearchTerm(""); setFilterStatus(""); setFilterClase(""); setFilterSucursal(""); setFilterVence(""); setFilterEjecutivo(""); setFilterCompania(""); setFilterSupervisor(""); setFilterChecklist(""); setFilterHastaMesActual(false); setFilterFechaDesde(""); setFilterFechaHasta(""); setFilterTrabajadasMes(false); setFilterNoRenovadas(false); setFilterCarpetaPendiente(false); setActiveFilterColumn(""); setActiveFilterValue(""); }}>
                Limpiar
              </Button>
            </div>
            
            {/* Active filters badges */}
            <div className="flex flex-wrap gap-2 mt-2">
              {filterTrabajadasMes && (
                <Badge variant="secondary" className="bg-indigo-100 text-indigo-700 flex items-center gap-1 text-xs">
                  <BarChart3 className="w-3 h-3" />
                  Trabajadas en el Mes
                  <button onClick={() => setFilterTrabajadasMes(false)} className="ml-1 hover:text-indigo-900">✕</button>
                </Badge>
              )}
              {filterNoRenovadas && (
                <Badge variant="secondary" className="bg-rose-100 text-rose-700 flex items-center gap-1 text-xs" data-testid="badge-no-renovadas">
                  <XCircle className="w-3 h-3" />
                  No Renovadas
                  <button onClick={() => setFilterNoRenovadas(false)} className="ml-1 hover:text-rose-900">✕</button>
                </Badge>
              )}
              {filterCarpetaPendiente && (
                <Badge variant="secondary" className="bg-amber-100 text-amber-700 flex items-center gap-1 text-xs" data-testid="badge-carpeta-pendiente">
                  <AlertTriangle className="w-3 h-3" />
                  Carpetas Pendientes de Entrega
                  <button onClick={() => setFilterCarpetaPendiente(false)} className="ml-1 hover:text-amber-900">✕</button>
                </Badge>
              )}
              {filterStatus && (
                <Badge variant="secondary" className="bg-blue-100 text-blue-700 flex items-center gap-1 text-xs">
                  Status: {filterStatus === "RENOVADA_O_EMISION" ? "Renovadas + Emisiones" : filterStatus}
                  <button onClick={() => { setFilterStatus(""); if (activeFilterColumn === "status") setActiveFilterValue(""); }} className="ml-1">✕</button>
                </Badge>
              )}
              {filterClase && (
                <Badge variant="secondary" className="bg-green-100 text-green-700 flex items-center gap-1 text-xs">
                  Clase: {filterClase}
                  <button onClick={() => { setFilterClase(""); if (activeFilterColumn === "clase_cliente") setActiveFilterValue(""); }} className="ml-1">✕</button>
                </Badge>
              )}
              {filterSucursal && (
                <Badge variant="secondary" className="bg-purple-100 text-purple-700 flex items-center gap-1 text-xs">
                  Sucursal: {filterSucursal}
                  <button onClick={() => { setFilterSucursal(""); if (activeFilterColumn === "sucursal") setActiveFilterValue(""); }} className="ml-1">✕</button>
                </Badge>
              )}
              {filterEjecutivo && (
                <Badge variant="secondary" className="bg-amber-100 text-amber-700 flex items-center gap-1 text-xs" data-testid="badge-filter-ejecutivo">
                  Ejecutivo: {filterEjecutivo.split("||").join(", ")}
                  <button onClick={() => { setFilterEjecutivo(""); if (activeFilterColumn === "ejecutivo") setActiveFilterValue(""); }} className="ml-1">✕</button>
                </Badge>
              )}
              {filterCompania && (
                <Badge variant="secondary" className="bg-cyan-100 text-cyan-700 flex items-center gap-1 text-xs">
                  Aseguradora: {filterCompania}
                  <button onClick={() => { setFilterCompania(""); if (activeFilterColumn === "compania") setActiveFilterValue(""); }} className="ml-1">✕</button>
                </Badge>
              )}
              {filterSupervisor && (
                <Badge variant="secondary" className="bg-teal-100 text-teal-700 flex items-center gap-1 text-xs" data-testid="badge-filter-supervisor">
                  Supervisor: {filterSupervisor}
                  <button onClick={() => { setFilterSupervisor(""); if (activeFilterColumn === "supervisor") setActiveFilterValue(""); }} className="ml-1">✕</button>
                </Badge>
              )}
              {filterChecklist && (
                <Badge variant="secondary" className="bg-sky-100 text-sky-700 flex items-center gap-1 text-xs" data-testid="badge-filter-checklist">
                  Checklist: {filterChecklist === "con" ? "Con checklist" : filterChecklist === "na" ? "Checklist N/A" : "Sin checklist"}
                  <button onClick={() => { setFilterChecklist(""); if (activeFilterColumn === "checklist") setActiveFilterValue(""); }} className="ml-1">✕</button>
                </Badge>
              )}
              {(filterFechaDesde || filterFechaHasta) && (
                <Badge variant="secondary" className="bg-rose-100 text-rose-700 flex items-center gap-1 text-xs">
                  Fecha: {filterFechaDesde || "..."} - {filterFechaHasta || "..."}
                  <button onClick={() => { setFilterFechaDesde(""); setFilterFechaHasta(""); }} className="ml-1">✕</button>
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Bulk action bar - aparece solo cuando hay items seleccionados */}
        {canImport && canDelete && selectedIds.size > 0 && (
          <Card className="mb-4 border-rose-200 bg-rose-50 dark:bg-rose-900/10 dark:border-rose-700">
            <CardContent className="py-3 px-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-sm text-rose-800 dark:text-rose-200 font-medium">
                <Trash2 className="w-4 h-4" />
                {selectedIds.size} renovación(es) seleccionada(s)
              </div>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearSelection}
                  className="text-slate-600 dark:text-slate-300"
                  data-testid="control-renov-bulk-clear"
                >
                  Cancelar
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                  className="bg-rose-600 hover:bg-rose-700"
                  data-testid="control-renov-bulk-delete"
                >
                  {bulkDeleting ? "Eliminando..." : `Eliminar ${selectedIds.size} seleccionada(s)`}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Table */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center justify-between">
              <span>Renovaciones ({total})</span>
              <span className="flex items-center gap-1">
                <Popover open={showColumnsMenu} onOpenChange={setShowColumnsMenu}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs text-slate-500 font-normal"
                      title="Mostrar u ocultar columnas de la grilla"
                      data-testid="toggle-columns-btn"
                    >
                      <ListChecks className="w-3.5 h-3.5 mr-1" />
                      Columnas
                      <ChevronDown className="w-3 h-3 ml-1" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-60 p-2 max-h-80 overflow-y-auto">
                    <p className="text-xs font-medium text-slate-500 px-1 pb-2">Columnas visibles</p>
                    {columnOrder.filter((k) => columnasGrilla[k] && columnasGrilla[k].visible !== false).map((k) => (
                      <label
                        key={k}
                        className="flex items-center gap-2 px-1 py-1.5 text-sm font-normal rounded hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer"
                        data-testid={`toggle-col-${k}`}
                      >
                        <Checkbox checked={!hiddenColumns.includes(k)} onCheckedChange={() => toggleColumnHidden(k)} />
                        <span>{columnasGrilla[k].label}</span>
                      </label>
                    ))}
                  </PopoverContent>
                </Popover>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-slate-500 font-normal"
                  onClick={resetColumnOrder}
                  title="Restablecer el orden de las columnas al orden por defecto"
                  data-testid="reset-column-order-btn"
                >
                  <RefreshCw className="w-3.5 h-3.5 mr-1" />
                  Restablecer columnas
                </Button>
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-auto max-h-[70vh]">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-800 border-y sticky top-0 z-10 shadow-sm">
                  <tr>
                    {canImport && canDelete && (
                      <th className="p-3 w-10 text-center">
                        <Checkbox
                          checked={allVisibleSelected()}
                          onCheckedChange={toggleSelectAllVisible}
                          aria-label="Seleccionar todas las renovaciones visibles"
                          data-testid="control-renov-select-all"
                          className={someVisibleSelected() ? "data-[state=checked]:bg-rose-600 border-rose-400" : ""}
                        />
                      </th>
                    )}
                    {columnasVisibles.map((col) => (
                      <th
                        key={col.key}
                        draggable
                        onDragStart={onColDragStart(col.key)}
                        onDragOver={onColDragOver}
                        onDrop={onColDrop(col.key)}
                        className={`p-3 ${col.center ? "text-center" : "text-left"} text-xs font-medium text-slate-500 uppercase ${col.sort ? "cursor-pointer" : ""} hover:bg-slate-100 dark:hover:bg-slate-700 select-none`}
                        onClick={col.sort ? () => handleSort(col.sort) : undefined}
                        title="Arrastre para reordenar las columnas"
                        {...(col.testId ? { "data-testid": col.testId } : {})}
                      >
                        <div className={`flex items-center ${col.center ? "justify-center" : ""}`}>{col.label} {col.sort ? renderSortIcon(col.sort) : null}</div>
                      </th>
                    ))}
                    <th className="p-3 text-left text-xs font-medium text-slate-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {loading ? (
                    <tr>
                      <td colSpan={(canImport && canDelete ? 1 : 0) + columnasVisibles.length + 1} className="p-8 text-center text-slate-500">Cargando...</td>
                    </tr>
                  ) : sortedRenovaciones.length === 0 ? (
                    <tr>
                      <td colSpan={(canImport && canDelete ? 1 : 0) + columnasVisibles.length + 1} className="p-8 text-center text-slate-500">No hay renovaciones registradas</td>
                    </tr>
                  ) : (
                    sortedRenovaciones.map((r) => (
                      <tr key={r.id} className={`hover:bg-slate-50 dark:hover:bg-slate-800/50 ${selectedIds.has(r.id) ? "bg-rose-50/40 dark:bg-rose-900/10" : ""}`}>
                        {canImport && canDelete && (
                          <td className="p-3 text-center">
                            <Checkbox
                              checked={selectedIds.has(r.id)}
                              onCheckedChange={() => toggleSelectOne(r.id)}
                              aria-label={`Seleccionar renovación ${r.poliza || r.cliente}`}
                              data-testid={`control-renov-select-${r.id}`}
                            />
                          </td>
                        )}
                        {columnasVisibles.map((col) => col.cell(r))}
                        <td className="p-3">
                          <div className="flex gap-1">
                            <Button 
                              size="icon" 
                              variant="ghost" 
                              className="text-blue-600"
                              onClick={() => { setChecklistRenovacion(r); setShowChecklistModal(true); }}
                              data-testid={`control-renov-checklist-btn-${r.id}`}
                              title="Checklist de Coberturas"
                            >
                              <FileCheck className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="icon" 
                              variant="ghost" 
                              className={r.es_emision ? "text-amber-600" : "text-green-600"}
                              onClick={() => handleGenerarCartaMarbetes(r)}
                              title={r.es_emision ? "Generar Carta de Emisión" : "Generar Carta de Marbetes"}
                            >
                              <FileText className="w-4 h-4" />
                            </Button>
                            {!isEjecutivoSalud(r.ejecutivo) && (
                              <Button 
                                size="icon" 
                                variant="ghost" 
                                className="text-purple-600"
                                onClick={() => openCartaEquiposDialog(r)}
                                title="Generar Carta de Equipos"
                                data-testid={`btn-carta-equipos-${r.id}`}
                              >
                                <Users2 className="w-4 h-4" />
                              </Button>
                            )}
                            <Button size="icon" variant="ghost" onClick={() => openEditDialog(r)} data-testid={`edit-renov-${r.id}`}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            {canImport && canDelete && (
                              <Button size="icon" variant="ghost" className="text-red-500" onClick={() => handleDelete(r.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {/* Controles de paginación */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t bg-slate-50/50 dark:bg-slate-800/30" data-testid="control-renov-pagination">
              <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                <span data-testid="control-renov-pagination-info">
                  {total === 0
                    ? "Sin resultados"
                    : `Mostrando ${((currentPage - 1) * pageSize) + 1}–${Math.min(currentPage * pageSize, total)} de ${total}`}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600 dark:text-slate-400 whitespace-nowrap">Por página:</span>
                <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                  <SelectTrigger className="h-8 w-[90px]" data-testid="control-renov-page-size-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[100, 200, 300, 400, 500, 1000].map((n) => (
                      <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {(() => {
                const totalPages = Math.max(1, Math.ceil(total / pageSize));
                return (
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline" size="sm" className="h-8 w-8 p-0"
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage <= 1}
                      data-testid="control-renov-page-first"
                      title="Primera página"
                    >
                      <ChevronsLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline" size="sm" className="h-8 w-8 p-0"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage <= 1}
                      data-testid="control-renov-page-prev"
                      title="Página anterior"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="px-3 text-sm font-medium text-slate-700 dark:text-slate-300 whitespace-nowrap" data-testid="control-renov-page-indicator">
                      Página {currentPage} de {totalPages}
                    </span>
                    <Button
                      variant="outline" size="sm" className="h-8 w-8 p-0"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage >= totalPages}
                      data-testid="control-renov-page-next"
                      title="Página siguiente"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline" size="sm" className="h-8 w-8 p-0"
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage >= totalPages}
                      data-testid="control-renov-page-last"
                      title="Última página"
                    >
                      <ChevronsRight className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })()}
            </div>
          </CardContent>
        </Card>
          </TabsContent>

          {/* Plantillas Tab Content */}
          <TabsContent value="plantillas" className="mt-0">
            <PlantillasChecklist 
              API_URL={API_URL} 
              getAuthHeaders={getAuthHeaders}
              canEdit={canImport}
            />
          </TabsContent>

          {/* Historial Checklist Tab Content */}
          <TabsContent value="historial" className="mt-0">
            <HistorialChecklist 
              API_URL={API_URL} 
              getAuthHeaders={getAuthHeaders}
              canEdit={canImport}
              currentUser={user}
            />
          </TabsContent>

          {/* Historial de Reporte Tab Content */}
          <TabsContent value="historial-reporte" className="mt-0">
            <HistorialReporte
              API_URL={API_URL}
              getAuthHeaders={getAuthHeaders}
              currentUser={user}
              refreshKey={reporteRefreshKey}
            />
          </TabsContent>

          {/* Configuración Tab Content (solo admin) */}
          {isAdmin && (
          <TabsContent value="configuracion" className="mt-0">
            <ConfiguracionRenovaciones 
              API_URL={API_URL} 
              getAuthHeaders={getAuthHeaders}
              canEdit={canImport}
              isAdmin={isAdmin}
              isSupervisor={isAdminOrSupervisor}
              onConfigChange={() => {
                // Reload config when configuration is changed
                fetchConfig();
              }}
            />
          </TabsContent>
          )}

          {/* Cambios en Lote Tab Content (solo admin) */}
          {isAdmin && (
          <TabsContent value="cambios-lote" className="mt-4">
            <div className="space-y-6">
              {/* Paso 1: ¿Qué cambiar? */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold">1</span>
                    ¿Qué campo deseas cambiar?
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="max-w-sm">
                    <Select
                      value={bulkCampo}
                      onValueChange={(v) => { setBulkCampo(v); setBulkNuevoValor(""); setBulkPolizas(null); setBulkSelected({}); }}
                    >
                      <SelectTrigger data-testid="bulk-campo-select">
                        <SelectValue placeholder="Selecciona un campo…" />
                      </SelectTrigger>
                      <SelectContent>
                        {(bulkOpciones.campos || []).map((c) => (
                          <SelectItem key={c.key} value={c.key} data-testid={`bulk-campo-${c.key}`}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {/* Paso 2: Filtros */}
              {bulkCampo && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold">2</span>
                      Filtra las pólizas a cambiar
                    </CardTitle>
                    <p className="text-xs text-slate-500 mt-1">Elige cualquier combinación. Deja en "Todos" lo que no quieras filtrar.</p>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {(bulkOpciones.filtros || []).map((f) => (
                        <div key={f.key}>
                          <Label className="text-xs text-slate-600 mb-1 block">{f.label}</Label>
                          <SearchableSelect
                            value={bulkFiltros[f.key] || ""}
                            onValueChange={(v) => handleBulkFiltroChange(f.key, v)}
                            options={bulkOpciones.opciones?.[f.key] || []}
                            allLabel="Todos"
                            placeholder="Todos"
                            data-testid={`bulk-filtro-${f.key}`}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center gap-3 mt-5">
                      <Button
                        onClick={handleBulkProcesar}
                        disabled={bulkLoadingPreview || Object.keys(bulkFiltros).length === 0}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid="bulk-procesar-btn"
                      >
                        {bulkLoadingPreview ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Procesando…</>) : (<><Search className="w-4 h-4 mr-2" /> Procesar</>)}
                      </Button>
                      {Object.keys(bulkFiltros).length > 0 && (
                        <button className="text-xs text-slate-500 hover:text-slate-700 underline" onClick={() => { setBulkFiltros({}); setBulkPolizas(null); setBulkSelected({}); }} data-testid="bulk-limpiar-filtros">
                          Limpiar filtros
                        </button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Paso 3 y 4: Resultados + nuevo valor */}
              {bulkPolizas !== null && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold">3</span>
                      Pólizas encontradas
                      <Badge variant="outline" className="ml-1">{bulkTotal}</Badge>
                    </CardTitle>
                    {bulkTruncado && (
                      <p className="text-xs text-amber-600 mt-1">Mostrando las primeras {bulkPolizas.length} de {bulkTotal}. Refina los filtros para trabajar con menos pólizas.</p>
                    )}
                  </CardHeader>
                  <CardContent>
                    {bulkPolizas.length === 0 ? (
                      <p className="text-sm text-slate-500 py-6 text-center">No se encontraron pólizas con esos filtros.</p>
                    ) : (
                      <>
                        <div className="border rounded-lg overflow-auto max-h-[420px]">
                          <table className="w-full text-sm">
                            <thead className="bg-slate-50 sticky top-0">
                              <tr className="text-left text-xs text-slate-500">
                                <th className="p-2 w-10">
                                  <Checkbox
                                    checked={bulkPolizas.length > 0 && bulkSelectedIds().length === bulkPolizas.length}
                                    onCheckedChange={(c) => bulkToggleAll(!!c)}
                                    data-testid="bulk-select-all"
                                  />
                                </th>
                                <th className="p-2">Cliente</th>
                                <th className="p-2">Póliza</th>
                                <th className="p-2">Ejecutivo</th>
                                <th className="p-2">Supervisor</th>
                                <th className="p-2">Vendedor</th>
                                <th className="p-2">Gestor Cobros</th>
                                <th className="p-2">Aseguradora</th>
                                <th className="p-2">Vence</th>
                              </tr>
                            </thead>
                            <tbody>
                              {bulkPolizas.map((p) => (
                                <tr key={p.id} className="border-t hover:bg-blue-50/40" data-testid={`bulk-row-${p.id}`}>
                                  <td className="p-2">
                                    <Checkbox
                                      checked={!!bulkSelected[p.id]}
                                      onCheckedChange={(c) => setBulkSelected((prev) => ({ ...prev, [p.id]: !!c }))}
                                      data-testid={`bulk-check-${p.id}`}
                                    />
                                  </td>
                                  <td className="p-2 font-medium">{p.cliente || "—"}</td>
                                  <td className="p-2">{p.poliza || "—"}</td>
                                  <td className="p-2">{p.ejecutivo || "—"}</td>
                                  <td className="p-2">{p.supervisor || "—"}</td>
                                  <td className="p-2">{p.vendedor || "—"}</td>
                                  <td className="p-2">{p.gestor_cobros || "—"}</td>
                                  <td className="p-2">{p.compania || "—"}</td>
                                  <td className="p-2">{p.vence_en || "—"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <div className="mt-5 p-4 rounded-lg bg-blue-50 border border-blue-100">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold">4</span>
                            <span className="text-sm font-medium text-slate-700">
                              Nuevo valor de <span className="font-bold">{(bulkOpciones.campos.find((c) => c.key === bulkCampo) || {}).label}</span> para las <span className="font-bold">{bulkSelectedIds().length}</span> póliza(s) seleccionada(s)
                            </span>
                          </div>
                          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                            <div className="w-full sm:max-w-sm">
                              <SearchableSelect
                                value={bulkNuevoValor}
                                onValueChange={(v) => setBulkNuevoValor(v)}
                                options={bulkOpciones.opciones?.[bulkCampo] || []}
                                allLabel="— Elegir nuevo valor —"
                                placeholder="— Elegir nuevo valor —"
                                data-testid="bulk-nuevo-valor"
                              />
                            </div>
                            <Button
                              onClick={handleBulkExecute}
                              disabled={bulkApplying || bulkSelectedIds().length === 0 || !bulkNuevoValor}
                              className="bg-emerald-600 hover:bg-emerald-700"
                              data-testid="bulk-aplicar-btn"
                            >
                              {bulkApplying ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Aplicando…</>) : (<><ListChecks className="w-4 h-4 mr-2" /> Aplicar cambio ({bulkSelectedIds().length})</>)}
                            </Button>
                          </div>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
          )}
        </Tabs>

        {/* Checklist Modal */}
        <ChecklistModal
          open={showChecklistModal}
          onClose={() => { setShowChecklistModal(false); setChecklistRenovacion(null); }}
          renovacion={checklistRenovacion}
          API_URL={API_URL}
          getAuthHeaders={getAuthHeaders}
          currentUser={user}
          onSaved={() => {
            toast.success("Checklist guardado en el historial");
            fetchRenovaciones();
            fetchDashboard();
          }}
        />

        {/* New/Edit Dialog */}
        <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editMode ? "Editar Renovación" : "Nueva Renovación"}</DialogTitle>
              {editMode && !canImport && (
                <p className="text-sm text-amber-600 mt-1">
                  Solo puedes modificar Status y Confirmación Recibido
                </p>
              )}
            </DialogHeader>
            {editMode && <ClienteAtencionWatcher nombre={formData.cliente} />}
            {editMode && <ClienteFacturaMesWatcher nombre={formData.cliente} />}
            <div className="grid grid-cols-2 gap-4 py-4">
              <div>
                <Label>Moneda *</Label>
                <Select 
                  value={formData.moneda} 
                  onValueChange={(v) => setFormData({...formData, moneda: v})}
                  disabled={editMode && !canImport}
                >
                  <SelectTrigger className={editMode && !canImport ? "bg-gray-100 cursor-not-allowed" : ""}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {config?.monedas?.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Sucursal *</Label>
                <Select 
                  value={formData.sucursal} 
                  onValueChange={(v) => setFormData({...formData, sucursal: v})}
                  disabled={editMode && !canImport}
                >
                  <SelectTrigger className={editMode && !canImport ? "bg-gray-100 cursor-not-allowed" : ""}>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(config?.sucursales || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Compañía *</Label>
                <Popover open={companiaFormOpen} onOpenChange={setCompaniaFormOpen}>
                  <PopoverTrigger asChild disabled={editMode && !canImport}>
                    <Button variant="outline" role="combobox"
                      className={`w-full justify-between font-normal ${editMode && !canImport ? "bg-gray-100 cursor-not-allowed" : ""}`}
                      data-testid="renov-form-compania">
                      <span className="truncate">{formData.compania || "Seleccionar aseguradora..."}</span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[300px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Buscar aseguradora..." />
                      <CommandList>
                        <CommandEmpty>No se encontró la aseguradora.</CommandEmpty>
                        <CommandGroup>
                          {companiasCatalogo.map((c) => (
                            <CommandItem key={c} value={c}
                              onSelect={() => { setFormData({ ...formData, compania: c }); setCompaniaFormOpen(false); }}>
                              <Check className={`mr-2 h-4 w-4 ${formData.compania === c ? "opacity-100" : "opacity-0"}`} />
                              {c}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label>Cliente *</Label>
                <Input 
                  value={formData.cliente} 
                  onChange={(e) => setFormData({...formData, cliente: e.target.value})}
                  disabled={editMode && !canImport}
                  className={editMode && !canImport ? "bg-gray-100 cursor-not-allowed" : ""}
                />
              </div>
              {editMode && puedeMarcar && formData.cliente && (
                <div className="col-span-2">
                  <label
                    className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer ${esPrioritario(formData.cliente) ? "border-red-300 bg-red-50" : "border-slate-200"}`}
                    data-testid="renov-prio-toggle"
                  >
                    <Checkbox
                      checked={esPrioritario(formData.cliente)}
                      onCheckedChange={async (checked) => {
                        try {
                          if (checked) {
                            await marcar(formData.cliente, "");
                            toast.success(`${formData.cliente} marcado como Atención Prioritaria`);
                          } else {
                            await desmarcar(formData.cliente);
                            toast.success(`Atención Prioritaria removida de ${formData.cliente}`);
                          }
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || "No se pudo actualizar");
                        }
                      }}
                      data-testid="renov-prio-checkbox"
                    />
                    <Star className={`w-4 h-4 ${esPrioritario(formData.cliente) ? "text-red-600 fill-current" : "text-amber-500"}`} />
                    <span className="text-sm font-medium text-slate-700">
                      Atención Prioritaria
                      <span className="block text-xs font-normal text-slate-500">Activa una alerta en todos los módulos para este cliente</span>
                    </span>
                  </label>
                </div>
              )}
              <div>
                <Label>Póliza *</Label>
                <Input 
                  value={formData.poliza} 
                  onChange={(e) => setFormData({...formData, poliza: e.target.value})}
                  disabled={editMode && !canImport}
                  className={editMode && !canImport ? "bg-gray-100 cursor-not-allowed" : ""}
                />
              </div>
              <div>
                <Label>Ramo *</Label>
                <Select 
                  value={formData.ramo} 
                  onValueChange={(v) => setFormData({...formData, ramo: v})}
                  disabled={editMode && !canImport}
                >
                  <SelectTrigger className={editMode && !canImport ? "bg-gray-100 cursor-not-allowed" : ""}>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(config?.ramos || []).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                  <SelectTrigger data-testid="renovacion-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(config?.status_options || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {/* Campos exclusivos para Individual/Pymes O cuando el editor es admin/supervisor */}
              {showFechaFields && (formData.status || "").toUpperCase() === "RECIBIDA" && (
                <div data-testid="fecha-recibida-oficina-wrapper">
                  <Label className="text-sm">
                    Recibida en oficina <span className="text-rose-600">*</span>
                    <span className="text-[10px] font-normal text-slate-500 ml-1">(no mayor a hoy)</span>
                  </Label>
                  <Input
                    type="date"
                    value={formData.fecha_recibida_oficina || ""}
                    max={new Date().toISOString().split("T")[0]}
                    onChange={(e) => setFormData({...formData, fecha_recibida_oficina: e.target.value})}
                    data-testid="fecha-recibida-oficina-input"
                    required
                  />
                </div>
              )}
              {/* Campo obligatorio cuando status = RENOVADA */}
              {showFechaFields && (formData.status || "").toUpperCase() === "RENOVADA" && (
                <div data-testid="fecha-facturado-korrenetz-wrapper">
                  <Label className="text-sm">
                    Facturado en Korrenetz <span className="text-rose-600">*</span>
                    <span className="text-[10px] font-normal text-slate-500 ml-1">(no mayor a hoy)</span>
                  </Label>
                  <Input
                    type="date"
                    value={formData.fecha_facturado_korrenetz || ""}
                    max={new Date().toISOString().split("T")[0]}
                    onChange={(e) => setFormData({...formData, fecha_facturado_korrenetz: e.target.value})}
                    data-testid="fecha-facturado-korrenetz-input"
                    required
                  />
                </div>
              )}
              {/* Resumen de validaciones completadas (solo lectura) */}
              {editMode && selectedRenovacion?.validaciones_renovacion?.items && (
                <details className="rounded-md border border-green-200 bg-green-50 dark:bg-green-900/20 px-3 py-2" data-testid="validaciones-resumen">
                  <summary className="text-xs font-medium text-green-800 dark:text-green-200 cursor-pointer">
                    ✓ Validaciones de renovación completadas por {selectedRenovacion.validaciones_renovacion.completado_por || "N/D"}
                    {selectedRenovacion.validaciones_renovacion.fecha ? ` el ${String(selectedRenovacion.validaciones_renovacion.fecha).split("T")[0]}` : ""}
                  </summary>
                  <ul className="mt-2 space-y-0.5">
                    {VALIDACIONES_RENOVACION.map(v => (
                      <li key={v.key} className="text-[11px] text-slate-600 dark:text-slate-300 flex justify-between gap-2">
                        <span>{v.label}</span>
                        <span className={`font-semibold ${selectedRenovacion.validaciones_renovacion.items[v.key] === "SI" ? "text-green-700" : "text-slate-500"}`}>
                          {selectedRenovacion.validaciones_renovacion.items[v.key] || "-"}
                        </span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              {/* Checkbox: Enviado por Email (entrega al cliente por correo) */}
              {(formData.status || "").toUpperCase() === "RENOVADA" && (
                <label
                  className="inline-flex items-center gap-2 px-2 py-1.5 rounded bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 cursor-pointer w-fit"
                  data-testid="enviado-por-email-wrapper"
                >
                  <input
                    type="checkbox"
                    checked={formData.enviado_por_email || false}
                    onChange={(e) => setFormData({...formData, enviado_por_email: e.target.checked})}
                    className="h-4 w-4 rounded border-emerald-400 text-emerald-600"
                    data-testid="checkbox-enviado-por-email"
                  />
                  <span className="text-[12px] font-medium text-emerald-800 dark:text-emerald-200 leading-none">
                    Enviado por Email (marca como entregado al cliente)
                  </span>
                </label>
              )}
              {(formData.ramo || "").toLowerCase().includes("vehiculo") && (
                <label className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 cursor-pointer w-fit">
                  <input
                    type="checkbox"
                    checked={formData.es_emision || false}
                    onChange={(e) => setFormData({...formData, es_emision: e.target.checked})}
                    className="h-3 w-3 rounded border-amber-400 text-amber-600"
                    data-testid="checkbox-es-emision"
                  />
                  <span className="text-[11px] font-medium text-amber-800 dark:text-amber-200 leading-none">Emisión</span>
                </label>
              )}
              {showCompletado && (
                <label className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 cursor-pointer w-fit" data-testid="completada-wrapper">
                  <input
                    type="checkbox"
                    checked={formData.completada || false}
                    onChange={(e) => setFormData({...formData, completada: e.target.checked})}
                    className="h-3 w-3 rounded border-green-400 text-green-600"
                    data-testid="checkbox-completada"
                  />
                  <span className="text-[11px] font-medium text-green-800 dark:text-green-200 leading-none">Completada</span>
                </label>
              )}
              <div>
                <Label>Vence En / Fecha Emisión *</Label>
                <Input 
                  type="date"
                  value={formData.vence_en} 
                  onChange={(e) => setFormData({...formData, vence_en: e.target.value})} 
                  disabled={editMode && !canImport}
                  className={editMode && !canImport ? "bg-gray-100 cursor-not-allowed" : ""}
                  data-testid="renovacion-vence-en-input"
                />
              </div>
              <div>
                <Label>Prima</Label>
                <Input 
                  type="number" 
                  value={formData.prima} 
                  onChange={(e) => setFormData({...formData, prima: parseFloat(e.target.value) || 0})}
                  disabled={editMode && !canImport}
                  className={editMode && !canImport ? "bg-gray-100 cursor-not-allowed" : ""}
                />
              </div>
              <div>
                <Label>Ejecutivo *</Label>
                <Popover open={ejecutivoOpen} onOpenChange={setEjecutivoOpen}>
                  <PopoverTrigger asChild disabled={editMode && !canImport}>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={ejecutivoOpen}
                      className={`w-full justify-between ${editMode && !canImport ? "bg-gray-100 cursor-not-allowed opacity-70" : ""}`}
                      disabled={editMode && !canImport}
                    >
                      {formData.ejecutivo || "Seleccionar ejecutivo..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[300px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Buscar ejecutivo..." />
                      <CommandList>
                        <CommandEmpty>No se encontró ejecutivo.</CommandEmpty>
                        <CommandGroup>
                          {usuariosEjecutivos.map((u) => (
                            <CommandItem
                              key={u.id}
                              value={u.name || u.email}
                              onSelect={(currentValue) => {
                                setFormData({...formData, ejecutivo: currentValue});
                                setEjecutivoOpen(false);
                              }}
                            >
                              <Check
                                className={`mr-2 h-4 w-4 ${formData.ejecutivo === (u.name || u.email) ? "opacity-100" : "opacity-0"}`}
                              />
                              {u.name || u.email}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label>Clase Cliente *</Label>
                <Select 
                  value={formData.clase_cliente} 
                  onValueChange={(v) => setFormData({...formData, clase_cliente: v})}
                  disabled={editMode && !canImport}
                >
                  <SelectTrigger className={editMode && !canImport ? "bg-gray-100 cursor-not-allowed" : ""}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {config?.clases_cliente?.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Grupo Cliente</Label>
                <Input value={formData.grupo_cliente || ""} onChange={(e) => setFormData({...formData, grupo_cliente: e.target.value})} placeholder="Ej: Grupo ABC" data-testid="form-grupo-cliente" />
              </div>

              {/* Campos Corporativo/Empresarial: solo Admin o usuarios Corporativo/Empresarial */}
              {showCorpFields && (
                <div className="col-span-2 border-t pt-4 mt-2">
                  <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3">Datos Corporativo / Empresarial</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Supervisor *</Label>
                      <Select
                        value={formData.supervisor || ""}
                        onValueChange={(v) => setFormData({...formData, supervisor: v})}
                      >
                        <SelectTrigger data-testid="form-supervisor"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                        <SelectContent>
                          {(config?.lista_supervisores || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Gestor de Cobros *</Label>
                      <Select
                        value={formData.gestor_cobros || ""}
                        onValueChange={(v) => setFormData({...formData, gestor_cobros: v})}
                      >
                        <SelectTrigger data-testid="form-gestor-cobros"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                        <SelectContent>
                          {(config?.gestores_cobros || []).map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Vendedor *</Label>
                      <Select
                        value={formData.vendedor || ""}
                        onValueChange={(v) => setFormData({...formData, vendedor: v})}
                      >
                        <SelectTrigger data-testid="form-vendedor"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                        <SelectContent>
                          {(config?.vendedores || []).map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Tipo de Póliza *</Label>
                      <Select
                        value={formData.tipo_poliza || ""}
                        onValueChange={(v) => setFormData({...formData, tipo_poliza: v})}
                      >
                        <SelectTrigger data-testid="form-tipo-poliza"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                        <SelectContent>
                          {(config?.tipos_poliza || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Línea *</Label>
                      <Select
                        value={formData.linea || ""}
                        onValueChange={(v) => setFormData({...formData, linea: v})}
                      >
                        <SelectTrigger data-testid="form-linea"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                        <SelectContent>
                          {(config?.lineas || []).map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              {/* Validaciones Corporativas - Only for Corporativo/Empresarial users (and admin) */}
              {editMode && showValidacionesCorp && (
                <div className="col-span-2 border-t pt-4 mt-2">
                  <h4 className="text-sm font-semibold mb-3 text-slate-700 dark:text-slate-300 flex items-center gap-2">
                    <FileCheck className="w-4 h-4" />
                    Validaciones Corporativas
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    {(() => {
                      const esAutoRamo = esRamoAutoStr(formData.ramo);
                      const conBadgeSiNo = ["presentacion", "lleva_carpeta", "checklist"];
                      const marbetes = ["marbete_digital_enviado", "marbete_fisico_enviado"];
                      return [
                        { key: "marbete_digital_enviado", label: "Marbete Digital Enviado" },
                        { key: "marbete_fisico_enviado", label: "Marbete Físico Enviado" },
                        { key: "facturado_korrenetz", label: "Facturado Korrenetz" },
                        { key: "enviado_al_cliente", label: "Enviado al Cliente" },
                        { key: "confirmacion_recepcion", label: "Confirmación Recepción" },
                        { key: "presentacion", label: "Presentación" },
                        { key: "lleva_carpeta", label: "Lleva Carpeta" },
                        { key: "checklist", label: "Checklist" },
                      ].map(({ key, label }) => {
                        const esMarbeteNA = marbetes.includes(key) && !esAutoRamo;
                        const cotejado = !!formData.validaciones_corp?.[key];
                        return (
                          <div key={key} className="flex items-center gap-2 p-2 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                            <input
                              type="checkbox"
                              checked={cotejado}
                              disabled={esMarbeteNA}
                              onChange={(e) => {
                              const nuevo = { ...formData.validaciones_corp, [key]: e.target.checked };
                              if (marbetes.includes(key) && !nuevo.marbete_digital_enviado && !nuevo.marbete_fisico_enviado) {
                                nuevo.marbete_multianual = false;
                                nuevo.marbete_multianual_vencimiento = "";
                              }
                              setFormData({ ...formData, validaciones_corp: nuevo });
                            }}
                              className="h-4 w-4 rounded border-slate-300 disabled:opacity-40"
                              data-testid={`val-corp-${key}`}
                            />
                            <Label className="text-sm cursor-pointer">{label}</Label>
                            {esMarbeteNA && (
                              <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-[10px] px-1.5" data-testid={`badge-na-${key}`} title="Solo aplica a pólizas de Automóvil">N/A</Badge>
                            )}
                            {key === "checklist" && selectedRenovacion?.checklist_no_requerido && (
                              <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-[10px] px-1.5" data-testid="badge-checklist-na-cotejo" title="El ramo de esta póliza no requiere checklist">N/A</Badge>
                            )}
                            {!esMarbeteNA && conBadgeSiNo.includes(key) && !(key === "checklist" && selectedRenovacion?.checklist_no_requerido) && (
                              cotejado ? (
                                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px] px-1.5" data-testid={`badge-si-${key}`}>SI</Badge>
                              ) : (
                                <Badge className="bg-slate-100 text-slate-600 border-slate-300 text-[10px] px-1.5" data-testid={`badge-no-${key}`}>NO</Badge>
                              )
                            )}
                            {marbetes.includes(key) && esAutoRamo && (
                              cotejado ? (
                                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px] px-1.5" data-testid={`badge-si-${key}`}>SI</Badge>
                              ) : (
                                <Badge className="bg-slate-100 text-slate-600 border-slate-300 text-[10px] px-1.5" data-testid={`badge-no-${key}`}>NO</Badge>
                              )
                            )}
                            {key === "checklist" && selectedRenovacion?.checklist_hecho && (
                              <span className="text-[10px] text-sky-600" title="Marcado automáticamente porque la póliza tiene el checklist realizado">(automático)</span>
                            )}
                          </div>
                        );
                      });
                    })()}
                    {/* Marbete multianual: pregunta al marcar marbete digital o físico entregado */}
                    {esRamoAutoStr(formData.ramo) &&
                      (formData.validaciones_corp?.marbete_digital_enviado || formData.validaciones_corp?.marbete_fisico_enviado) && (
                      <div className="col-span-2 flex flex-wrap items-center gap-3 p-2 rounded-lg bg-sky-50 dark:bg-slate-800/50 border border-sky-200 dark:border-slate-700" data-testid="marbete-multianual-wrapper">
                        <Label className="text-sm font-medium">¿El marbete es multianual?</Label>
                        <Select
                          value={formData.validaciones_corp?.marbete_multianual ? "SI" : "NO"}
                          onValueChange={(v) => setFormData({
                            ...formData,
                            validaciones_corp: {
                              ...formData.validaciones_corp,
                              marbete_multianual: v === "SI",
                              marbete_multianual_vencimiento: v === "SI" ? (formData.validaciones_corp?.marbete_multianual_vencimiento || "") : ""
                            }
                          })}
                        >
                          <SelectTrigger className="w-24 h-8" data-testid="marbete-multianual-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="NO">No</SelectItem>
                            <SelectItem value="SI">Sí</SelectItem>
                          </SelectContent>
                        </Select>
                        {formData.validaciones_corp?.marbete_multianual && (
                          <div className="flex items-center gap-2">
                            <Label className="text-sm">Fin de vigencia de los marbetes <span className="text-rose-600">*</span></Label>
                            <Input
                              type="date"
                              className="w-40 h-8"
                              value={formData.validaciones_corp?.marbete_multianual_vencimiento || ""}
                              onChange={(e) => setFormData({
                                ...formData,
                                validaciones_corp: { ...formData.validaciones_corp, marbete_multianual_vencimiento: e.target.value }
                              })}
                              data-testid="marbete-multianual-vencimiento-input"
                            />
                          </div>
                        )}
                      </div>
                    )}
                    {/* Visita with date picker */}
                    <div className="col-span-2 flex items-center gap-3 p-2 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                      <input
                        type="checkbox"
                        checked={formData.validaciones_corp?.visita || false}
                        onChange={(e) => setFormData({
                          ...formData,
                          validaciones_corp: { ...formData.validaciones_corp, visita: e.target.checked, fecha_visita: e.target.checked ? formData.validaciones_corp?.fecha_visita : "" }
                        })}
                        className="h-4 w-4 rounded border-slate-300"
                        data-testid="val-corp-visita"
                      />
                      <Label className="text-sm">Visita</Label>
                      {formData.validaciones_corp?.visita && (
                        <Input
                          type="date"
                          value={formData.validaciones_corp?.fecha_visita || ""}
                          onChange={(e) => setFormData({
                            ...formData,
                            validaciones_corp: { ...formData.validaciones_corp, fecha_visita: e.target.value }
                          })}
                          className="w-48 ml-4"
                          data-testid="val-corp-fecha-visita"
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Carpeta Entregada - Only for Corporativo/Empresarial users (and admin) */}
              {editMode && showValidacionesCorp && (
                <div className="col-span-2 border-t pt-4 mt-2" data-testid="seccion-carpeta-entregada">
                  <h4 className="text-sm font-semibold mb-3 text-slate-700 dark:text-slate-300 flex items-center gap-2">
                    <FileCheck className="w-4 h-4" />
                    Carpeta Entregada
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">¿Carpeta entregada?</Label>
                      <Select
                        value={formData.carpeta_entregada || "NO"}
                        onValueChange={(v) => setFormData({ ...formData, carpeta_entregada: v })}
                      >
                        <SelectTrigger data-testid="select-carpeta-entregada"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="NO">NO</SelectItem>
                          <SelectItem value="SI">SI</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {formData.carpeta_entregada === "SI" && (
                      <>
                        <div>
                          <Label className="text-sm">Fecha de entrega interna <span className="text-red-500">*</span></Label>
                          <Input
                            type="date"
                            value={formData.carpeta_fecha_entrega_interna || ""}
                            onChange={(e) => setFormData({ ...formData, carpeta_fecha_entrega_interna: e.target.value })}
                            data-testid="input-carpeta-fecha-interna"
                          />
                        </div>
                        <div>
                          <Label className="text-sm">Entregado a <span className="text-red-500">*</span></Label>
                          <SearchableSelect
                            value={formData.carpeta_entregado_a || ""}
                            onValueChange={(v) => setFormData({ ...formData, carpeta_entregado_a: v })}
                            options={[...new Set([...sociosVendedores.map(s => s.nombre), ...(formData.carpeta_entregado_a ? [formData.carpeta_entregado_a] : [])])].sort()}
                            allLabel="Seleccionar..."
                            searchPlaceholder="Buscar supervisor/vendedor..."
                            emptyText="No encontrado."
                            data-testid="select-carpeta-entregado-a"
                          />
                        </div>
                        <div>
                          <Label className="text-sm">Comentario <span className="text-red-500">*</span></Label>
                          <Textarea
                            value={formData.carpeta_comentario || ""}
                            onChange={(e) => setFormData({ ...formData, carpeta_comentario: e.target.value })}
                            rows={2}
                            data-testid="input-carpeta-comentario"
                          />
                        </div>
                        <div>
                          <Label className="text-sm">Fecha de entrega a cliente</Label>
                          <Input
                            type="date"
                            value={formData.carpeta_fecha_entrega_cliente || ""}
                            onChange={(e) => setFormData({ ...formData, carpeta_fecha_entrega_cliente: e.target.value })}
                            data-testid="input-carpeta-fecha-cliente"
                          />
                        </div>
                        <div>
                          <Label className="text-sm">Comentario (entrega a cliente)</Label>
                          <Textarea
                            value={formData.carpeta_comentario_cliente || ""}
                            onChange={(e) => setFormData({ ...formData, carpeta_comentario_cliente: e.target.value })}
                            rows={2}
                            data-testid="input-carpeta-comentario-cliente"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              <div className="col-span-2">
                <Label>Observaciones</Label>
                <Textarea value={formData.observaciones} onChange={(e) => setFormData({...formData, observaciones: e.target.value})} />
              </div>
            </div>
            <DialogFooter className="flex justify-between sm:justify-between">
              <div>
                {editMode && (userTipo === "Corporativo/Empresarial" || isAdminOrSupervisor) && (
                  <Button variant="outline" onClick={openCloneDialog} className="text-indigo-700 border-indigo-300 hover:bg-indigo-50" data-testid="clone-to-group-btn">
                    <Copy className="w-4 h-4 mr-2" />
                    Clonar a Grupo
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowNewDialog(false)}>Cancelar</Button>
                <Button onClick={handleSubmit}>{editMode ? "Actualizar" : "Guardar"}</Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de Validaciones obligatorias al pasar a RENOVADA (Individual/Pymes) */}
        <Dialog open={showValidacionesModal} onOpenChange={(o) => { if (!o) { setShowValidacionesModal(false); validacionesRef.current = null; } }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="validaciones-renovada-modal">
            <DialogHeader>
              <DialogTitle>Validaciones para marcar como RENOVADA</DialogTitle>
              <DialogDescription>
                Confirma cada paso con <b>Sí</b> (realizado) o <b>N/A</b> (no aplica para este cliente). Todas deben quedar marcadas para poder guardar.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-1.5">
              {VALIDACIONES_RENOVACION.map((v, idx) => {
                const val = validacionesItems[v.key];
                const faltante = validacionesFaltantes.includes(v.key);
                return (
                  <div key={v.key} className={`flex items-center gap-3 rounded-md border px-3 py-2 ${faltante ? "border-red-400 bg-red-50" : "border-slate-200"}`} data-testid={`validacion-row-${v.key}`}>
                    <span className={`text-xs font-semibold w-5 text-right ${faltante ? "text-red-600" : "text-slate-400"}`}>{idx + 1}.</span>
                    <span className={`flex-1 text-sm ${faltante ? "text-red-700" : "text-slate-700"}`}>{v.label}</span>
                    <div className="flex gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => { setValidacionesItems(prev => ({ ...prev, [v.key]: "SI" })); setValidacionesFaltantes(prev => prev.filter(k => k !== v.key)); }}
                        className={`px-3 py-1 rounded text-xs font-semibold border transition-colors ${val === "SI" ? "bg-green-600 text-white border-green-600" : "bg-white text-slate-600 border-slate-300 hover:border-green-500"}`}
                        data-testid={`validacion-si-${v.key}`}
                      >
                        Sí
                      </button>
                      <button
                        type="button"
                        onClick={() => { setValidacionesItems(prev => ({ ...prev, [v.key]: "N/A" })); setValidacionesFaltantes(prev => prev.filter(k => k !== v.key)); }}
                        className={`px-3 py-1 rounded text-xs font-semibold border transition-colors ${val === "N/A" ? "bg-slate-600 text-white border-slate-600" : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"}`}
                        data-testid={`validacion-na-${v.key}`}
                      >
                        N/A
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowValidacionesModal(false); validacionesRef.current = null; }} data-testid="validaciones-cancelar-btn">Cancelar</Button>
              <Button
                onClick={() => {
                  const faltan = VALIDACIONES_RENOVACION.filter(v => validacionesItems[v.key] !== "SI" && validacionesItems[v.key] !== "N/A");
                  if (faltan.length > 0) {
                    setValidacionesFaltantes(faltan.map(v => v.key));
                    toast.error(`Falta${faltan.length > 1 ? "n" : ""} ${faltan.length} validación${faltan.length > 1 ? "es" : ""} por completar: ${faltan.slice(0, 3).map(v => v.label).join("; ")}${faltan.length > 3 ? "…" : ""}`);
                    return;
                  }
                  validacionesRef.current = { ...validacionesItems };
                  setShowValidacionesModal(false);
                  handleSubmit();
                }}
                data-testid="validaciones-guardar-btn"
              >
                Guardar y marcar RENOVADA
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Clone to Group Dialog */}
        <Dialog open={showCloneDialog} onOpenChange={setShowCloneDialog}>
          <DialogContent className="sm:max-w-[600px] max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Copy className="w-5 h-5 text-indigo-600" />
                Clonar Información al Grupo
              </DialogTitle>
            </DialogHeader>
            {cloneLoading && !grupoRelacionados ? (
              <div className="text-center py-8 text-slate-500">Cargando grupo...</div>
            ) : grupoRelacionados?.clientes?.length > 0 ? (
              <div className="space-y-3">
                <div className="bg-indigo-50 dark:bg-indigo-900/20 p-3 rounded-lg text-sm">
                  <p className="font-semibold text-indigo-800 dark:text-indigo-300">Grupo: {grupoRelacionados.grupo_cliente}</p>
                  <p className="text-indigo-600 dark:text-indigo-400 text-xs mt-1">
                    Se copiarán: Status, Checks de validación y Observaciones de la póliza actual
                  </p>
                </div>
                <div className="max-h-[45vh] overflow-y-auto space-y-2 pr-1">
                  {grupoRelacionados.clientes.map((cli) => {
                    const allIds = cli.polizas.map(p => p.id);
                    const allSelected = allIds.every(id => selectedCloneIds.includes(id));
                    const someSelected = allIds.some(id => selectedCloneIds.includes(id));
                    const isExpanded = expandedClients[cli.cliente];
                    return (
                      <div key={cli.cliente} className="border rounded-lg overflow-hidden" data-testid={`clone-client-${cli.cliente}`}>
                        <div className="flex items-center gap-2 p-3 bg-slate-50 dark:bg-slate-800 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700"
                          onClick={() => setExpandedClients(prev => ({...prev, [cli.cliente]: !prev[cli.cliente]}))}
                        >
                          <Checkbox
                            checked={allSelected}
                            className={someSelected && !allSelected ? "opacity-50" : ""}
                            onCheckedChange={() => toggleClientPolizas(cli.cliente, cli.polizas)}
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`clone-check-client-${cli.cliente}`}
                          />
                          <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                          <span className="font-medium text-sm flex-1">{cli.cliente}</span>
                          <Badge variant="outline" className="text-xs">{cli.polizas.length} póliza{cli.polizas.length > 1 ? "s" : ""}</Badge>
                        </div>
                        {isExpanded && (
                          <div className="border-t divide-y">
                            {cli.polizas.map((pol) => (
                              <label key={pol.id} className="flex items-center gap-3 p-2 pl-10 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer text-sm" data-testid={`clone-poliza-${pol.id}`}>
                                <Checkbox
                                  checked={selectedCloneIds.includes(pol.id)}
                                  onCheckedChange={() => toggleCloneId(pol.id)}
                                />
                                <div className="flex-1 min-w-0">
                                  <span className="font-medium">{pol.poliza}</span>
                                  <span className="text-xs text-slate-500 ml-2">{pol.ramo} - {pol.compania}</span>
                                </div>
                                <Badge variant="secondary" className="text-[10px] shrink-0">{pol.status}</Badge>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500 text-right">{selectedCloneIds.length} póliza{selectedCloneIds.length !== 1 ? "s" : ""} seleccionada{selectedCloneIds.length !== 1 ? "s" : ""}</p>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                {grupoRelacionados?.grupo_cliente ? "No se encontraron otras pólizas en este grupo" : "Esta renovación no tiene grupo asignado"}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCloneDialog(false)}>Cancelar</Button>
              <Button 
                onClick={handleCloneToGroup} 
                disabled={cloneLoading || selectedCloneIds.length === 0}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="confirm-clone-btn"
              >
                {cloneLoading ? "Clonando..." : `Clonar a ${selectedCloneIds.length} póliza${selectedCloneIds.length !== 1 ? "s" : ""}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Import Dialog */}
        <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5" />
                Importar desde Excel
              </DialogTitle>
            </DialogHeader>
            {importPreview && (
              <div className="space-y-4">
                <div className="flex items-center gap-4 text-sm">
                  <Badge variant="outline">Archivo: {importPreview.filename}</Badge>
                  <Badge variant="outline">Filas: {importPreview.total_rows}</Badge>
                </div>

                <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
                  Los campos marcados con <span className="text-red-600 font-bold">*</span> son obligatorios. Si el archivo no incluye alguno, la importación se rechazará indicando cuáles faltan.
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {[
                    { field: "cliente", label: "Cliente", required: true },
                    { field: "poliza", label: "Póliza", required: true },
                    { field: "ramo", label: "Ramo", required: true },
                    { field: "moneda", label: "Moneda", required: true },
                    { field: "sucursal", label: "Sucursal", required: true },
                    { field: "ejecutivo", label: "Ejecutivo", required: true },
                    { field: "vence_en", label: "Vence En / Fecha Emisión", required: true },
                    { field: "clase_cliente", label: "Clase Cliente", required: true },
                    { field: "compania", label: "Compañía", required: true },
                    { field: "status", label: "Status", required: false },
                    { field: "prima", label: "Prima", required: false },
                    { field: "balance", label: "Balance", required: false },
                    { field: "valor_asegurado", label: "Valor Asegurado", required: false },
                    { field: "ejecutivo_renovaciones", label: "Ejecutivo Renovaciones", required: false }
                  ].map(({ field, label, required }) => {
                    const isMissing = required && (!columnMapping[field] || !(importPreview.headers || []).includes(columnMapping[field]));
                    return (
                    <div key={field}>
                      <Label className="text-xs">
                        {label}{required && <span className="text-red-600 font-bold ml-0.5">*</span>}
                      </Label>
                      <Select value={columnMapping[field] || "NONE"} onValueChange={(v) => setColumnMapping({...columnMapping, [field]: v === "NONE" ? "" : v})}>
                        <SelectTrigger className={isMissing ? "border-red-400 focus:ring-red-400" : ""} data-testid={`import-map-${field}`}><SelectValue placeholder="Seleccionar columna..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="NONE">-- No mapear --</SelectItem>
                          {importPreview.headers.map((h) => (
                            <SelectItem key={h} value={h}>{h}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  );})}
                </div>

                <div>
                  <Label className="text-sm font-medium">Vista previa de datos:</Label>
                  <div className="mt-2 border rounded overflow-x-auto max-h-40">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-100">
                        <tr>
                          {importPreview.headers.slice(0, 8).map((h) => (
                            <th key={h} className="p-2 text-left whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {importPreview.sample_data.slice(0, 3).map((row, i) => (
                          <tr key={i} className="border-t">
                            {importPreview.headers.slice(0, 8).map((h) => (
                              <td key={h} className="p-2 whitespace-nowrap">{String(row[h] || "")}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
            {importPreview && (
              <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 mt-1">
                <input
                  type="checkbox"
                  id="actualizar-existentes"
                  checked={importActualizarExistentes}
                  onChange={(e) => setImportActualizarExistentes(e.target.checked)}
                  className="w-4 h-4 mt-0.5 rounded border-blue-300"
                  data-testid="import-actualizar-existentes"
                />
                <Label htmlFor="actualizar-existentes" className="cursor-pointer text-sm font-normal">
                  <span className="font-medium">Actualizar registros existentes</span>
                  <span className="block text-xs text-slate-500">Si la póliza ya existe, en lugar de omitirla, se actualizan sus campos con los del archivo (sin crear duplicados). Útil para rellenar Vendedor, Supervisor, Gestor de Cobros, etc. en pólizas ya cargadas.</span>
                </Label>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowImportDialog(false); setImportPreview(null); }}>
                Cancelar
              </Button>
              <Button onClick={handleExecuteImport} disabled={importing}>
                {importing ? "Importando..." : "Importar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reporte de filas omitidas por campos obligatorios vacíos */}
        <Dialog open={!!importInvalidReport} onOpenChange={(open) => { if (!open) { setImportInvalidReport(null); setShowImportDialog(false); setImportPreview(null); setImportFile(null); } }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="import-invalid-report-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600">
                <AlertTriangle className="w-5 h-5" />
                Registros omitidos por campos obligatorios vacíos
              </DialogTitle>
              <DialogDescription>
                Se omitieron <strong>{importInvalidReport?.count}</strong> registro(s) porque tenían vacío al menos un campo obligatorio. Los demás registros válidos sí fueron importados.
              </DialogDescription>
            </DialogHeader>
            <div className="border rounded overflow-x-auto max-h-[50vh]">
              <table className="w-full text-sm">
                <thead className="bg-amber-50 sticky top-0">
                  <tr>
                    <th className="p-2 text-left font-medium w-24">Fila</th>
                    <th className="p-2 text-left font-medium">Campos obligatorios faltantes</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {(importInvalidReport?.rows || []).map((r, i) => (
                    <tr key={i} data-testid={`invalid-row-${r.fila}`}>
                      <td className="p-2 font-mono text-slate-600">Fila {r.fila}</td>
                      <td className="p-2">
                        {(r.campos || []).map((c) => (
                          <Badge key={c} variant="outline" className="mr-1 mb-1 text-red-600 border-red-200">{c}</Badge>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <DialogFooter>
              <Button onClick={() => { setImportInvalidReport(null); setShowImportDialog(false); setImportPreview(null); setImportFile(null); }} data-testid="close-invalid-report-btn">
                Entendido
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Carta de Equipos Dialog */}
        <Dialog open={showCartaEquiposDialog} onOpenChange={setShowCartaEquiposDialog}>
          <DialogContent className="max-w-md">
            {showCartaEquiposDialog && <ClienteAtencionWatcher nombre={cartaEquiposRenovacion?.cliente} />}
            {showCartaEquiposDialog && <ClienteFacturaMesWatcher nombre={cartaEquiposRenovacion?.cliente} />}
            <DialogHeader>
              <DialogTitle>Generar Carta de Equipos</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {cartaEquiposRenovacion && (
                <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
                  <p className="text-sm font-medium">{cartaEquiposRenovacion.cliente}</p>
                  <p className="text-xs text-slate-500">{cartaEquiposRenovacion.ramo} - {cartaEquiposRenovacion.aseguradora}</p>
                </div>
              )}
              
              {/* Gestor de Cobros - with search */}
              <div>
                <Label>Gestor de Cobros *</Label>
                <Popover open={gestorPopoverOpen} onOpenChange={setGestorPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={gestorPopoverOpen}
                      className="w-full justify-between font-normal"
                      data-testid="gestor-cobros-selector"
                    >
                      {selectedGestor
                        ? gestoresCobros.find((g) => g.id === selectedGestor)?.nombre || "Seleccionar..."
                        : "Buscar gestor de cobros..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[350px] p-0" align="start">
                    <Command>
                      <CommandInput 
                        placeholder="Buscar por nombre..." 
                        value={gestorSearchTerm}
                        onValueChange={setGestorSearchTerm}
                        data-testid="gestor-search-input"
                      />
                      <CommandList>
                        <CommandEmpty>No se encontró ningún gestor.</CommandEmpty>
                        <CommandGroup>
                          {gestoresCobros
                            .filter((g) => 
                              g.nombre?.toLowerCase().includes(gestorSearchTerm.toLowerCase()) ||
                              g.posicion?.toLowerCase().includes(gestorSearchTerm.toLowerCase())
                            )
                            .map((g) => (
                              <CommandItem
                                key={g.id}
                                value={g.nombre}
                                onSelect={() => {
                                  setSelectedGestor(g.id);
                                  setGestorPopoverOpen(false);
                                  setGestorSearchTerm("");
                                }}
                                data-testid={`gestor-option-${g.id}`}
                              >
                                <Check
                                  className={`mr-2 h-4 w-4 ${
                                    selectedGestor === g.id ? "opacity-100" : "opacity-0"
                                  }`}
                                />
                                <div className="flex flex-col">
                                  <span className="font-medium">{g.nombre}</span>
                                  <span className="text-xs text-slate-500">{g.posicion}</span>
                                </div>
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-slate-500 mt-1">
                  Aparecerá en la sección "Coordinación de pagos"
                </p>
              </div>
              
              {/* Supervisor / Vendedor - with search */}
              <div>
                <Label>Supervisor / Vendedor *</Label>
                <Popover open={socioVendedorPopoverOpen} onOpenChange={setSocioVendedorPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={socioVendedorPopoverOpen}
                      className="w-full justify-between font-normal"
                      data-testid="socio-vendedor-selector"
                    >
                      {selectedSocioVendedor
                        ? (() => {
                            const selected = sociosVendedores.find((s) => s.id === selectedSocioVendedor);
                            return selected ? (
                              <span className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">{selected.departamento}</Badge>
                                {selected.nombre}
                              </span>
                            ) : "Seleccionar...";
                          })()
                        : "Buscar supervisor o vendedor..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[350px] p-0" align="start">
                    <Command>
                      <CommandInput 
                        placeholder="Buscar por nombre..." 
                        value={socioVendedorSearchTerm}
                        onValueChange={setSocioVendedorSearchTerm}
                        data-testid="socio-vendedor-search-input"
                      />
                      <CommandList>
                        <CommandEmpty>No se encontró ningún usuario.</CommandEmpty>
                        <CommandGroup>
                          {sociosVendedores
                            .filter((s) => 
                              s.nombre?.toLowerCase().includes(socioVendedorSearchTerm.toLowerCase()) ||
                              s.departamento?.toLowerCase().includes(socioVendedorSearchTerm.toLowerCase())
                            )
                            .map((s) => (
                              <CommandItem
                                key={s.id}
                                value={s.nombre}
                                onSelect={() => {
                                  setSelectedSocioVendedor(s.id);
                                  setSocioVendedorPopoverOpen(false);
                                  setSocioVendedorSearchTerm("");
                                }}
                                data-testid={`socio-vendedor-option-${s.id}`}
                              >
                                <Check
                                  className={`mr-2 h-4 w-4 ${
                                    selectedSocioVendedor === s.id ? "opacity-100" : "opacity-0"
                                  }`}
                                />
                                <div className="flex flex-col">
                                  <span className="font-medium">{s.nombre}</span>
                                  <span className="text-xs text-slate-500">
                                    <Badge variant="outline" className="text-xs mr-1">{s.departamento}</Badge>
                                    {s.posicion}
                                  </span>
                                </div>
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-slate-500 mt-1">
                  El departamento aparecerá como encabezado de esta sección
                </p>
              </div>

              {/* Ejecutivo - only for admin/supervisor */}
              {(() => {
                const userRole = (user?.role || "").toLowerCase();
                const canSelectEjecutivo = userRole === "admin" || userRole === "administrador" || userRole === "supervisor";
                if (!canSelectEjecutivo) return null;
                return (
              <div>
                <Label>Ejecutivo <span className="text-slate-400 text-xs">(opcional)</span></Label>
                <Popover open={ejecutivoCartaPopoverOpen} onOpenChange={setEjecutivoCartaPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={ejecutivoCartaPopoverOpen}
                      className="w-full justify-between font-normal"
                      data-testid="ejecutivo-carta-selector"
                    >
                      {selectedEjecutivoCarta
                        ? (() => {
                            const selected = ejecutivosCarta.find((e) => e.id === selectedEjecutivoCarta);
                            return selected ? (
                              <span className="flex items-center gap-2">
                                {selected.departamento && <Badge variant="outline" className="text-xs">{selected.departamento}</Badge>}
                                {selected.nombre}
                              </span>
                            ) : "Seleccionar...";
                          })()
                        : "Buscar ejecutivo..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[350px] p-0" align="start">
                    <Command>
                      <CommandInput 
                        placeholder="Buscar por nombre..." 
                        value={ejecutivoCartaSearchTerm}
                        onValueChange={setEjecutivoCartaSearchTerm}
                        data-testid="ejecutivo-carta-search-input"
                      />
                      <CommandList>
                        <CommandEmpty>No se encontró ningún ejecutivo.</CommandEmpty>
                        <CommandGroup>
                          {ejecutivosCarta
                            .filter((e) => 
                              e.nombre?.toLowerCase().includes(ejecutivoCartaSearchTerm.toLowerCase()) ||
                              e.departamento?.toLowerCase().includes(ejecutivoCartaSearchTerm.toLowerCase())
                            )
                            .map((e) => (
                              <CommandItem
                                key={e.id}
                                value={e.nombre}
                                onSelect={() => {
                                  setSelectedEjecutivoCarta(e.id);
                                  setEjecutivoCartaPopoverOpen(false);
                                  setEjecutivoCartaSearchTerm("");
                                }}
                                data-testid={`ejecutivo-carta-option-${e.id}`}
                              >
                                <Check
                                  className={`mr-2 h-4 w-4 ${
                                    selectedEjecutivoCarta === e.id ? "opacity-100" : "opacity-0"
                                  }`}
                                />
                                <div className="flex flex-col">
                                  <span className="font-medium">{e.nombre}</span>
                                  <span className="text-xs text-slate-500">
                                    {e.departamento && <Badge variant="outline" className="text-xs mr-1">{e.departamento}</Badge>}
                                    {e.posicion}
                                  </span>
                                </div>
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-slate-500 mt-1">
                  Si no selecciona, se usará el usuario logueado
                </p>
              </div>
                );
              })()}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCartaEquiposDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleGenerateCartaEquipos} 
                disabled={generatingCarta || !selectedGestor || !selectedSocioVendedor}
                data-testid="generate-carta-btn"
              >
                {generatingCarta ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generando...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Generar PDF
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <DeleteImportDataModal
          open={showDeleteImportModal}
          onOpenChange={setShowDeleteImportModal}
          getAuthHeaders={() => getAuthHeaders()}
          onDeleted={fetchRenovaciones}
        />

        {/* ===== Dialog: Cálculo de Prima Prorrata ===== */}
        <Dialog open={showPrimaProrrataDialog} onOpenChange={setShowPrimaProrrataDialog}>
          <DialogContent className="max-w-lg" data-testid="prima-prorrata-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Calculator className="w-5 h-5 text-emerald-600" />
                Cálculo de Prima Prorrata
              </DialogTitle>
            </DialogHeader>

            {!primaProrrataMonedaConfirmada ? (
              // Paso 1: Seleccionar moneda y tipo de cálculo
              <div className="space-y-5 py-2">
                <div>
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
                    1. Selecciona la moneda:
                  </p>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { v: "RD$", label: "Peso Dominicano", hint: "RD$" },
                      { v: "USD$", label: "Dólar Americano", hint: "USD$" },
                      { v: "EUR$", label: "Euro", hint: "EUR$" },
                    ].map((m) => (
                      <button
                        key={m.v}
                        type="button"
                        onClick={() => setPrimaProrrata({ ...primaProrrata, moneda: m.v })}
                        data-testid={`prima-moneda-${m.v}`}
                        className={`p-3 rounded-lg border-2 transition text-center ${
                          primaProrrata.moneda === m.v
                            ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30 shadow-md"
                            : "border-slate-200 dark:border-slate-700 hover:border-emerald-300"
                        }`}
                      >
                        <div className="text-xl font-bold text-emerald-700 dark:text-emerald-300">{m.hint}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">{m.label}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
                    2. Selecciona el tipo de cálculo:
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      {
                        v: "prorrata",
                        label: "Prima Prorrata",
                        hint: "A partir de la prima anual",
                      },
                      {
                        v: "anual",
                        label: "Prima Anual",
                        hint: "A partir de una prima prorrata",
                      },
                    ].map((t) => (
                      <button
                        key={t.v}
                        type="button"
                        onClick={() => setPrimaProrrata({ ...primaProrrata, tipoCalculo: t.v })}
                        data-testid={`prima-tipo-${t.v}`}
                        className={`p-3 rounded-lg border-2 transition text-left ${
                          primaProrrata.tipoCalculo === t.v
                            ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30 shadow-md"
                            : "border-slate-200 dark:border-slate-700 hover:border-emerald-300"
                        }`}
                      >
                        <div className="text-sm font-bold text-emerald-700 dark:text-emerald-300">{t.label}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">{t.hint}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              // Paso 2: Formulario de cálculo (según tipo seleccionado)
              <div className="space-y-4 py-2">
                <div className="flex items-center justify-between p-2 rounded-md bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200">
                  <span className="text-sm">
                    <span className="font-bold text-emerald-700 dark:text-emerald-300">{primaProrrata.moneda}</span>
                    <span className="mx-2 text-slate-400">·</span>
                    <span className="text-slate-600 dark:text-slate-300">
                      {primaProrrata.tipoCalculo === "prorrata" ? "Calcular Prima Prorrata" : "Calcular Prima Anual"}
                    </span>
                  </span>
                  <button
                    type="button"
                    className="text-xs text-blue-600 hover:underline"
                    onClick={() => setPrimaProrrataMonedaConfirmada(false)}
                    data-testid="cambiar-moneda-btn"
                  >
                    Cambiar
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">
                      {primaProrrata.tipoCalculo === "anual" ? "Fecha inicio prorrata" : "Fecha Inicial"}
                    </Label>
                    <Input
                      type="date"
                      value={primaProrrata.fechaInicial}
                      onChange={(e) => setPrimaProrrata({ ...primaProrrata, fechaInicial: e.target.value })}
                      data-testid="prima-fecha-inicial"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">
                      {primaProrrata.tipoCalculo === "anual" ? "Fecha fin prorrata" : "Fecha Final"}
                    </Label>
                    <Input
                      type="date"
                      value={primaProrrata.fechaFinal}
                      onChange={(e) => setPrimaProrrata({ ...primaProrrata, fechaFinal: e.target.value })}
                      data-testid="prima-fecha-final"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-sm">Días transcurridos</Label>
                  <div
                    className="w-full rounded-md border bg-slate-100 dark:bg-slate-800 px-3 py-2 text-sm font-semibold text-slate-700 dark:text-slate-200"
                    data-testid="prima-dias-transcurridos"
                  >
                    {primaProrrataDiasTranscurridos > 0 ? `${primaProrrataDiasTranscurridos} días` : "—"}
                  </div>
                  {primaProrrataDiasTranscurridos < 0 && (
                    <p className="text-xs text-red-600 mt-1">La fecha final debe ser posterior a la inicial.</p>
                  )}
                </div>

                {primaProrrata.tipoCalculo === "prorrata" ? (
                  <>
                    <div>
                      <Label className="text-sm">Prima Anual con Impuesto</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500 font-semibold">
                          {primaProrrata.moneda}
                        </span>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          className="pl-14"
                          value={primaProrrata.primaAnual}
                          onChange={(e) => setPrimaProrrata({ ...primaProrrata, primaAnual: e.target.value })}
                          placeholder="0.00"
                          data-testid="prima-anual-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2 pt-2 border-t">
                      <div className="p-3 rounded-lg bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border border-emerald-200">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Prima Prorrata con ISC</span>
                          <span
                            className="text-lg font-bold text-emerald-700 dark:text-emerald-300"
                            data-testid="prima-prorrata-con-isc"
                          >
                            {formatPrimaProrrataAmount(primaProrrataConISC)}
                          </span>
                        </div>
                      </div>

                      <div className="p-3 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Prima Prorrata sin ISC</span>
                          <span
                            className="text-lg font-bold text-blue-700 dark:text-blue-300"
                            data-testid="prima-prorrata-sin-isc"
                          >
                            {formatPrimaProrrataAmount(primaProrrataSinISC)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <Label className="text-sm">Prima Prorrata con Impuesto</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500 font-semibold">
                          {primaProrrata.moneda}
                        </span>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          className="pl-14"
                          value={primaProrrata.primaProrrataMonto}
                          onChange={(e) => setPrimaProrrata({ ...primaProrrata, primaProrrataMonto: e.target.value })}
                          placeholder="0.00"
                          data-testid="prima-prorrata-monto-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2 pt-2 border-t">
                      <div className="p-3 rounded-lg bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border border-emerald-200">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Prima Anual</span>
                          <span
                            className="text-lg font-bold text-emerald-700 dark:text-emerald-300"
                            data-testid="prima-anual-result"
                          >
                            {formatPrimaProrrataAmount(primaAnualCalculada)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            <DialogFooter className="gap-2">
              {!primaProrrataMonedaConfirmada ? (
                <>
                  <Button variant="outline" onClick={() => setShowPrimaProrrataDialog(false)} data-testid="prima-cancelar-btn">
                    Cancelar
                  </Button>
                  <Button
                    onClick={() => setPrimaProrrataMonedaConfirmada(true)}
                    data-testid="prima-confirmar-moneda-btn"
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    Continuar
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={() => setPrimaProrrata({ ...primaProrrata, fechaInicial: "", fechaFinal: "", primaAnual: "", primaProrrataMonto: "" })}
                    data-testid="prima-limpiar-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-1.5" />
                    Limpiar campos
                  </Button>
                  <Button onClick={() => setShowPrimaProrrataDialog(false)} data-testid="prima-cerrar-btn">
                    Cerrar
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== Modal Auditar Fechas (admin) ===== */}
        <Dialog open={showAuditDialog} onOpenChange={(o) => { if (!o) { setShowAuditDialog(false); setAuditResult(null); setRepairResult(null); } }}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="audit-fechas-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CalendarCheck className="w-5 h-5 text-blue-600" />
                Auditoría de Fechas — Vence En / Día Vencimiento
              </DialogTitle>
            </DialogHeader>

            {auditLoading && (
              <div className="flex items-center justify-center py-12 text-slate-500">
                <Loader2 className="w-6 h-6 animate-spin mr-2" />
                Escaneando registros…
              </div>
            )}

            {!auditLoading && auditResult && (
              <div className="space-y-4">
                {/* Summary banner */}
                <div className={`p-4 rounded-lg border-2 ${auditResult.estado === "limpio" ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-300"}`}>
                  <div className="flex items-center gap-3">
                    {auditResult.estado === "limpio" ? (
                      <CheckCircle2 className="w-8 h-8 text-emerald-600 shrink-0" />
                    ) : (
                      <AlertTriangle className="w-8 h-8 text-amber-600 shrink-0" />
                    )}
                    <div>
                      <p className="text-sm font-semibold">
                        {auditResult.estado === "limpio"
                          ? "Sin anomalías detectadas"
                          : "Se detectaron anomalías que pueden repararse"}
                      </p>
                      <p className="text-xs text-slate-600">
                        Total revisado: <span className="font-medium">{auditResult.total_registros}</span> registros
                      </p>
                    </div>
                  </div>
                </div>

                {/* Categorias */}
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: "vence_en_vacio", label: "Vence En vacío" },
                    { key: "vence_en_formato_invalido", label: "Vence En formato inválido" },
                    { key: "dia_vencimiento_faltante", label: "Día Vencimiento faltante" },
                    { key: "dia_vencimiento_invalido", label: "Día Vencimiento inválido" },
                  ].map(({ key, label }) => {
                    const block = auditResult.anomalias?.[key] || { count: 0, samples: [] };
                    const ok = block.count === 0;
                    return (
                      <div key={key} className={`p-3 rounded border ${ok ? "bg-emerald-50/50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs font-medium text-slate-700">{label}</p>
                          <Badge className={ok ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}>
                            {block.count}
                          </Badge>
                        </div>
                        {!ok && block.samples?.length > 0 && (
                          <details className="mt-2">
                            <summary className="text-[11px] cursor-pointer text-rose-700 hover:underline">Ver primeros {Math.min(5, block.samples.length)} ejemplos</summary>
                            <ul className="mt-1 space-y-0.5 text-[11px] text-slate-600">
                              {block.samples.slice(0, 5).map((s, i) => (
                                <li key={i} className="truncate">
                                  • {s.cliente?.slice(0, 28) || "—"} / {s.poliza || "—"} (vence: <span className="font-mono">{JSON.stringify(s.vence_en)}</span>, día: <span className="font-mono">{JSON.stringify(s.dia_vencimiento)}</span>)
                                </li>
                              ))}
                            </ul>
                          </details>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Repair result */}
                {repairResult && (
                  <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                    <div className="flex items-center gap-2 mb-1">
                      <Wrench className="w-4 h-4 text-blue-600" />
                      <p className="text-sm font-semibold text-blue-800">Reparación completada</p>
                    </div>
                    <p className="text-xs text-blue-700">{repairResult.mensaje}</p>
                    {repairResult.estrategias_aplicadas && Object.keys(repairResult.estrategias_aplicadas).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {Object.entries(repairResult.estrategias_aplicadas).map(([k, count]) => (
                          <Badge key={k} className="bg-blue-100 text-blue-800 text-[10px]">
                            {k}: {count}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {repairResult.detalles?.length > 0 && (
                      <details className="mt-2">
                        <summary className="text-[11px] cursor-pointer text-blue-700 hover:underline">Ver detalles reparados ({repairResult.detalles.length})</summary>
                        <ul className="mt-1 space-y-0.5 text-[11px] text-slate-600 max-h-40 overflow-y-auto">
                          {repairResult.detalles.map((d, i) => (
                            <li key={i} className="truncate font-mono">
                              • {d.cliente}/{d.poliza}: {JSON.stringify(d.anterior)} → <span className="text-blue-700 font-semibold">{d.nuevo_vence_en}</span>{d.nuevo_dia ? ` (día ${d.nuevo_dia})` : ""} <span className="text-slate-400">[{d.estrategia}]</span>
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                    {repairResult.no_reparables > 0 && repairResult.no_reparables_detalle?.length > 0 && (
                      <details className="mt-2">
                        <summary className="text-[11px] cursor-pointer text-amber-700 hover:underline">⚠ {repairResult.no_reparables} no reparables (revisión manual)</summary>
                        <ul className="mt-1 space-y-0.5 text-[11px] text-slate-600 max-h-40 overflow-y-auto">
                          {repairResult.no_reparables_detalle.map((d, i) => (
                            <li key={i} className="truncate">
                              • {d.cliente}/{d.poliza}: vence={JSON.stringify(d.vence_en)}, día={JSON.stringify(d.dia_vencimiento)} — <span className="text-amber-700">{d.razon}</span>
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
              </div>
            )}

            <DialogFooter className="gap-2">
              {!auditLoading && auditResult && auditResult.estado !== "limpio" && !repairResult && (
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={repairLoading}
                  data-testid="repair-fechas-btn"
                  onClick={async () => {
                    setRepairLoading(true);
                    try {
                      const res = await axios.post(`${API_URL}${crApi()}/control-renovaciones/auditoria/reparar-fechas`, {}, {
                        ...getAuthHeaders(),
                        timeout: 120000,
                      });
                      setRepairResult(res.data);
                      toast.success(res.data.mensaje || "Reparación completada");
                      // re-run audit to refresh counts
                      const re = await axios.get(`${API_URL}${crApi()}/control-renovaciones/auditoria/fechas-faltantes`, {
                        ...getAuthHeaders(),
                        timeout: 60000,
                      });
                      setAuditResult(re.data);
                      // refresh main grid
                      fetchRenovaciones();
                    } catch (err) {
                      const detail = err.response?.data?.detail || err.message || "Error desconocido";
                      toast.error(`Error al reparar: ${detail}`);
                    } finally {
                      setRepairLoading(false);
                    }
                  }}
                >
                  {repairLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wrench className="w-4 h-4 mr-2" />}
                  Reparar automáticamente
                </Button>
              )}
              <Button variant="outline" onClick={() => { setShowAuditDialog(false); setAuditResult(null); setRepairResult(null); }} data-testid="audit-close-btn">
                Cerrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      {/* Dialog: Auditoría de Jerarquía (admin only) */}
      <Dialog open={showScopeDialog} onOpenChange={setShowScopeDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="scope-jerarquia-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              Auditoría de Jerarquía — ¿Qué ve cada usuario?
            </DialogTitle>
            <DialogDescription>
              Elegí un usuario para ver exactamente qué registros de Control de Renovaciones puede ver, según el organigrama y los nombres que matchean.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Selector de usuario */}
            <div>
              <Label className="text-sm font-medium">Usuario a auditar</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  placeholder="Buscar por nombre o email..."
                  value={scopeSearchQuery}
                  onChange={(e) => setScopeSearchQuery(e.target.value)}
                  className="flex-1"
                  data-testid="scope-search-input"
                />
                <select
                  value={scopeSelectedUserId}
                  onChange={(e) => { setScopeSelectedUserId(e.target.value); fetchScopeForUser(e.target.value); }}
                  className="border rounded px-2 py-2 text-sm bg-white min-w-[280px]"
                  data-testid="scope-user-select"
                  disabled={scopeUsersLoading}
                >
                  <option value="">— Elegí un usuario —</option>
                  {scopeUsersList
                    .filter(u => {
                      const q = scopeSearchQuery.toLowerCase();
                      return !q || (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
                    })
                    .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                    .map(u => (
                      <option key={u.id} value={u.id}>
                        {u.name} ({u.role || "user"}) — {u.email}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            {scopeLoading && (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-purple-500" />
                <p className="text-sm text-slate-500 mt-2">Calculando scope...</p>
              </div>
            )}

            {scopeData && !scopeLoading && (
              <div className="space-y-4">
                {/* Header con info del usuario */}
                <div className="p-3 rounded-lg border-2 border-purple-200 bg-purple-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold text-lg text-purple-900">{scopeData.target_usuario.name}</p>
                      <p className="text-xs text-purple-700">{scopeData.target_usuario.email} | rol: <strong>{scopeData.target_usuario.role}</strong></p>
                    </div>
                    <div className="text-right">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        scopeData.tipo_scope === "admin" ? "bg-rose-200 text-rose-800" :
                        scopeData.tipo_scope === "supervisor" ? "bg-amber-200 text-amber-800" :
                        scopeData.tipo_scope === "tipo_visibility" ? "bg-indigo-200 text-indigo-800" :
                        "bg-slate-200 text-slate-700"
                      }`}>
                        {scopeData.tipo_scope.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm mt-2 text-slate-700">{scopeData.descripcion_scope}</p>
                </div>

                {/* Estadísticas */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <p className="text-xs text-emerald-700">Registros visibles</p>
                    <p className="text-2xl font-bold text-emerald-800">{scopeData.estadisticas.visibles_para_target.toLocaleString()}</p>
                  </div>
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                    <p className="text-xs text-slate-600">Total en sistema</p>
                    <p className="text-2xl font-bold text-slate-700">{scopeData.estadisticas.total_registros_sistema.toLocaleString()}</p>
                  </div>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs text-blue-700">% Visibilidad</p>
                    <p className="text-2xl font-bold text-blue-800">{scopeData.estadisticas.porcentaje_visible}%</p>
                  </div>
                </div>

                {/* Jerarquía (árbol) */}
                {scopeData.jerarquia && (
                  <div>
                    <h4 className="font-semibold text-sm mb-2 flex items-center gap-1 text-slate-700">
                      <Users className="w-4 h-4" /> Jerarquía visible para este usuario
                    </h4>
                    <div className="border rounded-lg p-3 bg-white">
                      <ScopeTreeNode node={scopeData.jerarquia} />
                    </div>
                  </div>
                )}

                {/* Nombres que matchea el filtro */}
                {scopeData.nombres_matchea && scopeData.nombres_matchea.length > 0 && (
                  <details className="border rounded p-2 bg-slate-50">
                    <summary className="cursor-pointer text-sm font-semibold text-slate-700">
                      Nombres exactos que el filtro busca en el campo `ejecutivo` ({scopeData.nombres_matchea.length})
                    </summary>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {scopeData.nombres_matchea.map((n, i) => (
                        <span key={i} className="px-2 py-0.5 bg-white border rounded text-xs font-mono">{n}</span>
                      ))}
                    </div>
                  </details>
                )}

                {/* Huérfanos (problema más común) */}
                {scopeData.registros_huerfanos_muestra && scopeData.registros_huerfanos_muestra.length > 0 && (
                  <div className="border-2 border-amber-300 bg-amber-50 rounded-lg p-3">
                    <h4 className="font-semibold text-sm text-amber-800 mb-2 flex items-center gap-1">
                      ⚠️ Registros "huérfanos" detectados ({scopeData.registros_huerfanos_muestra.length})
                    </h4>
                    <p className="text-xs text-amber-700 mb-2">
                      Estos registros tienen un nombre en `ejecutivo` que se parece a alguien de la jerarquía de este usuario, pero NO matchea exactamente. Esto puede causar que el supervisor NO los vea.
                    </p>
                    <div className="border rounded overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-amber-100 text-amber-800">
                          <tr>
                            <th className="px-2 py-1 text-left">Ejecutivo en renovación</th>
                            <th className="px-2 py-1 text-center">Registros</th>
                            <th className="px-2 py-1 text-left">Se parece a</th>
                            <th className="px-2 py-1 text-left">Palabras coincidentes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {scopeData.registros_huerfanos_muestra.map((h, i) => (
                            <tr key={i} className="border-t">
                              <td className="px-2 py-1 font-mono">{h.ejecutivo_en_renovacion}</td>
                              <td className="px-2 py-1 text-center font-semibold">{h.registros_count}</td>
                              <td className="px-2 py-1">{(h.se_parece_a_jerarquia || []).join(", ")}</td>
                              <td className="px-2 py-1 text-slate-600">{(h.palabras_coincidentes || []).join(", ")}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-amber-700 mt-2">
                      💡 <strong>Solución</strong>: editá el campo `ejecutivo` de esos registros para que coincida exactamente con el `name` del usuario, o normalizá los nombres en la base.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScopeDialog(false)} data-testid="scope-close-btn">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Restaurar Estatus desde Excel exportado (solo admin) */}
      <Dialog open={showRestauraDialog} onOpenChange={(v) => { if (!restauraLoading) setShowRestauraDialog(v); }}>
        <DialogContent className="max-w-2xl" data-testid="restaurar-estatus-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0C314C] dark:text-white">Restaurar Estatus desde Excel</DialogTitle>
            <DialogDescription>
              Restaura únicamente el campo Estatus de cada póliza según el Excel exportado seleccionado. Ningún otro dato se modifica.
            </DialogDescription>
          </DialogHeader>
          {restauraLoading && !restauraPreview ? (
            <div className="flex items-center gap-2 py-6 justify-center text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin" /> Analizando archivo...
            </div>
          ) : restauraPreview ? (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="p-3 rounded-lg bg-sky-50 border border-sky-200">
                  <p className="text-2xl font-bold text-sky-700" data-testid="restaura-cambios-count">{restauraPreview.cambios}</p>
                  <p className="text-xs text-slate-600">Estatus a restaurar</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <p className="text-2xl font-bold text-slate-600">{restauraPreview.sin_cambio}</p>
                  <p className="text-xs text-slate-600">Ya coinciden</p>
                </div>
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <p className="text-2xl font-bold text-amber-700">{restauraPreview.no_encontradas}</p>
                  <p className="text-xs text-slate-600">Pólizas no encontradas</p>
                </div>
              </div>
              {restauraPreview.detalle?.length > 0 && (
                <div className="max-h-64 overflow-y-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="p-2 text-left">Póliza</th>
                        <th className="p-2 text-left">Cliente</th>
                        <th className="p-2 text-left">Estatus actual</th>
                        <th className="p-2 text-left">Se restaurará a</th>
                      </tr>
                    </thead>
                    <tbody>
                      {restauraPreview.detalle.map((c, i) => (
                        <tr key={i} className="border-t">
                          <td className="p-2 font-medium">{c.poliza}</td>
                          <td className="p-2">{c.cliente}</td>
                          <td className="p-2 text-rose-600">{c.de || "—"}</td>
                          <td className="p-2 text-emerald-700 font-semibold">{c.a}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {restauraPreview.cambios === 0 && (
                <p className="text-sm text-slate-500 text-center py-2">No hay estatus para restaurar: todos coinciden con el archivo.</p>
              )}
            </div>
          ) : null}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowRestauraDialog(false)} disabled={restauraLoading} data-testid="restaura-cancelar-btn">Cancelar</Button>
            <Button
              onClick={handleConfirmarRestauracion}
              disabled={restauraLoading || !restauraPreview || restauraPreview.cambios === 0}
              className="bg-[#0C314C] hover:bg-[#16304f] text-white font-semibold"
              data-testid="restaura-confirmar-btn"
            >
              {restauraLoading && restauraPreview ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Restaurar {restauraPreview?.cambios || 0} Estatus
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      </main>
    </div>
  );
}

// Componente recursivo del árbol de jerarquía
function ScopeTreeNode({ node }) {
  if (!node) return null;
  return (
    <div style={{ marginLeft: node.depth * 20 }} className="py-1">
      <div className="flex items-center gap-2">
        <span className="text-slate-400">{node.depth > 0 ? "└─" : "▸"}</span>
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${node.es_target ? "bg-purple-200 text-purple-900 ring-2 ring-purple-400" : "bg-slate-100 text-slate-700"}`}>
          {node.name}
        </span>
        <span className="text-xs text-slate-500">({node.role}) — {node.email}</span>
      </div>
      {node.supervisados_directos && node.supervisados_directos.length > 0 && (
        <div>
          {node.supervisados_directos.map((sn) => (
            <ScopeTreeNode key={sn.id} node={sn} />
          ))}
        </div>
      )}
    </div>
  );
}
